import { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { scopedPromoDiscountLabel } from '@/lib/promoScopeLabel'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/api/client'
import { useCategories } from '@/hooks/useCategories'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { printKotReceipts } from '@/lib/printKotReceipt'
import { shouldRelayCounterPrint, watchRelayedPrintJobs } from '@/lib/counterRelay'
import { useOfflineCapture } from '@/lib/offline/useOfflineCapture'
import { OfflineOrdersTray } from '@/components/counter/OfflineOrdersTray'
import { OfflineHeldTicket } from '@/components/counter/OfflineHeldTicket'
import { OfflinePaymentPrompt } from '@/components/counter/OfflinePaymentPrompt'
import { priceOfflineOrder } from '@/lib/offline/priceOfflineOrder'
import { withoutRetiredProducts } from '@/lib/productVisibility'
import { buildOfflineVoidSlips } from '@/lib/offline/buildKotSlip'
import { readStationCache } from '@/lib/offline/stationCache'
import type { OfflineOrder, OfflinePayment } from '@/lib/offline/offlineStore'
import { updateOfflineOrder, voidLineFromPendingOfflineOrder } from '@/lib/offline/offlineStore'
import {
  PROXY_UNREACHABLE_CODE,
  PROXY_UNREACHABLE_STATUS,
  useOnlineAndReachable,
} from '@/lib/offline/reachability'

// F-FE-N30 / F-FE-N125: the refusal copy is shared with the floor screen.
import { offlineHintForRefusal } from '@/lib/offline/refusalCopy'
import { isBhooklyNativeServerApp } from '@/lib/bhooklyNative'
import { isAndroidPrintClient } from '@/lib/bhooklyAppPrint'
import { getReceiptPrinter } from '@/lib/stationPrinterMap'
import { CounterPaymentPanel } from '@/components/counter/CounterPaymentPanel'
import { CounterOrderTypeToggle } from '@/components/counter/CounterOrderTypeToggle'
import { TicketHeader } from '@/components/counter/rail/TicketHeader'
import { CounterRailToolbar } from '@/components/counter/rail/CounterRailToolbar'
import { CounterLoyaltyChip } from '@/components/counter/CounterLoyaltyChip'
import {
  buildOpenOrdersStrip,
  openOrderTagLabel,
  getTicketLifecycle,
  isOpenOrdersStripMember,
  orderPayableRemaining,
} from '@/components/counter/rail/ticketState'
import { SentItemsSection, VOIDABLE_STATUSES } from '@/components/counter/rail/SentItemsSection'
import { isBillableLine, isUnsentLine } from '@/components/counter/rail/lineVisibility'
import { UnsentItemsSection } from '@/components/counter/rail/UnsentItemsSection'
import { CashTenderPad } from '@/components/counter/rail/CashTenderPad'
import { GiftCardTenderDialog } from '@/components/counter/rail/GiftCardTenderDialog'
import { useGiftCardTenderEnabled } from '@/hooks/useGiftCardTenderEnabled'
import { ChargeToAccountDialog } from '@/components/counter/rail/ChargeToAccountDialog'
import {
  ReceiveTabPaymentDialog,
  type TabSettlementExtras,
} from '@/components/counter/ReceiveTabPaymentDialog'
import { useHouseAccountsEnabled } from '@/hooks/useHouseAccountsEnabled'
import {
  CHARGE_ACCOUNT_INLINE_CODES,
  isNeverFiledTreatment,
  type ChargeAccountServerError,
} from '@/lib/houseAccounts'
import { printTabPaymentSlipForResult } from '@/lib/printTabPaymentSlip'
import { ActionFooter } from '@/components/counter/rail/ActionFooter'
import { useBlockRestart } from '@/lib/restartGuard'
import {
  getDraftUserId,
  readDraft,
  clearDraft,
  useDraftAutosave,
  restoreDraftItems,
  type DraftFields,
} from '@/components/counter/useCounterDraft'
import { shouldKeepCollecting } from '@/components/counter/firedOrderBinding'
import { TablesPicker } from '@/components/counter/TablesPicker'
import { useReservedTableGuard } from '@/components/reservations/ReservedTableGuard'
import {
  CounterCheckoutSuccessOverlay,
  type CheckoutCelebrationMode,
} from '@/components/counter/CounterCheckoutSuccessOverlay'
import { toGuestDateInputValue } from '@/components/counter/CounterGuestDetailsSection'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { useCounterHotkeys } from '@/hooks/useCounterHotkeys'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { useOnline } from '@/hooks/useOnline'
import { HeldOrdersSheet } from '@/components/counter/HeldOrdersSheet'
import {
  buildHeldCartLabel,
  heldCartErrorMessage,
  holdBlockMessage,
  holdBlockReason,
  type HeldCart,
} from '@/components/counter/heldCarts'
import { getOrderTypePricing, useEnabledOrderTypes } from '@/hooks/useEnabledOrderTypes'
import type { CounterOrderTypeOption } from '@/components/counter/CounterOrderTypeToggle'
import { colorForOrderTypeId, displayLabelForOrderType, normalizeOrderTypeId, sanitizeOrderTypeHexColor } from '@/lib/orderTypeSettings'
import { marketplaceProviderSlugs, onlineProvidersForOrder } from '@/lib/marketplaceProviders'
import { computeCartTotals, mergePricingSettings } from '@/lib/counterPricing'
import { loadServerPrebill } from '@/lib/counterPricingQuery'
import { menuGridState } from '@/lib/counterMenuState'
import { subscribeOrderReady } from '@/lib/kdsRealtime'
import { useDashboardStream, type DashboardStreamEvent } from '@/lib/dashboardStream'
import { invalidateTables, isTableAffectingEvent } from '@/lib/tablesCache'
import {
  createCounterListRefresher,
  patchOpenOrderInStrip,
  type CounterListRefresher,
} from '@/components/counter/counterListRefresh'
import { isTerminalOrderStatus, ORDER_NOT_EDITABLE } from '@/lib/orderStatus'
import { customerPhoneShapeError } from '@/lib/customerPhone'
import { cn } from '@/lib/utils'
import { useCurrency } from '@/contexts/CurrencyContext'
import { toastHelpers } from '@/lib/toast-helpers'
import {
  checkoutStoredIntentToReceiptPaymentMethod,
  getCashierNameFromStorage,
  getServerNameFromOrder,
  houseLegFromOrder,
  parseReceiptSettings,
  printCustomerReceipt,
  printOfflineNonFiscalReceipt,
  printSplitGuestReceipt,
  printUnpaidCustomerBill,
  type CustomerReceiptSettings,
  type ReceiptHouseLeg,
} from '@/lib/printCustomerReceipt'
import {
  SplitBillDialog,
  type PaySplitGuestArgs,
} from '@/components/counter/split/SplitBillDialog'
import type { GuestShare } from '@/lib/splitBilling'
import { printGuestReceiptForOrderId } from '@/lib/printGuestReceiptReplay'
import { buildReceiptLoyaltyBlock } from '@/lib/buildReceiptLoyaltyBlock'
import { buildReceiptThankYouBlock } from '@/lib/buildReceiptThankYouBlock'
import { buildReceiptReviewBlock } from '@/lib/buildReceiptReviewBlock'
import { resolveReceiptQrSlots } from '@/lib/receiptQrPolicy'
import { printPraTaxInvoice } from '@/lib/printPraTaxInvoice'
import { kitchenSoundService } from '@/services/soundService'
import { Skeleton } from '@/components/ui/skeleton'
import { PraInvoicePromptModal } from '@/components/counter/PraInvoicePromptModal'
import { PreviousDayBlockerModal } from '@/components/counter/PreviousDayBlockerModal'
import { StartTheDayBanner } from '@/components/counter/StartTheDayBanner'
import { OpenDayDialog } from '@/components/admin/dayclose/OpenDayDialog'
import { DailyClosingBanner } from '@/components/counter/DailyClosingBanner'
import { ManagerHandoffModal } from '@/components/counter/ManagerHandoffModal'
import { PinEntryModal } from '@/components/server/PinEntryModal'
import { KotPrintModal } from '@/components/counter/KotPrintModal'
import type { APIResponse, HouseAccount, StationKOT, TabSettlementResult } from '@/types'
import { ApiClientError, type AdjustmentAuthBody } from '@/api/client'
import { AdjustmentAuthDialog, type PendingAdjustment } from '@/components/counter/AdjustmentAuthDialog'
import { useAllSettings } from '@/hooks/useKitchenFlowEnabled'
import {
  adjustmentNeedFromRefusal,
  adjustmentRefusalMessage,
  DISCOUNT_CONTROLS_KEY,
  discountNeed,
  parseDiscountControls,
  serviceChargeNeed,
  type AdjustmentNeed,
} from '@/lib/discountControls'
import { ProductImagePlaceholder } from '@/components/menu/ProductImagePlaceholder'
import { MenuCategoryBar } from '@/components/menu/MenuCategoryBar'
import { categoryColor, categorySurfaceStyle } from '@/lib/menuCategoryStyle'
import {
  Search,
  GripVertical,
  BadgeDollarSign,
  PauseCircle,
  BookUser,
  Plus,
  Sparkles,
  X,
} from 'lucide-react'
import { format } from 'date-fns'
import type {
  Product,
  Category,
  CustomerQuickAddress,
  DiningTable,
  Order,
  OrderItem,
  CreateOrderRequest,
  ProcessPaymentRequest,
  TableSession,
  ServiceChargeOverride,
} from '@/types'
import { serviceChargeOverrideFromOrder } from '@/types'
import {
  cartLineMergeKey,
  foldCartCollisions,
  makeCartLine,
  type CartItem,
  type PricedCartItem,
} from './cartLine'
import { resolveLineUnitPrice, type LineSelection } from '@/lib/lineModifiers'
import { useMenuModifiers } from '@/lib/modifierCatalogue'
import { buildMenuRulesIndex, logUpsellEvents, markSoldOutDealChoiceMap, markSoldOutDealChoices, useMenuClock, useMenuRules } from '@/lib/menuRulesQuery'
import { autoDiscountLabel, describeHint, usefulHints } from '@/lib/autoDiscounts'
import { clockAt, resolveBasePrice } from '@/lib/menuRules'
import {
  ModifierConfigurator,
  type ConfiguredLine,
} from '@/components/menu/ModifierConfigurator'

const ORDER_TYPE_TAB_DESCRIPTIONS: Record<string, string> = {
  dine_in: 'Tables & tabs',
  takeout: 'Pickup orders',
  delivery: 'Driver handoff',
}

// orderPayableRemaining + lifecycle helpers live in rail/ticketState

/**
 * A fresh idempotency key for one cart submission (F-FE-N103).
 *
 * The fallback matters: `crypto.randomUUID` needs a secure context, and a
 * terminal served over plain http on the LAN does not have one. Falling back to
 * a weaker id is right — it only has to be unique among the handful of
 * submissions this terminal makes, and having no key at all would silently drop
 * the duplicate protection on exactly the deployments least able to spot it.
 */
function newSubmitOpId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `sub-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
}

const COUNTER_CHECKOUT_RAIL_KEY = 'pos-counter-checkout-rail-px'
const COUNTER_RAIL_DEFAULT = 448
const COUNTER_RAIL_MIN = 300

function readCheckoutRailWidth(): number {
  if (typeof window === 'undefined') return COUNTER_RAIL_DEFAULT
  const raw = localStorage.getItem(COUNTER_CHECKOUT_RAIL_KEY)
  const n = raw ? parseInt(raw, 10) : NaN
  return Number.isFinite(n) && n >= COUNTER_RAIL_MIN ? n : COUNTER_RAIL_DEFAULT
}

// Saved height of the old resizable categories pane. Categories now render as
// a fixed single-row chip bar, so the key is cleared on mount.
const LEGACY_CATEGORY_PANE_KEY = 'pos-counter-category-pane-px'

export function CounterInterface() {
  const [checkoutOpen, setCheckoutOpen] = useState(false)
  const [orderType, setOrderType] = useState<string>('dine_in')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [selectedTable, setSelectedTable] = useState<DiningTable | null>(null)
  const [dineInSession, setDineInSession] = useState<TableSession | null>(null)
  const [customerName, setCustomerName] = useState('')
  const [customerEmail, setCustomerEmail] = useState('')
  const [customerPhone, setCustomerPhone] = useState('')
  const [guestBirthday, setGuestBirthday] = useState('')
  // Delivery-only. deliveryFeeOverride null = use the settings default; a
  // number replaces it in the live total and is persisted with the order.
  const [deliveryAddress, setDeliveryAddress] = useState('')
  const [deliveryFeeOverride, setDeliveryFeeOverride] = useState<number | null>(null)
  // Any order type. serviceChargeOverride null = the settings rate for the
  // order type; a {kind:'rate'|'amount', value} replaces it in the live total
  // and is persisted with the order (migration 081).
  const [serviceChargeOverride, setServiceChargeOverride] = useState<ServiceChargeOverride | null>(null)
  // F-CTRL-N80: a reason / manager PIN for a service charge waived on a ticket
  // that is not placed yet — sent with CreateOrder.
  const [serviceChargeAuth, setServiceChargeAuth] = useState<AdjustmentAuthBody | null>(null)
  // Mirrored in a ref: the cart payload reads it, and a resubmit after the
  // reason/PIN dialog runs before React re-renders with the new state.
  const serviceChargeAuthRef = useRef<AdjustmentAuthBody | null>(null)
  serviceChargeAuthRef.current = serviceChargeAuth
  // Saved addresses of the last-picked customer (F-FE-N29). Lives here (not in
  // the guest popover, which unmounts on close) so the default can still fill
  // the delivery field when the cashier picks the customer FIRST and switches
  // the order type to delivery afterwards.
  const [customerSavedAddresses, setCustomerSavedAddresses] = useState<CustomerQuickAddress[]>([])
  const [cart, setCart] = useState<CartItem[]>([])
  const [orderNotes, setOrderNotes] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [createCheckoutIntent, setCreateCheckoutIntent] = useState<'cash' | 'card' | 'online'>('cash')

  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null)
  const [paymentCheckoutIntent, setPaymentCheckoutIntent] = useState<'cash' | 'card' | 'online'>('cash')

  // F-MM-N7: `onPrimaryPay` awaits an in-flight tender switch, so anything it
  // reads afterwards must not come from the closure captured at tap time —
  // that snapshot predates the re-price. These refs always hold the latest.
  const selectedOrderRef = useRef<Order | null>(null)
  selectedOrderRef.current = selectedOrder
  const paymentCheckoutIntentRef = useRef<'cash' | 'card' | 'online'>('cash')
  paymentCheckoutIntentRef.current = paymentCheckoutIntent
  const [cashReceived, setCashReceived] = useState('')
  const [cashModalOpen, setCashModalOpen] = useState(false)
  // Selected online provider (Easypaisa/JazzCash/…), migration 050. Shown inline
  // in the payment panel when Online is selected and the tenant has active
  // sub-methods; the Charge CTA requires a pick.
  const [selectedOnlineSub, setSelectedOnlineSub] = useState<string | null>(null)
  // Split bill (F-FE-N19). The session itself lives on the order
  // (payOrder.split_state) — this only controls the dialog's visibility.
  const [splitDialogOpen, setSplitDialogOpen] = useState(false)
  const [discountMode, setDiscountMode] = useState<'amount' | 'percent'>('amount')
  const [discountValue, setDiscountValue] = useState('')
  // Triggered when the backend returns 409 previous_day_open — counter
  // staff cannot ring sales until yesterday's day is closed.
  const [previousDayBlocker, setPreviousDayBlocker] = useState<{
    open: boolean
    message?: string
  }>({ open: false })

  // F-CTRL-N32: backend returns 409 `day_not_open` when nobody has started
  // today. Auto-open was removed — a business day now begins only when a human
  // states what's in the drawer. Counter staff can do this themselves, so we
  // raise the Start-the-day dialog inline rather than sending them to find a
  // manager. The cart is untouched; they retry the sale after opening.
  const [startDayBlocker, setStartDayBlocker] = useState<{
    open: boolean
    message?: string
  }>({ open: false })

  // Manager-PIN handoff modal — opened by the Daily Closing banner when
  // a cashier-tier session needs to hand off to a manager.
  const [handoffOpen, setHandoffOpen] = useState(false)

  // Whether the logged-in user can navigate to /admin/day-close. Only
  // admin and manager have that permission; for plain counter staff we
  // surface a "ask your manager" message instead of a CTA.
  const userCanRunDayClose = useMemo(() => {
    try {
      const raw = localStorage.getItem('pos_user')
      if (!raw) return false
      const u = JSON.parse(raw) as { role?: string }
      return u.role === 'admin' || u.role === 'manager'
    } catch {
      return false
    }
  }, [])

  // Raw role string for the banner so it can render either the
  // "Close now" (manager) or "Get manager" (cashier) CTA.
  const userRole = useMemo(() => {
    try {
      const raw = localStorage.getItem('pos_user')
      if (!raw) return ''
      const u = JSON.parse(raw) as { role?: string }
      return u.role ?? ''
    } catch {
      return ''
    }
  }, [])

  // Shared connectivity signal (proactive UX only — the authoritative failure
  // signal is ApiClientError.isNetworkError in the submit path). Drives the
  // offline place-order guard, the honest hint, and the reconnect nudge.
  // F-FE-N30: the OS signal alone is not enough. With the router up and the ISP
  // down — the outage restaurants actually have — navigator.onLine stays true,
  // so every `!online` branch below (dine-in table select, the fire button, the
  // capture path) silently never fired. Treat the terminal as connected only
  // when the backend has also actually answered.
  const osOnline = useOnline()
  const { backendReachable } = useOnlineAndReachable()
  const online = osOnline && backendReachable
  // Per-cashier key for the local order draft (data-loss guard). Null if
  // pos_user is unreadable — persistence then no-ops rather than writing an
  // un-namespaced key another cashier could inherit.
  const draftUserId = useMemo(() => getDraftUserId(), [])

  /**
   * Context for the post-payment PRA tax invoice prompt. When non-null, the
   * PraInvoicePromptModal is shown; the captured order/settings/payment are
   * reused to build the second slip without re-querying the server.
   */
  const [praPromptContext, setPraPromptContext] = useState<{
    order: Order
    settings: CustomerReceiptSettings
    paymentMethod: string
    paidAt: Date
  } | null>(null)
  const [praPrinting, setPraPrinting] = useState(false)
  /** Full-screen thank-you after a completed payment (customer-facing terminal). */
  const [checkoutCelebration, setCheckoutCelebration] = useState<CheckoutCelebrationMode | null>(null)
  const [historyReadOnlyOrder, setHistoryReadOnlyOrder] = useState<Order | null>(null)
  const [provisionalPrintBusy, setProvisionalPrintBusy] = useState(false)
  const [historyGuestPrintBusy, setHistoryGuestPrintBusy] = useState(false)
  /** When set, cart adds lines to this existing table order (occupied table flow). */
  const [continuingOrderId, setContinuingOrderId] = useState<string | null>(null)
  /**
   * F-FE-N31: the offline-held order the cart is appending to.
   *
   * Deliberately separate from continuingOrderId, which names an order that
   * EXISTS server-side. This one lives only in this device's queue: there is
   * nothing to fetch, nothing to pay, and the append is a local mutation that
   * replays later as part of a single create.
   */
  const [offlineTarget, setOfflineTarget] = useState<OfflineOrder | null>(null)
  /**
   * The held offline order the cashier opened the payment sheet for
   * (F-CTRL-N55). Just the entry — its value is re-derived from the cached menu
   * on demand, because a queue entry stores no prices by design.
   */
  const [offlinePayTarget, setOfflinePayTarget] = useState<OfflineOrder | null>(null)
  const [offlinePayBusy, setOfflinePayBusy] = useState(false)
  /**
   * Bumped after an append so the tray re-reads immediately. The pending COUNT
   * is unchanged by an append — without this nonce the row's item count stayed
   * stale until the next 5s poll, which reads as "the round didn't land".
   */
  const [offlineTrayNonce, setOfflineTrayNonce] = useState(0)
  /** Full order data when continuing an existing order (includes items already sent to kitchen). */
  const [existingOrder, setExistingOrder] = useState<Order | null>(null)
  /** Brief highlight on product tile after a successful add-to-cart tap. */
  const [lastTappedProductId, setLastTappedProductId] = useState<string | null>(null)
  const tapFlashTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Per-tap nonce — drives the "+1" pop animation on the product card by being
  // included in the rendered <span>'s React key, so every increment forces a
  // remount + fresh keyframe re-fire (no state-cleanup timer needed because
  // the CSS animation ends at opacity 0 via `forwards`).
  const [tapPop, setTapPop] = useState<{ productId: string; nonce: number }>({
    productId: '',
    nonce: 0,
  })
  // Brief highlight on cart row after add / quantity bump (slightly longer
  // than the product tile). Keyed by LINE, not product: two lines of the same
  // product must be able to flash independently.
  const [lastCartFlashLineId, setLastCartFlashLineId] = useState<string | null>(null)
  const cartFlashTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Keyed by cart LINE id (not product id) since F-MOD-N2. Because line ids
  // are minted fresh per line rather than drawn from the bounded catalogue,
  // the map has to be emptied whenever the cart is — see clearCart.
  const cartRowRefs = useRef<Record<string, HTMLTableRowElement | null>>({})
  const [cartLiveAnnouncement, setCartLiveAnnouncement] = useState<{ text: string; id: number }>({ text: '', id: 0 })

  /**
   * Empty the cart and drop the row refs with it.
   *
   * Before line ids existed, `cartRowRefs` was keyed by product id and so was
   * naturally bounded by the size of the menu. Line ids are unique per line,
   * so a shift's worth of cleared carts would otherwise leave thousands of
   * dead entries behind. Every place that used to call `setCart([])` calls
   * this instead.
   */
  /**
   * The options sheet. `editingLineId` non-null means we are changing an
   * existing staged line rather than adding a new one.
   */
  const [configurator, setConfigurator] = useState<{
    product: Product
    initial: ConfiguredLine | null
    editingLineId: string | null
    /** F-DEAL-N4: "make it a deal" — on confirm, take one unit off this line. */
    dealUpgradeFromLineId?: string | null
  } | null>(null)

  const clearCart = useCallback(() => {
    setCart([])
    cartRowRefs.current = {}
  }, [])
  const [existingItemsExpanded, setExistingItemsExpanded] = useState(false)
  const [tableTransferOpen, setTableTransferOpen] = useState(false)
  const [discardCartConfirmOpen, setDiscardCartConfirmOpen] = useState(false)
  /**
   * Set when a tender switch off 'card' was refused because a bank card
   * promo is applied (409 card_promo_requires_card_tender). The dialog
   * offers "remove the promo and switch" — the cashier makes the money
   * decision, never this component.
   */
  const [cardPromoTenderConflict, setCardPromoTenderConflict] = useState<{
    orderId: string
    intent: 'cash' | 'card' | 'online'
  } | null>(null)
  const [tablesPickerOpen, setTablesPickerOpen] = useState(false)
  const [voidModal, setVoidModal] = useState<{
    orderId: string; itemId: string; itemName: string; qty: number; unitPrice: number
    /** Whole-order void: every voidable line under one reason + PIN. */
    wholeOrder?: boolean
  } | null>(null)
  /**
   * Inline affirmation after items fire to the kitchen. Replaces the previous
   * floating "Add-on sent" toast that overlapped the Totals + primary CTA in
   * the ticket rail. Auto-clears after a few seconds.
   */
  const [firedConfirmation, setFiredConfirmation] = useState<
    { count: number; mode: 'new' | 'continue' } | null
  >(null)
  const firedConfirmationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // "Confirm before printing" (café) flow: when the kitchen setting is on, Fire
  // KOT opens this Print/Skip dialog instead of auto-printing.
  const [kotPrintOpen, setKotPrintOpen] = useState(false)
  const [lastFireKots, setLastFireKots] = useState<StationKOT[] | undefined>(undefined)
  /**
   * Inline banner shown above the remaining-balance row inside the checkout
   * dialog after a partial payment is recorded. Replaces a toast that would
   * otherwise float away from the operator's point of focus.
   */
  const [partialPaymentBanner, setPartialPaymentBanner] = useState<string | null>(null)

  const [checkoutRailPx, setCheckoutRailPx] = useState(readCheckoutRailWidth)
  /** Below lg (1024px): toggle between menu browser and ticket/checkout rail.
   *  From lg (1024px) up we show the side-by-side split + drag-to-resize
   *  handle; phones AND portrait tablets keep the cleaner tabbed UI.
   *
   *  F-FE-N145: this was md (768px), and the tradeoff noted here -- that the
   *  split "can feel cramped on ~768-900px" -- stopped being acceptable when a
   *  ~800px portrait tablet cleared the bar by a few dozen pixels and lost the
   *  tabs, leaving a ~430px menu beside the 300px COUNTER_RAIL_MIN rail. 1024
   *  also matches the offline banner, which already treats lg as the desktop /
   *  landscape-tablet line.
   */
  const [mobileWorkspaceTab, setMobileWorkspaceTab] = useState<'menu' | 'ticket'>(
    'menu',
  )
  // Keep this in sync with the `lg:` split-layout classes in the render below.
  const isSplitLayout = useMediaQuery('(min-width: 1024px)')
  const counterSplitRef = useRef<HTMLDivElement>(null)
  const railDragRef = useRef<{ startX: number; startW: number; maxW: number } | null>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const railScrollRef = useRef<HTMLDivElement>(null)

  const queryClient = useQueryClient()
  const { formatCurrency } = useCurrency()

  // F-FE-N176: the one owner for refreshing the Open Orders strip + tables on
  // this screen. Every surface that used to invalidate them directly around a
  // payment (stream events, checkout-intent, payment onSuccess) asks through
  // here, so one payment reads GET /orders once instead of 4–5 times.
  const listRefresherRef = useRef<CounterListRefresher | null>(null)
  if (listRefresherRef.current == null) {
    listRefresherRef.current = createCounterListRefresher(queryClient)
  }
  const listRefresher = listRefresherRef.current
  useEffect(() => () => listRefresherRef.current?.dispose(), [])

  const {
    enabledIds: enabledOrderTypeIds,
    raw: orderTypesRaw,
    marketplaceSettledEnabled,
  } = useEnabledOrderTypes()

  const counterOrderToggleOptions = useMemo<CounterOrderTypeOption[]>(() => {
    return orderTypesRaw.map((r) => ({
      id: r.id,
      label: (r.label && r.label.trim()) || r.id,
      description:
        ORDER_TYPE_TAB_DESCRIPTIONS[normalizeOrderTypeId(r.id)] ?? 'Counter checkout',
      color: sanitizeOrderTypeHexColor(r.color, '#64748b'),
    }))
  }, [orderTypesRaw])

  const firstEnabledOrderTypeId = useMemo(() => {
    const row = orderTypesRaw.find((r) => enabledOrderTypeIds.has(normalizeOrderTypeId(r.id)))
    return row?.id ?? 'dine_in'
  }, [orderTypesRaw, enabledOrderTypeIds])

  const createOrderPricingExtras = useMemo(
    () => getOrderTypePricing(orderType, orderTypesRaw, { marketplaceSettledEnabled }),
    [orderType, orderTypesRaw, marketplaceSettledEnabled]
  )

  // Live delivery fee on the not-yet-placed ticket: a manual override when set,
  // otherwise the settings default for the order type.
  const effectiveDeliveryFee =
    orderType === 'delivery' && deliveryFeeOverride != null
      ? deliveryFeeOverride
      : createOrderPricingExtras.deliveryFee

  // Delivery orders can't be placed without a phone + address. The server
  // enforces this too (delivery_details_required); the gate keeps the cashier
  // off the 400 and points them at the Guest details popover.
  const deliveryDetailsMissing =
    orderType === 'delivery' && (!customerPhone.trim() || !deliveryAddress.trim())

  // A typed guest phone becomes a customers row on the server, so it has to
  // look like a phone number. Same gate shape as delivery details: stop the
  // cashier here rather than at the 400, and only when a new order is being
  // created (continuing an existing tab doesn't re-resolve the customer).
  const guestPhoneError = customerPhoneShapeError(customerPhone)

  // If an admin disables the currently-selected order type mid-session,
  // auto-switch to the first enabled type and clear transient session state
  // (table, customer, checkout) so the cashier isn't stuck on a hidden tab.
  // Mirrors the reset logic in CounterOrderTypeToggle's onChange handler below.
  useEffect(() => {
    if (enabledOrderTypeIds.size === 0) return
    if (enabledOrderTypeIds.has(normalizeOrderTypeId(orderType))) return
    setOrderType(firstEnabledOrderTypeId)
    setDineInSession(null)
    setSelectedTable(null)
    setCheckoutOpen(false)
    setSelectedOrder(null)
    // Guest fields deliberately survive an order-type change (F-FE-N29): the
    // cashier just captured them and re-searching the customer on every type
    // switch was real counter friction. They still clear on order completion.
    setDeliveryAddress('')
    setDeliveryFeeOverride(null)
    setServiceChargeOverride(null)
    setServiceChargeAuth(null)
  }, [enabledOrderTypeIds, orderType, firstEnabledOrderTypeId])

  // F-FE-N29: fill the delivery field from the picked customer's saved default
  // when the order BECOMES a delivery. The pick-time prefill (guest popover)
  // only fires when the order is already a delivery; this covers the cashier
  // who attaches the customer first and switches the order type after. Only
  // when the field is empty — never clobber a typed address, and deliberately
  // not re-triggered by the cashier clearing the field.
  useEffect(() => {
    if (orderType !== 'delivery') return
    if (customerSavedAddresses.length === 0) return
    const def = customerSavedAddresses.find((a) => a.is_default) ?? customerSavedAddresses[0]
    setDeliveryAddress((cur) => (cur.trim() ? cur : def.address))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderType, customerSavedAddresses])

  const dismissCheckoutCelebration = useCallback(() => {
    setCheckoutCelebration(null)
  }, [])

  // Mark this route so the shadcn ToastViewport repositions to top-center
  // (see index.css -> body[data-counter-route="true"]). The default bottom-right
  // viewport would otherwise overlap the Totals + primary CTA on the checkout rail.
  useEffect(() => {
    if (typeof document === 'undefined') return
    document.body.setAttribute('data-counter-route', 'true')
    return () => {
      document.body.removeAttribute('data-counter-route')
    }
  }, [])

  // Clear the partial-payment affirmation whenever the checkout dialog closes
  // or the operator switches to a different order so a stale banner never
  // leaks across tickets.
  useEffect(() => {
    if (!checkoutOpen) setPartialPaymentBanner(null)
  }, [checkoutOpen, selectedOrder?.id])

  useEffect(() => {
    return subscribeOrderReady((e) => {
      toastHelpers.success(
        'Ready for pickup',
        `Order #${e.orderNumber} — kitchen bumped (${Math.floor(e.completionSeconds / 60)}m ${e.completionSeconds % 60}s)`
      )
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      listRefresher.request({ tables: true })
    })
  }, [queryClient, listRefresher])

  // Live floor + open-orders freshness across terminals: a table paid/cleared
  // or a tab opened/fired on another device broadcasts on the dashboard stream.
  // Tables and the Open Orders strip must invalidate together — otherwise the
  // picker shows occupied tables while the strip keeps a stale snapshot
  // (audit/OPEN_ORDERS_STRIP_HIDDEN_2026-07-23.md).
  //
  // order_updated is handled separately from isTableAffectingEvent (which is
  // scoped to the TABLES cache, where a guest edit truly changes nothing): it
  // refreshes the open-orders strip, and when the event is a guest attach for
  // the order currently open on the rail — e.g. from a staff phone on /scan —
  // it re-reads that order so the rail's LOCAL guest fields update too. The
  // ref (not state) keeps this callback's identity stable for the stream hook.
  const activeRailOrderIdRef = useRef<string | null>(null)
  const handleTableStream = useCallback(
    (ev: DashboardStreamEvent) => {
      // F-CTRL-N72: a cart was held / resumed / discarded on some till.
      if (ev.type === 'held_carts_changed') {
        queryClient.invalidateQueries({ queryKey: ['counterHeldCarts'] })
        return
      }
      if (ev.type === 'order_updated') {
        listRefresher.request()
        const activeId = activeRailOrderIdRef.current
        if (ev.extra?.change === 'guest' && activeId && ev.order_id === activeId) {
          void apiClient.getOrder(activeId).then((res) => {
            // Guard against the rail having moved to another order meanwhile.
            if (!res.success || !res.data || activeRailOrderIdRef.current !== activeId) return
            const o = res.data
            setCustomerName(o.customer_name ?? '')
            setCustomerEmail(o.customer_email ?? '')
            setCustomerPhone(o.customer_phone ?? '')
            setGuestBirthday(toGuestDateInputValue(o.guest_birthday))
            setDineInSession((prev) =>
              prev
                ? {
                    ...prev,
                    customerName: o.customer_name,
                    customerEmail: o.customer_email,
                    customerPhone: o.customer_phone,
                    guestBirthday: o.guest_birthday,
                  }
                : prev,
            )
          })
        }
        return
      }
      if (!isTableAffectingEvent(ev)) return
      // F-FE-N176: a payment emits `payment` and (dine-in) `table_status_changed`
      // in the same millisecond, and the paying till's own onSuccess asks again.
      // The refresher folds them into one read; the note lets onSuccess see the
      // stream already covered its payment.
      if (ev.type === 'payment') listRefresher.notePaymentEvent(ev.order_id)
      listRefresher.request({ tables: true })
    },
    [queryClient, listRefresher],
  )
  const streamStatus = useDashboardStream({ onEvent: handleTableStream })

  useEffect(() => {
    return () => {
      if (tapFlashTimeoutRef.current != null) {
        clearTimeout(tapFlashTimeoutRef.current)
        tapFlashTimeoutRef.current = null
      }
      if (cartFlashTimeoutRef.current != null) {
        clearTimeout(cartFlashTimeoutRef.current)
        cartFlashTimeoutRef.current = null
      }
      if (firedConfirmationTimeoutRef.current != null) {
        clearTimeout(firedConfirmationTimeoutRef.current)
        firedConfirmationTimeoutRef.current = null
      }
    }
  }, [])

  const { data: categories = [] } = useCategories()

  // The whole catalogue, once. Category is a client-side view over this list,
  // NOT a separate fetch.
  //
  // It used to be keyed on selectedCategory, which made the category the data
  // SOURCE rather than a filter — so with "Matcha" selected the cashier's search
  // box could only ever see those 5 items, and typing "brownie" returned
  // nothing. It also meant a network round-trip on every category tap.
  //
  // Fetching everything is cheap here: the largest catalogue in the fleet is
  // ~200 products. getAllProducts() walks every page — /products is capped at
  // 100/page, and a single call silently truncated the menu on bigger tenants.
  const {
    data: products = [],
    isError: productsLoadFailed,
    refetch: refetchProducts,
  } = useQuery({
    queryKey: ['products', 'all'],
    queryFn: () => apiClient.getAllProducts(),
    // F-FE-N114: the walk retries each page itself; this is the outer retry for
    // a whole walk lost to a link that is down for seconds at a time. Default
    // backoff (1s, 2s, 4s) gives it room to come back before the failure state
    // below is shown.
    retry: 3,
  })

  const { data: tables = [] } = useQuery({
    queryKey: ['tables'],
    // `?? []` and not just the `= []` destructuring default above: that default
    // only fires for undefined, so a JSON `null` body sails past it and becomes
    // the query data verbatim — and sortedTables spreads this ([...tables]).
    queryFn: () => apiClient.getTables().then((res) => res.data ?? []),
    // SSE is the primary freshness signal, but with the stream down a table
    // freed elsewhere (server settle, auto-release sweeper) would stay
    // occupied on the picker forever. Poll as the safety net.
    refetchInterval: streamStatus === 'live' ? 60_000 : 15_000,
  })

  // --- Local order-draft persistence (data-loss guard) -----------------------
  // Persist the NEW-order draft (only while continuingOrderId == null — once it
  // becomes a server-side order it's no longer a draft) so a reload/crash/outage
  // doesn't vaporize a staged cart. Does NOT enable offline selling.
  const draftFields: DraftFields = useMemo(
    () => ({
      orderType,
      cart: cart.map((i) => ({
        productId: i.product.id,
        quantity: i.quantity,
        special_instructions: i.special_instructions,
        // Option ids and counts only. Omitted when empty so a plain cart's
        // draft stays byte-identical to what the v1 schema wrote.
        ...(i.selections.length > 0 ? { selections: i.selections } : {}),
      })),
      orderNotes,
      selectedTableId: selectedTable?.id ?? null,
      customerName,
      customerEmail,
      customerPhone,
      guestBirthday,
      deliveryAddress,
      deliveryFeeOverride,
      serviceChargeOverride,
    }),
    [
      orderType,
      cart,
      orderNotes,
      selectedTable,
      customerName,
      customerEmail,
      customerPhone,
      guestBirthday,
      deliveryAddress,
      deliveryFeeOverride,
      serviceChargeOverride,
    ],
  )
  // Only a NEW order's cart is a draft worth restoring. When the cart is
  // destined for a ticket that already exists — server-side or held offline —
  // saving it would resurrect that round onto a fresh order after a restart.
  //
  // F-FE-N159: and never before the one-time restore below has run. The screen
  // mounts with an empty cart and the restore waits for the menu; autosaving
  // that empty cart 500ms in used to DELETE the saved draft whenever the menu
  // took longer than that to load, so a reload lost the staged order it exists
  // to protect. Until the restore settles, the saved draft is left untouched.
  const [draftRestoreSettled, setDraftRestoreSettled] = useState(false)
  useDraftAutosave(
    draftUserId,
    draftFields,
    draftRestoreSettled && continuingOrderId == null && offlineTarget == null
  )

  // Load a saved snapshot (local draft or a resumed held cart, F-CTRL-N72) into
  // the composer. `items` must already be reconciled against the live menu by
  // restoreDraftItems, so prices are always current.
  const applySnapshotFields = useCallback(
    (snap: DraftFields, items: CartItem[]): { tableDropped: boolean } => {
      setCart(items)
      setOrderType(snap.orderType || 'dine_in')
      setOrderNotes(snap.orderNotes ?? '')
      setCustomerName(snap.customerName ?? '')
      setCustomerEmail(snap.customerEmail ?? '')
      setCustomerPhone(snap.customerPhone ?? '')
      setGuestBirthday(snap.guestBirthday ?? '')
      setDeliveryAddress(snap.deliveryAddress ?? '')
      setDeliveryFeeOverride(snap.deliveryFeeOverride ?? null)
      setServiceChargeOverride(snap.serviceChargeOverride ?? null)
      // Only re-bind the table if it's still free — never stomp a table another
      // cashier opened while this cart was saved.
      if (snap.selectedTableId) {
        const t = tables.find((tb) => tb.id === snap.selectedTableId)
        if (t && !t.is_occupied) {
          setSelectedTable(t)
          return { tableDropped: false }
        }
        return { tableDropped: true }
      }
      return { tableDropped: false }
    },
    [tables],
  )

  // Restore once, on mount, only into an empty new-order slate, and only after
  // the menu has loaded so items reconcile against live products/prices.
  const draftRestoreAttemptedRef = useRef(false)
  useEffect(() => {
    if (draftRestoreAttemptedRef.current) return
    if (!draftUserId) {
      // No user to key on: autosave is a no-op anyway, nothing to protect.
      draftRestoreAttemptedRef.current = true
      setDraftRestoreSettled(true)
      return
    }
    if (continuingOrderId != null || cart.length > 0) {
      // Something was loaded before the menu arrived — that work is newer than
      // any saved draft, so let autosave own it from here.
      draftRestoreAttemptedRef.current = true
      setDraftRestoreSettled(true)
      return
    }
    // Reconcile against the FULL product list. `products` is now always the
    // full catalogue (category is a client-side view), so the old
    // category-dependent fallback is gone.
    const fullProducts = queryClient.getQueryData<Product[]>(['products', 'all']) ?? products
    if (!fullProducts || fullProducts.length === 0) return // wait for the menu
    draftRestoreAttemptedRef.current = true
    setDraftRestoreSettled(true)
    const draft = readDraft(draftUserId)
    if (!draft || draft.cart.length === 0) return
    const { items, dropped } = restoreDraftItems(draft.cart, fullProducts)
    if (items.length === 0) {
      clearDraft(draftUserId)
      return
    }
    const { tableDropped } = applySnapshotFields(draft, items)
    toastHelpers.info(
      'Unfinished order restored',
      (dropped > 0
        ? `Your staged order is back — ${dropped} item${dropped > 1 ? 's' : ''} removed (no longer available).`
        : 'Your staged order is back.') + (tableDropped ? ' Its table is now taken — pick a table again.' : ''),
    )
  }, [draftUserId, continuingOrderId, cart.length, products, tables, queryClient, applySnapshotFields])

  // Reconnect nudge: when connectivity returns and a held new-order draft is
  // still staged, invite a manual place (single tap on the re-enabled CTA). We
  // never auto-fire — there's no idempotency key, so an auto-resubmit after a
  // flapping connection could duplicate the order.
  const prevOnlineRef = useRef(online)
  useEffect(() => {
    const was = prevOnlineRef.current
    prevOnlineRef.current = online
    if (!was && online && continuingOrderId == null && cart.length > 0) {
      toastHelpers.info('Back online', 'You can place your unfinished order now.')
    }
  }, [online, continuingOrderId, cart.length])
  // --- end order-draft persistence -------------------------------------------

  // Daily Closing banner data. Reuses the same React Query key the
  // AdminSidebar polls (1-min refetch) so we share cache and don't
  // double the request load.
  // Counter-scope endpoint (/day-ops/current, admin+manager+counter) rather
  // than the /admin one (admin+manager). This previously 403'd for cashier
  // sessions, so `currentDay` was permanently null for them and DailyClosingBanner
  // never rendered — despite having a "Get manager" CTA written for exactly that
  // role. Same handler, same payload, same cache key; only the gate differs.
  const { data: dayOpsCurrentRes, isPending: dayOpsCurrentPending } = useQuery({
    queryKey: ['day-ops', 'current'],
    queryFn: () => apiClient.getDayOpsCurrentCounter(),
    staleTime: 30_000,
    refetchInterval: 60_000,
  })
  const currentDay = dayOpsCurrentRes?.data ?? null

  // F-FE-N30 — offline order capture.
  //
  // F-CTRL-N52: this used to pass `currentDay?.business_date ?? ''` in as
  // `todayISO`, because the reserved-number pool was keyed to a business date.
  // The comment here claimed the value survived an outage via the persisted
  // query cache. It does not: `['day-ops','current']` is live data and is
  // deliberately excluded from PERSIST_PREFIXES (routes/__root.tsx). So a
  // terminal restarted mid-outage read it as '', matched no pool, and refused to
  // sell — while holding twenty valid numbers filed under the real date. It was
  // also '' every morning before the day was opened.
  //
  // Capture needs no date now. The ticket is minted locally and the business day
  // is resolved server-side from captured_at at replay, which is the only place
  // it can be resolved correctly anyway.
  const offlineCapture = useOfflineCapture({ online })

  /** Name printed on the "Server:" line of an offline kitchen ticket. */
  const offlineServerName = useMemo(() => {
    try {
      const raw = localStorage.getItem('pos_user')
      if (!raw) return '—'
      const u = JSON.parse(raw) as { username?: string }
      return u.username?.trim() || '—'
    } catch {
      return '—'
    }
  }, [])

  /**
   * Reset the composer after an offline capture. Mirrors the online success
   * path so the two feel identical to the cashier — and clears the draft, since
   * the order now lives in the offline queue rather than as an unplaced cart.
   *
   * `keepTable` is set when a held order stays loaded for its next round
   * (F-FE-N31): dropping the table there would print the following round's slip
   * with no table on it, sending the runner hunting.
   */
  const clearOfflineCapturedCart = useCallback((keepTable = false) => {
    clearCart()
    setOrderNotes('')
    if (!keepTable) setSelectedTable(null)
    clearDraft(draftUserId)
  }, [draftUserId])

  // Report the outcome of the automatic post-reconnect drain. The unreconciled
  // count gets its own warning rather than being folded into the success line —
  // those items were fired to the kitchen and could NOT be billed, so they need
  // a human, not a green tick.
  useEffect(() => {
    offlineCapture.onReplayComplete((s) => {
      if (s.synced > 0) {
        // F-CTRL-N37: the synced orders now exist server-side, but every query
        // holding them is still showing the pre-outage snapshot — so the strip
        // stayed empty until the cashier reloaded by hand, which nobody in a
        // service would think to do. Refresh the surfaces the orders land on.
        queryClient.invalidateQueries({ queryKey: ['orders'] })
        queryClient.invalidateQueries({ queryKey: ['counterPendingOrders'] })
        invalidateTables(queryClient)
        toastHelpers.success('Back online', `${s.synced} offline order(s) synced.`)
      }
      if (s.unreconciled > 0) {
        toastHelpers.warning(
          'Needs attention',
          `${s.unreconciled} offline item(s) were sent to the kitchen but could not be billed.`,
        )
      }
      // F-CTRL-N54: synced, but not marked fired. The sale is safe; the risk is
      // the NEXT fire on that order sweeping up lines the kitchen already
      // cooked and printing a duplicate round. Cheaper to say so now than to let
      // the kitchen discover it.
      // F-CTRL-N55: an offline payment that did not reconcile. The row is
      // already in the queue and Day Close will block on it; this makes it
      // visible now, while the cashier still remembers the transaction.
      if (s.needsAttention > 0) {
        toastHelpers.warning(
          'Payment needs a manager',
          `${s.needsAttention} offline payment(s) did not reconcile — the amount billed ` +
            'differs from what was collected, or the payment could not be recorded. ' +
            'Resolve them in Day Close before counting the till.',
        )
      }
      if (s.needsFire > 0) {
        toastHelpers.warning(
          'Check the kitchen ticket',
          `${s.needsFire} synced order(s) could not be marked as already fired. ` +
            'Fire them from the order before adding anything, or the kitchen gets a repeat.',
        )
      }
    })
  }, [offlineCapture])

  const { data: closeoutHourSetting } = useQuery({
    queryKey: ['settings', 'day_boundary_hour'],
    queryFn: () => apiClient.getSetting('day_boundary_hour'),
    staleTime: 5 * 60_000,
  })
  const closeoutHour = useMemo(() => {
    const raw = closeoutHourSetting?.data
    const n = typeof raw === 'number' ? raw : Number(raw)
    return Number.isFinite(n) && n >= 0 && n <= 23 ? n : 5
  }, [closeoutHourSetting])

  const navigate = useNavigate()
  const counterSearch = useSearch({ from: '/admin/counter' })

  const { data: pricingRaw } = useQuery({
    queryKey: ['counterPricing'],
    queryFn: () => apiClient.getCounterPricing().then((r) => (r.success && r.data ? r.data : null)),
  })

  const pricing = useMemo(() => mergePricingSettings(pricingRaw), [pricingRaw])

  // Is a promo redemption active on the currently-open ticket? Used to
  // disable the per-line discount affordance with a helpful tooltip,
  // so cashiers see why before the server returns 409 conflict_promo.
  const activeRailOrderId = existingOrder?.id ?? selectedOrder?.id ?? null
  // Latest-value ref for the stream handler (declared above with it) — the
  // callback must stay identity-stable, so it reads this instead of state.
  activeRailOrderIdRef.current = activeRailOrderId
  // Cache the raw APIResponse envelope (read `.data` below), matching the other
  // two consumers of this key — CounterLoyaltyChip and CounterPromoCodeBlock,
  // which render on the same open ticket and read `.data`. Unwrapping here
  // poisoned the shared cache so those two saw "no promo applied" (→ double-apply
  // → 409 conflict_promo). Same bug class as F-FE-N6.
  const { data: activePromoRes } = useQuery({
    queryKey: ['counter-applied-promo', activeRailOrderId],
    queryFn: () =>
      activeRailOrderId
        ? apiClient.getAppliedPromo(activeRailOrderId)
        : Promise.resolve(null),
    enabled: Boolean(activeRailOrderId),
    staleTime: 5_000,
  })
  const hasActivePromo = Boolean(activePromoRes?.data?.promo_code_id)

  // Applied bank card promo on the open ticket — drives the "HBL Card
  // Discount (20%)" label on the bill totals + receipt and disables the
  // per-line discount affordance (mutex, same as promo codes). Shares the
  // query key with CounterCardPromoBlock, so cache stays consistent.
  const { data: activeCardPromoRes } = useQuery({
    queryKey: ['counter-applied-card-promo', activeRailOrderId],
    queryFn: () =>
      activeRailOrderId
        ? apiClient.getAppliedCardPromo(activeRailOrderId)
        : Promise.resolve(null),
    enabled: Boolean(activeRailOrderId),
    staleTime: 5_000,
  })
  const activeCardPromo = activeCardPromoRes?.data
  const hasActiveCardPromo = Boolean(activeCardPromo?.applied)
  const activeCardPromoLabel = hasActiveCardPromo ? activeCardPromo?.label ?? null : null
  // F-MKT-N2: an item-only promo code names itself on the bill-discount row
  // ("Promo DRINKS20 · Beverages"), so guest and cashier can see the discount
  // didn't cover the whole bill. Whole-bill codes keep the generic label.
  const activeDiscountLabel = activeCardPromoLabel ?? scopedPromoDiscountLabel(activePromoRes?.data)

  const { data: pendingOrders = [], isLoading: pendingOrdersLoading } = useQuery({
    queryKey: ['counterPendingOrders'],
    queryFn: async () => {
      // open_only=true: server returns non-terminal rows only, so a flood of
      // completed takeaways cannot push older open dine-in bills out of the
      // last-N page (P1 — OPEN_ORDERS_STRIP_HIDDEN_2026-07-23).
      const r = await apiClient.getOrders({ per_page: 100, open_only: true })
      const list = Array.isArray(r.data) ? r.data : []
      return list.filter((o) => isOpenOrdersStripMember(o))
    },
    // Match tables: SSE is primary; poll so a dead stream cannot freeze the
    // strip for a whole shift while the floor still updates.
    refetchInterval: streamStatus === 'live' ? 60_000 : 15_000,
  })

  // Held carts (F-CTRL-N72) — parked, un-placed carts any till can resume.
  // SSE (held_carts_changed) is the primary signal; poll as the safety net.
  const { data: heldCarts = [], isLoading: heldCartsLoading } = useQuery({
    queryKey: ['counterHeldCarts'],
    queryFn: async () => {
      const r = await apiClient.listHeldCarts()
      return Array.isArray(r.data) ? r.data : []
    },
    enabled: online,
    refetchInterval: streamStatus === 'live' ? 60_000 : 15_000,
  })

  const ordersToCloseStrip = useMemo(
    () => buildOpenOrdersStrip(pendingOrders, tables ?? [], orderType),
    [orderType, pendingOrders, tables]
  )

  // The strip only shows orders of the active order type, so its surface is
  // tinted with that type's configured color (light purple under Delivery,
  // etc.) to make the tab→rail relationship visible. The amber dot/label stay
  // as the constant "needs attention" cue; amber is also the fallback surface
  // when the order type has no color configured.
  const railAccent = colorForOrderTypeId(orderTypesRaw, orderType)
  const railSurfaceStyle = railAccent
    ? { background: `color-mix(in srgb, ${railAccent} 7%, hsl(var(--card)))` }
    : undefined

  const { data: paymentOrderDetail, isFetching: paymentOrderFetching } = useQuery({
    queryKey: ['order', selectedOrder?.id, 'payment-panel'],
    queryFn: async () => {
      const r = await apiClient.getOrder(selectedOrder!.id)
      if (!r.success || !r.data) throw new Error(r.message || 'Failed to load order')
      return r.data
    },
    enabled: Boolean(selectedOrder && checkoutOpen),
  })

  const orderForPayment = paymentOrderDetail ?? selectedOrder ?? null
  /** Resolved order for payment panel (always defined when an order is selected). */
  const payOrder = selectedOrder ? (orderForPayment ?? selectedOrder) : null
  /**
   * The order the payment panel is actually rendering, readable from inside an
   * async handler (F-MM-N10). `onPrimaryPay` awaits a tender switch before it
   * sends, so it needs a ref rather than the render-time closure — and it needs
   * THIS copy, the one the Charge button's figure is drawn from.
   */
  const payOrderRef = useRef<Order | null>(null)
  payOrderRef.current = payOrder

  /**
   * Is a cart submission already in flight? (F-FE-N103)
   *
   * A ref, not state, and that is the whole point. The Send button is rendered
   * `disabled={submitCartMutation.isPending}`, which LOOKS like a double-submit
   * guard and is not one: `isPending` only becomes true after React re-renders,
   * so a second activation in the same frame still sees an enabled button. At
   * Chunkyard that produced 17 duplicate orders in 60 days, created 2-49ms
   * apart — each one firing its own KOT, so the kitchen cooked the order twice
   * and the cashier had to void one at the till.
   *
   * A ref updates synchronously, so it is readable by the second call before any
   * render happens. See audit/DUPLICATE_ORDERS_CHUNKYARD_2026-08-30.md.
   */
  const submitInFlightRef = useRef(false)

  /**
   * Idempotency key for the CURRENT cart submission (F-FE-N103).
   *
   * Minted per submission and held until one succeeds — deliberately not per
   * call. Two calls racing to send the same cart must carry the SAME key, or the
   * server sees two distinct submissions and duplicates exactly as before.
   *
   * Surviving a failure is a feature, not an oversight: re-sending after a
   * timeout reuses the key, so the ambiguous case api/client.ts warns about
   * (`ECONNABORTED` — the server may already have the request) stops being able
   * to double-charge the kitchen.
   */
  const cartSubmitOpIdRef = useRef<string | null>(null)
  /**
   * F-FE-N154 — fire an order's unsent lines and route the resulting slips.
   *
   * Extracted verbatim from submitCartMutation.onSuccess, which was the ONLY
   * caller. That is precisely why an order whose fire failed had no way back:
   * the fire lived inside the submit path, so it could only ever run as part of
   * a cart submission, and a loaded order with nothing in the cart could not
   * reach it (`primaryDisabled` requires `cart.length > 0`). The rail's
   * "Send to kitchen" now calls the same code, so recovering a dropped fire is
   * one tap instead of ringing a throwaway item to drag the stale lines along.
   *
   * Never throws: a fire that fails must not take the caller down with it — the
   * order exists either way and the lines stay visible and billable.
   */
  const fireAndPrint = useCallback(
    async ({
      orderId,
      itemCount,
      mode,
    }: {
      orderId: string
      itemCount: number
      mode: 'new' | 'continue'
    }) => {
      try {
        // F-FE-N101: the third argument is load-bearing, not optional polish.
        // This screen is NOT always the desktop till — a `counter` or `manager`
        // login on the Bhookly Server tablet lands here too, and a tablet cannot
        // reach the counter's USB printer. Without the flag the backend hands the
        // slips back for THIS device to print; on Android that means
        // normalizeAgentTarget() rejects the station's Windows printer name
        // ("POSPrinter POS-80C" contains a space), the job goes out with an empty
        // target, and the APK falls back to its own default printer IP. At
        // Chunkyard every ticket a tablet fired died with EHOSTUNREACH — while
        // the VOID chit printed at the counter perfectly, because voids go
        // through PinEntryModal and PinEntryModal always sends the flag. Same
        // screen, same device, opposite outcomes, for want of this boolean.
        //
        // shouldRelayCounterPrint() is !desktop(), so the counter PC's own
        // behaviour is unchanged byte for byte.
        const fr = await apiClient.fireKOT(orderId, undefined, shouldRelayCounterPrint())
        if (!fr.success) {
          toastHelpers.error('Kitchen (KOT)', fr.message || 'Could not send order to the kitchen.')
        } else {
          void kitchenSoundService.playFireKotSound(orderId)
          // F-FE-N101: say whether the counter actually printed. No-op on the
          // desktop (nothing relays there) and on any fire with no counter-bound
          // slip. Without it a tablet's only feedback is the inline "fired"
          // flash, which is true of the kitchen but says nothing about paper —
          // exactly the silence this relay exists to prevent.
          void watchRelayedPrintJobs(fr.data?.relayed_print_jobs)
          // Inline affirmation in the ticket header instead of a floating toast
          // that would overlap the Totals + primary CTA on the checkout rail.
          if (firedConfirmationTimeoutRef.current != null) {
            clearTimeout(firedConfirmationTimeoutRef.current)
          }
          setFiredConfirmation({ count: itemCount, mode })
          firedConfirmationTimeoutRef.current = setTimeout(() => {
            setFiredConfirmation(null)
            firedConfirmationTimeoutRef.current = null
          }, 3500)
          const settingsRes = await queryClient.fetchQuery({
            queryKey: ['settings', 'all'],
            queryFn: () => apiClient.getAllSettings(),
          })
          const sd = settingsRes.data as Record<string, unknown>
          // Per-tenant "confirm before printing" (café mode): if on, open the
          // Print/Skip dialog so a counter-only café can skip the printed KOT.
          // Off (default) auto-prints straight to the routed station printers —
          // printKotReceipts handles per-station printer_target routing in both
          // paths, so the auto-routing is preserved either way.
          const confirmPrint =
            sd?.['kitchen.confirm_kot_print'] === true || sd?.['kitchen.confirm_kot_print'] === 'true'
          // F-KDS-KOT1: the KOT print dialog belongs to the printer/KOT flow
          // only. A pure-KDS fire produces no printer slip, so opening the dialog
          // just shows an empty "nothing to print" prompt — the KOT affordance
          // leaking into KDS mode. Only open it when the fire actually produced a
          // printer-routed slip (true for kot_only, and for any hybrid/kds fire
          // that hit a printer station); otherwise fall through to
          // printKotReceipts, which is a no-op for KDS-only routing.
          //
          // Android PWA only: open the Print modal so the tap is a user gesture
          // for intent:// (Chrome blocks deferred custom-scheme launches).
          // Desktop browser + Electron auto-print; Server APK uses the native bridge.
          // F-FE-N101: `!k.relayed` matters now that this screen can relay. A
          // relayed slip is already queued for the counter, so counting it here
          // would open the "tap to print" dialog for paper that is already on
          // its way — a prompt the waiter cannot satisfy. Mirrors the guard
          // KOTServerInterface has carried since F-FE-N60.
          const hasPrinterSlip = (fr.data?.kots ?? []).some(
            (k) => !k.relayed && k.output_type === 'printer',
          )
          const phoneNeedsGesture =
            isAndroidPrintClient() && hasPrinterSlip && !isBhooklyNativeServerApp()
          if ((confirmPrint || phoneNeedsGesture) && hasPrinterSlip) {
            setLastFireKots(fr.data?.kots)
            setKotPrintOpen(true)
          } else {
            printKotReceipts(fr.data?.kots, parseReceiptSettings(sd))
          }
        }
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Request failed'
        toastHelpers.error('Kitchen (KOT)', msg)
      }
      queryClient.invalidateQueries({ queryKey: ['newEnhancedKitchenOrders'] })
    },
    [queryClient],
  )


  const submitCartMutation = useMutation({
    mutationFn: async (): Promise<{
      orderId: string
      mode: 'new' | 'continue'
      itemCount: number
    }> => {
      const lines = cart.map((item) => ({
        product_id: item.product.id,
        quantity: item.quantity,
        special_instructions: item.special_instructions,
        // Option ids and counts only — the server reads every price itself
        // from modifier_options inside the order's transaction. The client
        // never sends money. CLAUDE.md invariant #11.
        ...(item.selections.length > 0
          ? {
              modifiers: item.selections.map((sel) => ({
                option_id: sel.option_id,
                quantity: sel.quantity,
              })),
            }
          : {}),
      }))
      const itemCount = cart.reduce((sum, item) => sum + item.quantity, 0)
      if (continuingOrderId) {
        // F-FE-N103: the append path duplicates just as readily as the create
        // path — the same double-fire adds the same round twice. This endpoint
        // already carries a generic idempotency guard (claimIdempotencyKey,
        // built for offline replay) that opts out on an empty key, so passing
        // one here opts the live counter in.
        const res = await apiClient.addItemsToOrder(
          continuingOrderId,
          lines,
          cartSubmitOpIdRef.current ?? undefined,
        )
        if (!res.success) {
          // F-CTRL-N30 backstop. The server refused because the order reached a
          // terminal state (409 order_not_editable) — almost always VoidItem's
          // auto-cancel firing while this terminal still had the order loaded.
          // Drop the dead reference and KEEP the cart, so the operator just taps
          // again and the items land on a fresh order instead of being retyped.
          if (res.error === ORDER_NOT_EDITABLE) {
            setContinuingOrderId(null)
            setExistingOrder(null)
            setSelectedOrder(null)
            throw new Error(
              'That order was already closed, so nothing was added. Your items are still in the cart — tap again to ring them up as a new order.'
            )
          }
          throw new Error(res.message || 'Could not add items to order')
        }
        return { orderId: continuingOrderId, mode: 'continue', itemCount }
      }
      const orderData: CreateOrderRequest = {
        table_id: orderType === 'dine_in' ? selectedTable?.id : undefined,
        customer_name: customerName.trim() || undefined,
        customer_email: customerEmail.trim() || undefined,
        customer_phone: customerPhone.trim() || undefined,
        guest_birthday: guestBirthday.trim() || undefined,
        order_type: orderType,
        guest_count: orderType === 'dine_in' ? (dineInSession?.guestCount ?? 0) : 0,
        assigned_server_id:
          orderType === 'dine_in' && dineInSession?.serverId
            ? dineInSession.serverId
            : undefined,
        items: lines,
        notes: orderNotes || undefined,
        delivery_address:
          orderType === 'delivery' ? deliveryAddress.trim() || undefined : undefined,
        delivery_fee_override:
          orderType === 'delivery' && deliveryFeeOverride != null
            ? deliveryFeeOverride
            : undefined,
        service_charge_override_rate:
          serviceChargeOverride?.kind === 'rate' ? serviceChargeOverride.value : undefined,
        service_charge_override_amount:
          serviceChargeOverride?.kind === 'amount' ? serviceChargeOverride.value : undefined,
        // F-CTRL-N80: the waiver's reason / manager PIN, when the tenant asks.
        service_charge_reason_code: serviceChargeAuthRef.current?.reason_code,
        service_charge_reason_note: serviceChargeAuthRef.current?.reason_note,
        service_charge_pin: serviceChargeAuthRef.current?.pin,
        // F-FE-N103: two requests carrying this key can only ever become one
        // order — the second loses at the unique index on orders and comes back
        // `duplicate_submit`, which onError swallows.
        client_op_id: cartSubmitOpIdRef.current ?? undefined,
      }
      const res = await apiClient.createCounterOrder(orderData)
      if (!res.success || !res.data?.id) {
        throw new Error(res.message || 'Could not create order')
      }
      return { orderId: res.data.id, mode: 'new', itemCount }
    },
    onSuccess: async (result) => {
      // F-FE-N103: this submission is done, so the next one gets a fresh key.
      // Cleared only on success — a failure keeps it so a retry of the SAME
      // cart is deduplicated rather than treated as a new order.
      cartSubmitOpIdRef.current = null
      clearCart()
      setOrderNotes('')
      clearDraft(draftUserId)
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      // F-FE-N176: coalesces with the order_created / table_status_changed
      // stream events the same submit triggers.
      listRefresher.request({ tables: true })
      // Adding items to an existing order re-derives any applied card promo
      // server-side (subtotal changed) — refresh its state.
      if (result.mode === 'continue') {
        void refreshCardPromoAfterCartEdit(result.orderId)
      }

      await fireAndPrint({
        orderId: result.orderId,
        itemCount: result.itemCount,
        mode: result.mode,
      })

      const ref = await apiClient.getOrder(result.orderId)
      if (ref.success && ref.data && ref.data.status !== 'completed' && ref.data.status !== 'cancelled') {
        const od = ref.data
        // `existingOrder` is display state — the ticket header, the Pay button,
        // the highlighted tile. `continuingOrderId` is the DECIDER: it is the
        // only thing that routes the next submit into addItemsToOrder. Setting
        // both here is what left a fired order collecting the next customer's
        // items on the tablet (F-FE-N112). Keep the order loaded so fire -> Pay
        // is still one tap; arm the append only where a tab genuinely continues.
        setExistingOrder(od)
        setContinuingOrderId(
          shouldKeepCollecting({
            mode: result.mode,
            hasTable: Boolean(od.table_id ?? od.table?.id),
            isTabletTill: isBhooklyNativeServerApp(),
          })
            ? od.id
            : null,
        )
        // Keep checkout / payment rail in sync with fired items (was stale until full refresh).
        setSelectedOrder((prev) => (prev?.id === od.id ? od : prev))
        queryClient.setQueryData(['order', od.id, 'payment-panel'], od)
        setExistingItemsExpanded(false)
        setCustomerName(od.customer_name ?? '')
        setCustomerEmail(od.customer_email ?? '')
        setCustomerPhone(od.customer_phone ?? '')
        setGuestBirthday(toGuestDateInputValue(od.guest_birthday))
        setDeliveryAddress(od.delivery_address ?? '')
        setDeliveryFeeOverride(od.delivery_fee_override ?? null)
        setServiceChargeOverride(serviceChargeOverrideFromOrder(od))
        if (orderType === 'dine_in') {
          setDineInSession((prev) => {
            const disp =
              od.user && (od.user.first_name || od.user.last_name)
                ? `${od.user.first_name} ${od.user.last_name}`.trim()
                : od.user?.username ?? ''
            const base: TableSession = prev ?? {
              guestCount: 0,
              serverId: '',
              serverDisplayName: '',
              customerName: undefined,
              customerEmail: undefined,
              customerPhone: undefined,
              guestBirthday: undefined,
            }
            return {
              ...base,
              guestCount: od.guest_count ?? 0,
              serverId: od.user_id ?? '',
              serverDisplayName: disp,
              customerName: od.customer_name ?? undefined,
              customerEmail: od.customer_email ?? undefined,
              customerPhone: od.customer_phone ?? undefined,
              guestBirthday: toGuestDateInputValue(od.guest_birthday) || undefined,
            }
          })
        }
      }
    },
    onError: (err: Error) => {
      // F-FE-N103: the server-side backstop firing. This request is the SAME
      // submission arriving twice — the operator tapped once, and the first
      // request already created the order, fired its KOT and moved the screen
      // on. There is nothing to do and nothing to tell anyone: an error toast
      // for a duplicate we successfully PREVENTED would be worse than silence.
      // (`offline_order_duplicate` is the append endpoint's wording for the same
      // event — that handler is shared with real offline replay, so it keeps its
      // own message.)
      if (
        err instanceof ApiClientError &&
        (err.code === 'duplicate_submit' || err.code === 'offline_order_duplicate')
      ) {
        return
      }
      if (err instanceof ApiClientError && err.code === 'previous_day_open') {
        setPreviousDayBlocker({ open: true, message: err.message })
        return
      }
      if (err instanceof ApiClientError && err.code === 'day_not_open') {
        setStartDayBlocker({ open: true, message: err.message })
        return
      }
      // Transport failure: keep the cart (it's also persisted locally) and never
      // auto-retry — there's no idempotency key, so a machine resubmit could
      // duplicate the order. Distinguish "never sent" from "sent, unknown".
      if (err instanceof ApiClientError && err.isNetworkError) {
        if (err.axiosCode === 'ECONNABORTED') {
          toastHelpers.warning(
            "Couldn't confirm your order",
            'We lost the connection after sending. Check the Orders list / kitchen before placing again to avoid a duplicate.',
          )
          queryClient.invalidateQueries({ queryKey: ['orders'] })
          queryClient.invalidateQueries({ queryKey: ['counterPendingOrders'] })
          return
        }
        // F-FE-N30: the request never left. If this terminal can capture
        // offline, do it now rather than telling the cashier to wait — this is
        // the FIRST failure that reveals the outage, and until it happened the
        // predicate had no way to know the backend was gone.
        if (offlineCapture.capability.ok) {
          void handleOfflineSubmitCart()
          return
        }
        toastHelpers.warning(
          'Offline',
          'No connection — your order is still held. It will place when you reconnect.',
        )
        return
      }
      // F-FE-N30: the desktop shell answers with a real 502 when it cannot
      // reach the tenant, so an outage arrives as an HTTP error rather than a
      // transport failure. Same recovery — the order is not lost to a toast.
      if (
        err instanceof ApiClientError &&
        err.status === PROXY_UNREACHABLE_STATUS &&
        err.code === PROXY_UNREACHABLE_CODE &&
        offlineCapture.capability.ok
      ) {
        void handleOfflineSubmitCart()
        return
      }
      // F-CTRL-N80: the ticket's service-charge waiver needs a reason or a
      // manager PIN (a terminal on stale settings did not ask). Ask now, keep
      // the answer with the ticket, and place it again — the cart is intact.
      if (err instanceof ApiClientError) {
        const need = adjustmentNeedFromRefusal(err.code, serviceChargeAuthRef.current ?? undefined)
        if (need) {
          setPendingAdjustment({
            title: 'Waive the service charge',
            summary: 'This ticket waives the service charge. Add a reason or a manager PIN to place it.',
            need,
            pinReason: 'Waiving the service charge needs a manager.',
            error: adjustmentRefusalMessage(err.code),
            previous: serviceChargeAuthRef.current ?? undefined,
            run: (auth) => {
              serviceChargeAuthRef.current = auth
              setServiceChargeAuth(auth)
              handleSubmitCart()
            },
          })
          return
        }
      }
      toastHelpers.error('Order', err.message || 'Could not create order')
    },
  })

  /**
   * Print one guest's split slip (F-FE-N20). Best-effort: a print hiccup
   * must never block the payment flow — the money is already recorded.
   */
  const printSplitGuestSlip = useCallback(
    async (
      orderId: string,
      order: Order | null,
      share: GuestShare,
      guestNo: number,
      guestCount: number,
      paymentMethod: string,
      /** F-FE-N63 — cash this guest handed over; undefined on card/online. */
      cashTendered?: number
    ) => {
      try {
        let o = order
        if (!o) {
          const res = await apiClient.getOrder(orderId)
          if (res.success && res.data) o = res.data
        }
        if (!o) return
        const settingsRes = await queryClient.fetchQuery({
          queryKey: ['settings', 'all'],
          queryFn: () => apiClient.getAllSettings(),
        })
        if (!settingsRes.success || !settingsRes.data) return
        const cfg = parseReceiptSettings(settingsRes.data as Record<string, unknown>)
        const { printed } = printSplitGuestReceipt(o, cfg, share, {
          cashierName: getCashierNameFromStorage(),
          paymentMethod,
          paidAt: new Date(),
          guestNo,
          guestCount,
          orderTypeLabel: displayLabelForOrderType(o.order_type, orderTypesRaw),
          formatAmount: formatCurrency,
          cashTendered,
        })
        if (!printed) {
          toastHelpers.error('Split slip', 'Popup blocked — allow popups for this site')
        }
      } catch {
        /* slip is optional */
      }
    },
    [queryClient, formatCurrency]
  )

  /**
   * Pull the order from the server and push it into BOTH copies the till reads
   * from: the `['order', id, 'payment-panel']` query the rail renders, and the
   * `selectedOrder` / `existingOrder` state the pay tap reads its amount from.
   *
   * F-MM-N10: those two used to be able to disagree. Invalidating only the
   * query refreshed what the cashier SAW while `selectedOrder` kept the old
   * total, so the Charge button showed the new figure and the tap sent the old
   * one — and the backend rejected it with a bare `amount_exceeds_balance`.
   * Every path that rewrites totals server-side must land here or mirror it.
   */
  const refreshOrderIntoTill = useCallback(
    async (orderId: string) => {
      queryClient.invalidateQueries({ queryKey: ['counterPendingOrders'] })
      try {
        const fresh = await apiClient.getOrder(orderId)
        if (fresh.success && fresh.data) {
          const od = fresh.data
          queryClient.setQueryData(['order', orderId, 'payment-panel'], od)
          setSelectedOrder((prev) => (prev?.id === od.id ? od : prev))
          setExistingOrder((prev) => (prev?.id === od.id ? od : prev))
          return
        }
      } catch {
        /* fall through to a plain invalidation */
      }
      queryClient.invalidateQueries({ queryKey: ['order', orderId, 'payment-panel'] })
    },
    [queryClient]
  )

  /**
   * F-FE-N154 — remove a line the kitchen never saw.
   *
   * Deliberately NOT the void flow. A void exists to audit food that was made
   * and then destroyed: it takes a manager PIN and writes an append-only
   * void_log row. An unsent line was never made, so there is nothing to
   * authorise and nothing to audit — charging a manager PIN for it would be
   * theatre, and a void_log row would be a false record of destroyed food.
   *
   * The endpoint and the client method have existed since the cart-edit work;
   * nothing ever called them, because the only place these lines could render
   * filtered them out. See
   * audit/PARCHED_UNFIRED_ORDER_NO_VOID_BANNER_2026-09-10.md.
   */
  const removeUnsentItemMutation = useMutation({
    mutationFn: async ({ orderId, itemId }: { orderId: string; itemId: string }) => {
      const res = await apiClient.removeDraftItem(orderId, itemId)
      if (!res.success) throw new Error(res.message || 'Could not remove the item')
      return { orderId }
    },
    onSuccess: async ({ orderId }) => {
      // Totals moved server-side — RemoveDraftItem re-rolls subtotal,
      // item_discounts_total and the canonical recompute inside its own
      // transaction — so the till re-READS rather than patching locally. Same
      // discipline as the line-discount path, and for the same reason: a
      // locally-patched total is what makes the pay tap send a stale figure the
      // backend then refuses (F-MM-N10).
      await refreshOrderIntoTill(orderId)
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      invalidateTables(queryClient)
    },
    onError: (e) => {
      toastHelpers.error('Remove item', e instanceof Error ? e.message : 'Request failed')
    },
  })
  const removingItemId = removeUnsentItemMutation.isPending
    ? (removeUnsentItemMutation.variables?.itemId ?? null)
    : null

  /**
   * F-FE-N154 — send lines that never reached the kitchen.
   *
   * The recovery the counter did not have. When a create succeeds but its fire
   * does not, the cart is already cleared and the screen has moved on, so the
   * lines sit unsent on a loaded order with `cart.length === 0` — and the
   * primary CTA is disabled on exactly that condition. The only route was to
   * ring an unrelated item and fire, which drags the stale lines along and
   * prints a slip for food the kitchen may already have made.
   */
  const [fireUnsentPending, setFireUnsentPending] = useState(false)
  const handleFireUnsent = useCallback(async () => {
    const orderId = existingOrder?.id
    if (!orderId) return
    const count = (existingOrder?.items ?? []).filter(isUnsentLine).length
    if (count === 0) return
    setFireUnsentPending(true)
    try {
      await fireAndPrint({ orderId, itemCount: count, mode: 'continue' })
      await refreshOrderIntoTill(orderId)
      queryClient.invalidateQueries({ queryKey: ['orders'] })
    } finally {
      setFireUnsentPending(false)
    }
  }, [existingOrder, fireAndPrint, refreshOrderIntoTill, queryClient])

  /**
   * F-MM-N24: the gift leg of a bill, remembered per order id.
   *
   * The receipt is printed by whichever tender CLOSES the bill, which on the
   * commonest flow is the cash that follows the card — not the card itself.
   * Reading the gift details off the payment being processed therefore printed
   * a slip with no gift lines at all whenever the guest paid the remainder,
   * which is precisely when they most want to see what is left on the card.
   *
   * A ref, not state: it must survive the mutation callbacks without causing a
   * render, and it is read inside the same callback that writes it.
   */
  const giftLegByOrder = useRef<
    Record<string, { code: string; amount: number; balance: number }>
  >({})
  // F-MM-N30: the house-account charge's server figures, for the receipt.
  const houseLegByOrder = useRef<Record<string, ReceiptHouseLeg>>({})
  // F-MM-N30: a charge refusal the Charge-to-account dialog shows in place
  // (credit limit / PIN needed / wrong PIN) instead of a toast.
  const [chargeAccountError, setChargeAccountError] = useState<ChargeAccountServerError | null>(null)

  const processPaymentMutation = useMutation({
    mutationFn: async ({
      orderId,
      paymentData,
      houseAccount,
    }: {
      orderId: string
      paymentData: ProcessPaymentRequest
      /** Present when this tender settles a split guest — drives the guest
       *  slip + suppresses the standard partial-payment receipt/banner. */
      splitPrint?: { share: GuestShare; guestNo: number; guestCount: number }
      /**
       * F-FE-N63 — cash the customer handed over, for the receipt's
       * "Cash Received / Change Returned" pair.
       *
       * Carried as a mutation VARIABLE rather than read from state at print
       * time: runCashPayment clears `cashReceived` synchronously right after
       * firing this mutation, while the receipt prints later in onSuccess — so
       * by then the state is already empty. Variables are captured at mutate()
       * and handed back to onSuccess, which makes the value survive by
       * construction instead of by ordering luck.
       */
      cashTendered?: number
      /**
       * F-MM-N30 — charge the whole bill to a house account instead of taking
       * a tender. paymentData then carries the account's RATE intent (cash for
       * a staff meal, the tab's own rate for a tab) so the receipt prints the
       * rate the server billed at; nothing is sent to the payments endpoint.
       */
      houseAccount?: { accountId: string; managerPin?: string }
    }) => {
      if (houseAccount) {
        const res = await apiClient.chargeOrderToAccount(orderId, {
          account_id: houseAccount.accountId,
          manager_pin: houseAccount.managerPin,
        })
        if (!res.success) {
          throw new Error(res.message || 'Could not charge the account')
        }
        const d = res.data
        if (d) {
          houseLegByOrder.current[orderId] = {
            kind: d.account_kind,
            name: d.customer_name,
            allowanceRemaining: d.allowance_remaining,
            overage: d.overage,
            balance: d.balance,
          }
        }
        return res
      }
      const res = await apiClient.processCounterPayment(orderId, paymentData)
      if (!res.success) {
        throw new Error(res.message || 'Payment could not be processed')
      }
      return res
    },
    // F-FE-N176: when the tap went out, so onSuccess can tell whether the
    // stream's `payment` event for THIS payment already refreshed the strip.
    onMutate: () => ({ startedAt: Date.now() }),
    onError: (err: Error, variables) => {
      if (
        variables?.houseAccount &&
        err instanceof ApiClientError &&
        err.code &&
        CHARGE_ACCOUNT_INLINE_CODES.has(err.code)
      ) {
        setChargeAccountError({ code: err.code, message: err.message, at: Date.now() })
        // The switch was turned off elsewhere — let the till catch up.
        if (err.code === 'house_account_kind_disabled') {
          queryClient.invalidateQueries({ queryKey: ['settings', 'all'] })
        }
        return
      }
      if (err instanceof ApiClientError && err.code === 'previous_day_open') {
        setPreviousDayBlocker({ open: true, message: err.message })
        return
      }
      if (err instanceof ApiClientError && err.code === 'day_not_open') {
        setStartDayBlocker({ open: true, message: err.message })
        return
      }
      if (err instanceof ApiClientError && err.code === 'split_tender_intent_locked') {
        toastHelpers.error(
          'Split bill',
          'This bill is locked to the card tax rate — a cash tender would change it. Void the recorded payments and restart the split as mixed-tender.'
        )
        return
      }
      if (err instanceof ApiClientError && err.code === 'card_promo_requires_card_tender') {
        toastHelpers.error(
          'Bank card promo',
          'This bill has a bank card promo — it must be settled by card. Remove the promo to take another tender.'
        )
        return
      }
      if (err instanceof ApiClientError && err.code === 'split_guest_already_paid') {
        toastHelpers.error('Split bill', 'This guest was already settled on another terminal.')
        if (selectedOrder) {
          queryClient.invalidateQueries({ queryKey: ['order', selectedOrder.id, 'payment-panel'] })
        }
        return
      }
      // F-MM-N7: the order was re-priced under the operator — either the tender
      // changed or an admin edited a tax / service-charge rate while the ticket
      // sat open. The backend refused rather than bank the stale figure (which,
      // when the new total is HIGHER, used to be accepted as a silent part
      // payment). Pull the corrected order so the panel redraws on the real
      // number, and say plainly what happened instead of showing a raw code.
      if (err instanceof ApiClientError && err.code === 'order_reprice_conflict') {
        if (selectedOrder) {
          queryClient.invalidateQueries({ queryKey: ['order', selectedOrder.id, 'payment-panel'] })
          queryClient.invalidateQueries({ queryKey: ['counterPendingOrders'] })
        }
        toastHelpers.error(
          'Price changed',
          err.message || 'This order was re-priced. Check the new total and take the payment again.'
        )
        return
      }
      // F-MM-N10: the till sent a figure the order no longer owes. The usual
      // cause is a cached copy that missed a server-side total rewrite (a bank
      // card promo or promo code applied on the rail). The backend now names
      // both figures; pull the corrected order into every store so the panel
      // and the next tap agree, instead of leaving the cashier on a raw code.
      if (err instanceof ApiClientError && err.code === 'amount_exceeds_balance') {
        if (selectedOrder) void refreshOrderIntoTill(selectedOrder.id)
        toastHelpers.error(
          'Amount changed',
          err.message ||
            'The amount sent is more than this order still owes. The total was refreshed — take the payment again.'
        )
        return
      }
      toastHelpers.error('Payment', err.message || 'Payment failed')
    },
    onSuccess: async (data, variables, mutateCtx) => {
      if (variables.houseAccount) {
        queryClient.invalidateQueries({ queryKey: ['counter-house-accounts'] })
        queryClient.invalidateQueries({ queryKey: ['counter-house-account'] })
        queryClient.invalidateQueries({ queryKey: ['house-accounts'] })
      }
      // F-MM-N24: record the gift leg BEFORE any branching. A gift card that
      // only part-pays the bill lands in the partial branch, which never
      // reaches the receipt code — so recording it later would lose exactly
      // the case this exists for. The balance is the server's figure, read
      // from the card's append-only ledger.
      if (variables.paymentData.payment_method === 'gift_card') {
        const d = data?.data as unknown as
          | { gift_card_code?: string; gift_card_balance?: number }
          | null
          | undefined
        if (d?.gift_card_code && typeof d.gift_card_balance === 'number') {
          giftLegByOrder.current[variables.orderId] = {
            code: d.gift_card_code,
            amount: variables.paymentData.amount,
            balance: d.gift_card_balance,
          }
        }
      }

      let orderAfter: Order | null = null
      try {
        const orderRes = await apiClient.getOrder(variables.orderId)
        if (orderRes.success && orderRes.data) orderAfter = orderRes.data
      } catch {
        /* order fetch optional for branching */
      }

      // If we cannot refetch, assume check closed (payment API already succeeded).
      const fullyPaid = orderAfter == null ? true : orderAfter.status === 'completed'

      // Split guest settled: print their slip now (before the standard final
      // receipt on the last tender) — each guest walks away with their share.
      if (variables.splitPrint) {
        await printSplitGuestSlip(
          variables.orderId,
          orderAfter,
          variables.splitPrint.share,
          variables.splitPrint.guestNo,
          variables.splitPrint.guestCount,
          variables.paymentData.payment_method,
          // F-FE-N63. Deliberately absent on the reprint path below: a reprint
          // has no record of the note that was handed over, and omitting the
          // block is honest where printing "Change Returned Rs 0" would not be.
          variables.cashTendered
        )
      }

      if (fullyPaid) {
        setSplitDialogOpen(false)
        void kitchenSoundService.playPaymentSuccessSound(variables.orderId)
        setCheckoutCelebration(orderType)
        setPartialPaymentBanner(null)
        setSelectedOrder(null)
        setCheckoutOpen(false)
        setCashModalOpen(false)
        setCashReceived('')
        setDiscountValue('')
        setExistingOrder(null)
        setContinuingOrderId(null)
        setSelectedTable(null)
        setDineInSession(null)
        setCustomerName('')
        setCustomerEmail('')
        setCustomerPhone('')
        setGuestBirthday('')
        setCustomerSavedAddresses([])
        setDeliveryAddress('')
        setDeliveryFeeOverride(null)
        setServiceChargeOverride(null)
        setServiceChargeAuth(null)
        clearCart()
        clearDraft(draftUserId)
        setExistingItemsExpanded(false)
        queryClient.removeQueries({ queryKey: ['order', variables.orderId, 'payment-panel'] })
      } else {
        // Inline banner inside the checkout dialog (above the remaining-balance
        // ledger) rather than a floating toast that pulls the operator's eye
        // away from the payment rail. Split tenders skip the banner — the
        // split dialog owns the "N of M guests paid" progress display.
        setPartialPaymentBanner(
          variables.splitPrint
            ? null
            : orderAfter
              ? 'Remaining balance still due on this check.'
              : 'Refresh the order if totals look wrong.'
        )
        setCashModalOpen(false)
        setCashReceived('')
        if (orderAfter) {
          setSelectedOrder(orderAfter)
          setExistingOrder((prev) => (prev?.id === orderAfter.id ? orderAfter : prev))
          queryClient.setQueryData(['order', variables.orderId, 'payment-panel'], orderAfter)
          setCustomerName(orderAfter.customer_name ?? '')
          setCustomerEmail(orderAfter.customer_email ?? '')
          setCustomerPhone(orderAfter.customer_phone ?? '')
          setGuestBirthday(toGuestDateInputValue(orderAfter.guest_birthday))
          setDeliveryAddress(orderAfter.delivery_address ?? '')
          setDeliveryFeeOverride(orderAfter.delivery_fee_override ?? null)
          setServiceChargeOverride(serviceChargeOverrideFromOrder(orderAfter))
          if (orderAfter.order_type === 'dine_in') {
            setDineInSession((prev) => {
              const disp =
                orderAfter.user && (orderAfter.user.first_name || orderAfter.user.last_name)
                  ? `${orderAfter.user.first_name} ${orderAfter.user.last_name}`.trim()
                  : orderAfter.user?.username ?? ''
              const base: TableSession = prev ?? {
                guestCount: 0,
                serverId: '',
                serverDisplayName: '',
                customerName: undefined,
                customerEmail: undefined,
                customerPhone: undefined,
                guestBirthday: undefined,
              }
              return {
                ...base,
                guestCount: orderAfter.guest_count ?? 0,
                serverId: orderAfter.user_id ?? '',
                serverDisplayName: disp,
                customerName: orderAfter.customer_name ?? base.customerName,
                customerEmail: orderAfter.customer_email ?? base.customerEmail,
                customerPhone: orderAfter.customer_phone ?? base.customerPhone,
                guestBirthday:
                  toGuestDateInputValue(orderAfter.guest_birthday) || base.guestBirthday,
              }
            })
          }
        }
      }

      queryClient.invalidateQueries({ queryKey: ['orders'] })
      // F-FE-N176: the server publishes a `payment` stream event after it
      // commits every counter payment, and handleTableStream refreshes the
      // strip + tables on it. That event normally lands BEFORE this point (we
      // awaited a getOrder above), so asking again here was a second full
      // GET /orders for nothing. Ask only when the stream has not covered it —
      // stream down, or the event late — so the strip can never be left stale.
      if (
        !(mutateCtx && listRefresher.paymentRefreshCoveredSince(variables.orderId, mutateCtx.startedAt))
      ) {
        listRefresher.request({ tables: true })
      }

      // Mid-split tenders print only the guest slip (above). The standard
      // receipt path below runs for normal tenders and for the completing
      // tender of a split (the table's full fiscal receipt).
      if (variables.splitPrint && !fullyPaid) return

      try {
        const orderRes =
          orderAfter != null
            ? { success: true as const, data: orderAfter }
            : await apiClient.getOrder(variables.orderId)
        const settingsRes = await queryClient.fetchQuery({
          queryKey: ['settings', 'all'],
          queryFn: () => apiClient.getAllSettings(),
        })
        if (orderRes.success && orderRes.data && settingsRes.success && settingsRes.data) {
          const paidAt = new Date()
          const paymentMethod = variables.paymentData.payment_method
          const receiptSettings = parseReceiptSettings(
            settingsRes.data as Record<string, unknown>,
          )
          const fd = orderRes.data.fiscal_details
          /** Wait for localhost IMSFiscal before printing once — avoids provisional then duplicate slip. */
          const needsLocalFiscalRelay =
            orderRes.data.status === 'completed' &&
            String(fd?.status ?? '').toUpperCase() === 'PENDING_LOCAL' &&
            !!(fd?.local_payload && fd.local_payload.length > 0)

          let orderForReceipt = orderRes.data
          let receiptPrinted = false

          // Per-receipt loyalty claim + auto thank-you blocks — both
          // best-effort, fetched in parallel so the network hops overlap.
          // Failures are swallowed — a marketing hiccup must never
          // prevent the receipt from printing.
          const brandedQrEnabled =
            (settingsRes.data as Record<string, unknown>).branded_qr_enabled !== false
          const [rawLoyaltyClaim, thankYouPromo, rawReviewInvite, receiptCardPromoLabel] =
            await Promise.all([
              buildReceiptLoyaltyBlock(orderRes.data.id, {
                logoUrl: receiptSettings.logoUrl,
                enabled: brandedQrEnabled,
              }),
              buildReceiptThankYouBlock(orderRes.data.id),
              buildReceiptReviewBlock(orderRes.data.id, {
                logoUrl: receiptSettings.logoUrl,
                enabled: brandedQrEnabled,
              }),
              // Bank card promo label ("HBL Card Discount (20%)") for the
              // discount row — best-effort like the other blocks.
              apiClient
                .getAppliedCardPromo(orderRes.data.id)
                .then((r) => (r.data?.applied ? r.data.label ?? null : null))
                .catch(() => null)
                // F-MKT-N2: else an item-only promo code's own label.
                .then(async (card) =>
                  card ??
                  (await apiClient
                    .getAppliedPromo(variables.orderId)
                    .then((r) => scopedPromoDiscountLabel(r.data))
                    .catch(() => null))
                ),
            ])

          // At most one marketing QR reaches the slip, and loyalty wins when
          // it is capturing an unknown guest — see receiptQrPolicy.ts.
          const { loyalty: loyaltyClaim, review: reviewInvite } = resolveReceiptQrSlots(
            rawLoyaltyClaim,
            rawReviewInvite,
          )

          // F-MM-N24: prefer the leg remembered when the CARD was applied, and
          // fall back to the ORDER's own payment rows.
          //
          // The ref lives in this component's memory, so it does not survive a
          // page reload — and a bill is routinely finished after one: the till
          // is refreshed, the desktop app restarts, a shift changes, or the
          // card goes on at one terminal and the remainder is taken at
          // another. In every one of those the slip printed with no gift lines
          // at all (caught on order #20260917-004 during the end-to-end run).
          //
          // The payment row is server truth and carries both the code
          // (reference_number) and the amount. Only the remaining balance
          // needs a lookup, and that is best-effort: losing the balance line
          // is far better than losing the whole block.
          let giftLeg: { code: string; amount: number; balance?: number } | null =
            giftLegByOrder.current[variables.orderId] ?? null
          if (!giftLeg) {
            const row = (orderForReceipt.payments ?? []).find(
              (p) => p.payment_method === 'gift_card' && (p.amount ?? 0) > 0 && p.reference_number,
            )
            if (row?.reference_number) {
              giftLeg = { code: row.reference_number, amount: row.amount }
              try {
                const bal = await apiClient.previewGiftCard(row.reference_number)
                if (bal.success && typeof bal.data?.balance === 'number') {
                  giftLeg = { ...giftLeg, balance: bal.data.balance }
                }
              } catch {
                /* balance is optional on the slip — print the leg regardless */
              }
            }
          }

          const printFinalReceipt = (order: typeof orderForReceipt) => {
            const { printed } = printCustomerReceipt(order, receiptSettings, {
              giftLeg,
              houseLeg: houseLegByOrder.current[variables.orderId] ?? houseLegFromOrder(order),
              cashierName: getCashierNameFromStorage(),
              paymentMethod,
              paidAt,
              orderTypeLabel: displayLabelForOrderType(order.order_type, orderTypesRaw),
              formatAmount: formatCurrency,
              billKind: 'final',
              loyaltyClaim,
              thankYouPromo,
              reviewInvite,
              cardPromoLabel: receiptCardPromoLabel,
              // F-FE-N63. Undefined on every non-cash tender and on any path
              // that did not capture the note, which is exactly when the
              // receipt should stay silent rather than claim zero change.
              cashTendered: variables.cashTendered,
            })
            receiptPrinted = printed
          }

          if (!needsLocalFiscalRelay) {
            printFinalReceipt(orderForReceipt)
          } else {
            try {
              const localResp = await fetch(
                'http://localhost:8524/api/IMSFiscal/GetInvoiceNumberByModel',
                {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: fd.local_payload!,
                },
              )
              const localJson = await localResp.text()
              let invoiceNumber = ''
              let qrCode = ''
              let errorMsg = ''
              try {
                const parsed = JSON.parse(localJson) as Record<string, unknown>
                const code = String(parsed.Code ?? '')
                if (code === '100' || code === '') {
                  invoiceNumber = String(parsed.InvoiceNumber ?? '')
                  qrCode = String(
                    (parsed.QRCode as string | undefined) ??
                      (parsed.QrCode as string | undefined) ??
                      '',
                  )
                  if (!invoiceNumber || invoiceNumber.toLowerCase() === 'not available') {
                    invoiceNumber = ''
                    errorMsg =
                      String(parsed.Response ?? parsed.Errors ?? '') ||
                      'No invoice number returned'
                  }
                } else {
                  errorMsg = `Code ${code}: ${String(parsed.Response ?? parsed.Errors ?? 'Rejected')}`
                }
              } catch {
                errorMsg = 'Invalid JSON from IMSFiscal'
              }
              await apiClient.postLocalFiscalResult(variables.orderId, {
                invoice_number: invoiceNumber,
                qr_code: qrCode,
                raw_response: localJson,
                error: errorMsg,
              })

              if (invoiceNumber) {
                toastHelpers.success('FBR', `Invoice ${invoiceNumber} registered via local server`)
                let synced: Order | null = null
                for (let attempt = 0; attempt < 12; attempt++) {
                  const refreshed = await apiClient.getOrder(variables.orderId)
                  const o = refreshed.success ? refreshed.data : null
                  const f = o?.fiscal_details
                  if (
                    o &&
                    String(f?.status ?? '').toUpperCase() === 'SYNCED' &&
                    String(f?.irn ?? '').trim() !== ''
                  ) {
                    synced = o
                    break
                  }
                  await new Promise((r) => setTimeout(r, 100))
                }
                if (synced) orderForReceipt = synced
                else {
                  const fallback = await apiClient.getOrder(variables.orderId)
                  if (fallback.success && fallback.data) orderForReceipt = fallback.data
                }
                printFinalReceipt(orderForReceipt)
              } else {
                toastHelpers.error('FBR', errorMsg || 'Local fiscal server returned no invoice')
                printFinalReceipt(orderForReceipt)
              }
            } catch (localErr: unknown) {
              const msg = localErr instanceof Error ? localErr.message : ''
              const errMsg =
                msg.includes('Failed to fetch') || msg.includes('NetworkError')
                  ? 'IMSFiscal not running — ensure the local fiscal server is started on this machine'
                  : msg || 'Local fiscal relay failed'
              toastHelpers.error('FBR Local', errMsg)
              await apiClient
                .postLocalFiscalResult(variables.orderId, {
                  invoice_number: '',
                  qr_code: '',
                  raw_response: '',
                  error: errMsg,
                })
                .catch(() => {})
              printFinalReceipt(orderForReceipt)
            }
          }

          if (!receiptPrinted) {
            // F-FE-N104: tell the truth about WHY. On the Server APK there is
            // no popup to allow — the shell has no receipt transport at all, so
            // the old blanket message sent the cashier hunting a browser setting
            // that does not exist on that device. The sale is recorded either
            // way; what they need to know is where to get the paper.
            toastHelpers.error(
              'Receipt not printed',
              isBhooklyNativeServerApp()
                ? 'This tablet cannot print receipts yet — the payment is saved, print it from the counter.'
                : 'Popup blocked — allow popups for this site to print the receipt.',
            )
          }

          // Staff meals and marketplace-settled bills are never filed, so
          // there is no PRA invoice to offer.
          if (
            receiptSettings.praInvoiceEnabled &&
            orderForReceipt.status === 'completed' &&
            !isNeverFiledTreatment(orderForReceipt.tax_treatment)
          ) {
            setPraPromptContext({
              order: orderForReceipt,
              settings: receiptSettings,
              paymentMethod,
              paidAt,
            })
          }
        }
      } catch {
        /* receipt is optional */
      }
    },
  })

  /**
   * Dismiss the PRA tax invoice prompt without printing — the main receipt
   * already went to the printer, nothing else needs to happen.
   */
  const handlePraSkip = useCallback(() => {
    setPraPromptContext(null)
  }, [])

  /**
   * Print the PRA tax invoice slip, persist the print event, then dismiss.
   * Persistence failures don't block the printed slip — the receipt is
   * already in the customer's hand, so we log rather than surface an error.
   */
  const handlePraPrint = useCallback(async () => {
    if (!praPromptContext || praPrinting) return
    setPraPrinting(true)
    try {
      const { order, settings, paymentMethod, paidAt } = praPromptContext
      const { invoiceNumber, printed } = await printPraTaxInvoice(order, settings, {
        cashierName: getCashierNameFromStorage(),
        paymentMethod,
        paidAt,
        orderTypeLabel: displayLabelForOrderType(order.order_type, orderTypesRaw),
        formatAmount: formatCurrency,
      })
      // Only record the print event when the print window actually opened.
      // If the browser's popup blocker intercepted the window, the cashier
      // still sees the prompt close — but we do not lie to the audit trail.
      if (printed) {
        try {
          await apiClient.markPraInvoicePrinted(order.id, invoiceNumber || undefined)
          queryClient.invalidateQueries({ queryKey: ['orders'] })
          queryClient.invalidateQueries({ queryKey: ['order', order.id] })
        } catch {
          /* audit log is best-effort; print already succeeded */
        }
      }
    } finally {
      setPraPrinting(false)
      setPraPromptContext(null)
    }
  }, [praPromptContext, praPrinting, formatCurrency, queryClient])

  const checkoutIntentMutation = useMutation({
    mutationFn: ({
      orderId,
      intent,
    }: {
      orderId: string
      intent: 'cash' | 'card' | 'online'
    }) => apiClient.updateCheckoutIntent(orderId, { checkout_payment_method: intent }),
    onSuccess: (res, variables) => {
      if (res.success && res.data) {
        const order = res.data
        setSelectedOrder(order)
        queryClient.setQueryData(['order', variables.orderId, 'payment-panel'], order)
        // Keep the rail "On order" section in sync whenever checkout intent updates;
        // the returned order is always the ticket being rung out (variables.orderId).
        if (order.id === variables.orderId) {
          setExistingOrder(order)
        }
        // F-FE-N176: the response IS the re-priced order, so patch its strip
        // tile in place rather than re-reading the whole open-orders list on
        // every checkout open. Not in the cached strip yet → real refresh.
        if (!patchOpenOrderInStrip(queryClient, order)) listRefresher.request()
        return
      }
      listRefresher.request()
    },
    onError: (err) => {
      // F-FE-N31: this very failure is what REVEALED the outage — the same two
      // conditions noteRequestFailure() keys off in api/client.ts, so by the
      // time we get here the offline banner is already up. A second, redder
      // message saying "check the connection" adds nothing the cashier does not
      // now know, and it lands on top of the rail during service. Stay quiet;
      // no money can move while the backend is unreachable anyway.
      if (
        err instanceof ApiClientError &&
        (err.isNetworkError ||
          (err.status === PROXY_UNREACHABLE_STATUS && err.code === PROXY_UNREACHABLE_CODE))
      ) {
        return
      }
      // Bank card promo is card-tender-conditional: the backend refused the
      // switch rather than silently dropping the discount. Quiet here — the
      // startIntentSwitch catch (which knows the attempted order + intent)
      // opens the "Remove promo & switch?" dialog for the cashier.
      if (err instanceof ApiClientError && err.code === 'card_promo_requires_card_tender') {
        return
      }
      // F-MM-N7: a tender switch that fails used to be completely silent — the
      // chip flipped, the server never re-priced, and the next tap paid the old
      // tender's total. Say so. The pay button stays live either way: the
      // backend arbitrates, and refusing to let the till take money over a
      // failed re-price would be a far worse trade.
      toastHelpers.error(
        'Payment type',
        "Couldn't switch payment type — check the connection. The total on screen may be for the previous type."
      )
    },
  })

  /**
   * The in-flight tender switch, so a pay tap can wait for the re-priced total
   * instead of sending the one currently on screen (F-MM-N7).
   *
   * Resolves to the fresh order, or null if the switch failed / timed out. It
   * never rejects — callers treat "no answer" as "carry on and let the backend
   * arbitrate", which is the whole point: this must never be able to block a sale.
   */
  const pendingIntentRef = useRef<{ p: Promise<Order | null>; settled: boolean } | null>(null)

  const startIntentSwitch = useCallback(
    (orderId: string, intent: 'cash' | 'card' | 'online') => {
      const entry: { p: Promise<Order | null>; settled: boolean } = {
        settled: false,
        p: Promise.resolve(null),
      }
      // F-FE-N31: don't ask a backend we know is unreachable. Offline this
      // fired on every tap that opened a ticket — including simply loading an
      // open order to add to it — and each one produced a red "check the
      // connection" toast over the rail. There is nothing to re-price offline:
      // no payment can be taken, so no tender can be chosen. "No answer" is
      // already this function's documented result, so returning it directly is
      // the same contract without the request or the noise.
      if (!online) {
        entry.settled = true
        pendingIntentRef.current = entry
        return entry.p
      }
      entry.p = checkoutIntentMutation
        .mutateAsync({ orderId, intent })
        .then((res) => (res?.success && res.data ? (res.data as Order) : null))
        .catch((err) => {
          // Card promo is card-tender-conditional: the backend blocked the
          // switch off 'card'. Surface the cashier's choice ("remove promo
          // and switch?") — this catch is the one place that still knows
          // which order and which target tender were attempted.
          if (err instanceof ApiClientError && err.code === 'card_promo_requires_card_tender') {
            setCardPromoTenderConflict({ orderId, intent })
          }
          return null
        })
        .then((order) => {
          entry.settled = true
          return order
        })
      pendingIntentRef.current = entry
      return entry.p
    },
    [checkoutIntentMutation, online]
  )

  /**
   * Wait for an in-flight tender switch, but never for long.
   *
   * The axios timeout is 30s (api/client.ts), so a hung request — captive
   * portal, packet loss, the kind of wifi restaurants actually have — would
   * park a naive `disabled={isPending}` button for half a minute. A till that
   * cannot take money is a worse failure than the stale amount this is
   * avoiding, and the backend already refuses a stale amount safely. So: cap
   * the wait, then proceed regardless.
   */
  const INTENT_SETTLE_CAP_MS = 1500
  const settleIntent = useCallback(async (): Promise<Order | null> => {
    const entry = pendingIntentRef.current
    // Nothing in flight. Deliberately returns null rather than the last
    // resolved order: once a switch has settled, React state has caught up and
    // `selectedOrder` is the better source — it also carries anything that
    // happened SINCE, such as a discount or promo applied after the switch.
    // Replaying a stale resolved order here would pay the pre-discount amount.
    if (!entry || entry.settled) return null
    let timer: ReturnType<typeof setTimeout> | undefined
    const capped = new Promise<null>((resolve) => {
      timer = setTimeout(() => resolve(null), INTENT_SETTLE_CAP_MS)
    })
    try {
      return await Promise.race([entry.p, capped])
    } finally {
      if (timer) clearTimeout(timer)
    }
  }, [])

  // F-CTRL-N80 / F-FE-N213: the tenant's discount controls. The server is
  // authoritative; these only decide when the counter asks for a reason or a
  // manager PIN, and the dialog reopens if the server refuses anyway.
  const allSettings = useAllSettings()
  const discountControls = useMemo(() => parseDiscountControls(allSettings?.[DISCOUNT_CONTROLS_KEY]), [allSettings])
  const [pendingAdjustment, setPendingAdjustment] = useState<PendingAdjustment | null>(null)
  const pinLimitReason =
    discountControls.pin_above_percent != null
      ? `Discounts over ${discountControls.pin_above_percent}% of the bill need a manager.`
      : undefined
  /** Ask for what `need` says, then run; or just run. */
  const askThen = useCallback(
    (need: AdjustmentNeed, info: Omit<PendingAdjustment, 'need' | 'run'>, run: (auth: AdjustmentAuthBody) => void) => {
      if (need.reason || need.pin) setPendingAdjustment({ ...info, need, run })
      else run({})
    },
    [],
  )

  const discountMutation = useMutation({
    mutationFn: ({
      orderId,
      discount_amount,
      discount_percent,
      auth,
    }: {
      orderId: string
      discount_amount?: number
      discount_percent?: number
      auth?: AdjustmentAuthBody
    }) => apiClient.applyOrderDiscount(orderId, { discount_amount, discount_percent, ...auth }),
    onSuccess: (res, vars) => {
      if (res.success && res.data) {
        setSelectedOrder(res.data)
        setExistingOrder((prev) => (prev?.id === res.data!.id ? res.data! : prev))
      }
      setDiscountValue('')
      queryClient.invalidateQueries({ queryKey: ['order', vars.orderId, 'payment-panel'] })
      queryClient.invalidateQueries({ queryKey: ['counterPendingOrders'] })
    },
    onError: (err: Error, vars) => {
      // F-DEAL-N3: a manual discount smaller than the automatic deal is refused
      // (best single discount wins) — say why instead of doing nothing.
      if (err instanceof ApiClientError && err.code === 'better_auto_discount') {
        toastHelpers.info('The deal already saves more', err.message)
        return
      }
      const code = err instanceof ApiClientError ? err.code : undefined
      const need = adjustmentNeedFromRefusal(code, vars.auth)
      if (need) {
        setPendingAdjustment({
          title: 'Discount on the bill',
          summary: 'This discount needs your reason or a manager before it applies.',
          need,
          pinReason: pinLimitReason,
          error: adjustmentRefusalMessage(code),
          previous: vars.auth,
          run: (auth) => discountMutation.mutate({ ...vars, auth }),
        })
        return
      }
      toastHelpers.error('Discount', err.message || 'Could not apply the discount.')
    },
  })

  // Per-order delivery fee override. For an existing order we PATCH and let the
  // server recompute totals; for an unplaced ticket we stash the override
  // locally so it flows into CreateOrder. Mirrors discountMutation's state sync.
  const deliveryFeeMutation = useMutation({
    mutationFn: ({ orderId, fee }: { orderId: string; fee: number | null }) =>
      apiClient.setOrderDeliveryFee(orderId, fee),
    onSuccess: (res, vars) => {
      if (res.success && res.data) {
        setSelectedOrder((prev) => (prev?.id === res.data!.id ? res.data! : prev))
        setExistingOrder((prev) => (prev?.id === res.data!.id ? res.data! : prev))
        setDeliveryFeeOverride(res.data.delivery_fee_override ?? null)
      } else if (!res.success) {
        toastHelpers.error('Delivery fee', res.message || 'Could not update the delivery fee.')
      }
      queryClient.invalidateQueries({ queryKey: ['order', vars.orderId, 'payment-panel'] })
      queryClient.invalidateQueries({ queryKey: ['counterPendingOrders'] })
    },
  })

  // Apply (number) or clear (null) the delivery fee. Routes to the server when
  // the order exists; otherwise updates the local override on the compose ticket.
  const onEditDeliveryFee = useCallback(
    (fee: number | null) => {
      const orderId = existingOrder?.id ?? continuingOrderId ?? selectedOrder?.id ?? null
      if (orderId) {
        deliveryFeeMutation.mutate({ orderId, fee })
      } else {
        setDeliveryFeeOverride(fee)
      }
    },
    [existingOrder?.id, continuingOrderId, selectedOrder?.id, deliveryFeeMutation]
  )

  // Per-order service charge override (migration 081). Same shape as the
  // delivery fee: PATCH + server recompute for a placed order, local state for
  // an unplaced ticket so it rides into CreateOrder. The response lands in
  // both order copies (F-MM-N10) so the Charge button and the pay tap agree.
  const serviceChargeMutation = useMutation({
    mutationFn: ({ orderId, override, auth }: { orderId: string; override: ServiceChargeOverride | null; auth?: AdjustmentAuthBody }) =>
      apiClient.setOrderServiceCharge(orderId, override, auth),
    onSuccess: (res, vars) => {
      if (res.success && res.data) {
        setSelectedOrder((prev) => (prev?.id === res.data!.id ? res.data! : prev))
        setExistingOrder((prev) => (prev?.id === res.data!.id ? res.data! : prev))
        setServiceChargeOverride(serviceChargeOverrideFromOrder(res.data))
      } else if (!res.success) {
        toastHelpers.error('Service charge', res.message || 'Could not update the service charge.')
      }
      queryClient.invalidateQueries({ queryKey: ['order', vars.orderId, 'payment-panel'] })
      queryClient.invalidateQueries({ queryKey: ['counterPendingOrders'] })
    },
    onError: (err: Error, vars) => {
      const code = err instanceof ApiClientError ? err.code : undefined
      const need = adjustmentNeedFromRefusal(code, vars.auth)
      if (need) {
        setPendingAdjustment({
          title: 'Waive the service charge',
          summary: 'Waiving the service charge needs your reason or a manager.',
          need,
          pinReason: 'Waiving the service charge needs a manager.',
          error: adjustmentRefusalMessage(code),
          previous: vars.auth,
          run: (auth) => serviceChargeMutation.mutate({ ...vars, auth }),
        })
        return
      }
      toastHelpers.error('Service charge', err.message || 'Could not update the service charge.')
    },
  })

  // Apply (override) or clear (null) the service charge. Routes to the server
  // when the order exists; otherwise updates the local override on the ticket.
  const onEditServiceCharge = useCallback(
    (override: ServiceChargeOverride | null) => {
      const orderId = existingOrder?.id ?? continuingOrderId ?? selectedOrder?.id ?? null
      const placed = existingOrder ?? selectedOrder ?? null
      // F-CTRL-N80: only a WAIVER of a charge the bill would carry can ask.
      const carriesCharge = getOrderTypePricing(placed?.order_type ?? orderType, orderTypesRaw).includeServiceCharge
      const atSetRate = placed?.service_charge_at_set_rate ?? (carriesCharge ? 1 : 0)
      const current = placed ? (placed.service_charge_amount ?? 0) : atSetRate
      const waiving = override != null && override.value === 0
      const need = serviceChargeNeed(discountControls, { changed: waiving && current > 0.005, atSetRate, after: waiving ? 0 : 1 })
      askThen(
        need,
        {
          title: 'Waive the service charge',
          summary: 'This bill will carry no service charge.',
          pinReason: 'Waiving the service charge needs a manager.',
        },
        (auth) => {
          if (orderId) {
            serviceChargeMutation.mutate({ orderId, override, auth })
          } else {
            setServiceChargeOverride(override)
            setServiceChargeAuth(override == null ? null : auth)
          }
        },
      )
    },
    [existingOrder, continuingOrderId, selectedOrder, serviceChargeMutation, discountControls, askThen, orderType, orderTypesRaw]
  )

  // Per-line discount mutation. Mirrors discountMutation but scoped to
  // a single OrderItem. The server enforces the promo mutex (409
  // conflict_promo) and the line-subtotal cap; we surface those as
  // toasts so the cashier knows why the call failed without re-deriving
  // the constraint client-side.
  const itemDiscountMutation = useMutation({
    mutationFn: ({
      orderId,
      itemId,
      discount_amount,
      discount_percent,
      pin,
      auth,
    }: {
      orderId: string
      itemId: string
      discount_amount?: number
      discount_percent?: number | null
      pin?: string
      auth?: AdjustmentAuthBody
    }) =>
      apiClient.applyOrderItemDiscount(orderId, itemId, {
        discount_amount,
        discount_percent,
        pin,
        ...auth,
      }),
    onSuccess: (res, vars) => {
      if (res.success && res.data) {
        setSelectedOrder(res.data)
        setExistingOrder((prev) => (prev?.id === res.data!.id ? res.data! : prev))
      } else if (!res.success) {
        const msg = res.error === 'conflict_promo'
          ? 'Remove the promo to apply a line discount.'
          : res.message || 'Could not apply line discount.'
        toastHelpers.error('Line discount', msg)
      }
      queryClient.invalidateQueries({ queryKey: ['order', vars.orderId, 'payment-panel'] })
      queryClient.invalidateQueries({ queryKey: ['counterPendingOrders'] })
    },
    onError: (err: Error, vars) => {
      const code = err instanceof ApiClientError ? err.code : undefined
      const need = adjustmentNeedFromRefusal(code, vars.auth)
      if (need) {
        setPendingAdjustment({
          title: 'Discount on an item',
          summary: 'This discount needs your reason or a manager before it applies.',
          need,
          pinReason: pinLimitReason,
          error: adjustmentRefusalMessage(code),
          previous: vars.auth,
          run: (auth) => itemDiscountMutation.mutate({ ...vars, auth }),
        })
        return
      }
      toastHelpers.error('Line discount', err.message || 'Could not apply line discount.')
    },
  })

  const categoryById = useMemo(() => {
    const m = new Map<string, Category>()
    ;(categories as Category[]).forEach((c) => m.set(c.id, c))
    return m
  }, [categories])

  /**
   * Product id → menu name, for rendering a held offline order (F-FE-N31).
   * The queue stores ids only, so names come from the live menu — which is also
   * what makes a price change during the outage impossible to resurrect.
   * Falls back to the id rather than a blank: an unrecognisable line the
   * cashier can still read out beats a row that looks empty.
   */
  const offlineProductName = useCallback(
    (productId: string) => products.find((p) => p.id === productId)?.name ?? productId,
    [products]
  )

  /**
   * The cart is adding to a ticket that already exists — server-side
   * (continuingOrderId) or held on this device (offlineTarget). Both mean the
   * same thing to the composer: the primary action appends, and the guest-detail
   * guards that only apply to creating an order do not apply.
   */
  const appendingToLoadedOrder = Boolean(continuingOrderId || offlineTarget)

  /**
   * Whether this terminal can fire the cart with no connection.
   *
   * F-CTRL-N52 collapsed this to the capability predicate alone. It used to
   * carry an exception letting an APPEND through when the reserved-number pool
   * had run dry, because otherwise a long outage stopped the tables already
   * seated from ordering — the worst possible moment to stop serving. There is
   * no pool to run dry any more, so the exception has nothing left to except.
   */
  const offlineFireReady = offlineCapture.capability.ok

  // Total item count across all categories, for the "All" tile badge. Summed
  // from per-category product_count so it stays stable regardless of which
  // category is currently selected (products[] is category-scoped on filter).
  const totalCategoryItems = useMemo(
    () =>
      (categories as Category[]).reduce(
        (sum, c) => sum + (c.product_count ?? 0),
        0
      ),
    [categories]
  )

  const isSearching = searchTerm.trim().length > 0

  /**
   * Search deliberately IGNORES the selected category.
   *
   * A cashier typing into the search box is telling you what they want; if they
   * knew which tab it lived under they would have tapped it. Scoping the search
   * to the current tab means "brownie" returns nothing while Matcha is selected,
   * which reads as the product being missing.
   *
   * The category chip stays visually selected during a search and the view snaps
   * back to it when the box is cleared — search is a temporary overlay, not a
   * navigation change, so the cashier never loses their place mid-rush.
   *
   * SKU and barcode are matched too, so a scanner works in this box.
   */
  const filteredProducts = useMemo(() => {
    const q = searchTerm.trim().toLowerCase()
    // F-FE-N161: retired products never reach the grid — not the category view
    // and not search, so a cashier cannot find one by typing its name either.
    // Only products that are BOTH unavailable and hidden from the storefront
    // qualify, so an 86'd item still shows greyed out. See lib/productVisibility.
    const sellable = withoutRetiredProducts(products)
    const base =
      isSearching || selectedCategory === 'all'
        ? sellable
        : sellable.filter((p) => p.category_id === selectedCategory)
    if (!q) return base
    return base.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        (p.description?.toLowerCase().includes(q) ?? false) ||
        (p.sku?.toLowerCase().includes(q) ?? false) ||
        (p.barcode?.toLowerCase().includes(q) ?? false),
    )
  }, [products, selectedCategory, searchTerm, isSearching])

  const searchScopeNotice =
    isSearching && selectedCategory !== 'all'
      ? categories.find((c) => c.id === selectedCategory)?.name ?? null
      : null

  const gridState = menuGridState({
    filteredCount: filteredProducts.length,
    hasSearch: searchTerm.trim().length > 0,
    productsCount: products.length,
    loadFailed: productsLoadFailed,
  })

  const sortedTables = useMemo(() => {
    const arr = [...tables]
    arr.sort((a, b) => String(a.table_number).localeCompare(String(b.table_number), undefined, { numeric: true }))
    return arr
  }, [tables])

  // Modifier configuration for the whole menu: one cached query key, so it
  // survives an outage alongside the menu itself (F-MOD-N7).
  const { data: menuModifiers, isLoading: menuModifiersLoading } = useMenuModifiers()

  // F-DEAL-N1..N4: price rules, item hours, deal parts, automatic deals and
  // upsells. The base price follows the order type and the clock; the server
  // re-prices every line anyway (invariant #11) — this is so the screen agrees.
  const menuRules = useMenuRules()
  const menuClock = useMenuClock(menuRules.utc_offset_minutes)
  const rulesIndex = useMemo(() => buildMenuRulesIndex(menuRules, menuClock), [menuRules, menuClock])
  // F-DEAL-N2/N3: Foodpanda-style marketplace orders take only rules that
  // name their order type (the platform fixes their prices).
  const isMarketplaceOrderType = createOrderPricingExtras.taxTreatment === 'marketplace_settled'
  const basePriceOf = useCallback(
    (p: Pick<Product, 'id' | 'price'>) => rulesIndex.basePrice(p, orderType, false, isMarketplaceOrderType).price,
    [rulesIndex, orderType, isMarketplaceOrderType],
  )
  const productById = useMemo(() => new Map(products.map((p) => [p.id, p] as const)), [products])
  // The options sheet resets its picks when `groups` changes identity, so the
  // deal-choice sold-out marking is memoised on the DATA, never the clock.
  const configuratorProductId = configurator?.product.id ?? null
  const configuratorGroups = useMemo(
    () =>
      configuratorProductId
        ? markSoldOutDealChoices(menuModifiers.groupsForProduct(configuratorProductId), (id) => menuRules.combo_choices?.[id]?.is_available === false)
        : [],
    [configuratorProductId, menuModifiers, menuRules],
  )
  const configuratorOptionById = useMemo(
    () =>
      configuratorProductId
        ? markSoldOutDealChoiceMap(menuModifiers.optionByIdFor(configuratorProductId), (id) => menuRules.combo_choices?.[id]?.is_available === false)
        : new Map(),
    [configuratorProductId, menuModifiers, menuRules],
  )
  const configuratorIncludes = useMemo(
    () => (configuratorProductId ? (menuRules.combos?.[configuratorProductId] ?? []).map((p) => `${p.quantity} ${p.name}`) : []),
    [configuratorProductId, menuRules],
  )
  /** The upsell strip under the ticket (F-DEAL-N4). */
  const [upsellOffer, setUpsellOffer] = useState<{
    triggerId: string
    triggerLineId: string
    triggerName: string
    prompt: string
    productIds: string[]
    dealIds: string[]
  } | null>(null)
  const upsellShownRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    if (cart.length === 0) {
      setUpsellOffer(null)
      upsellShownRef.current = new Set()
    }
  }, [cart.length])

  // Unit price is DERIVED here, never stored on the line and never persisted.
  // Same discipline as restoreDraftItems rebuilding from the fresh product:
  // a price that is only ever computed cannot go stale.
  const pricedCart: PricedCartItem[] = useMemo(
    () =>
      cart.map((item) => ({
        ...item,
        // F-MOD-N13: the product's own option prices — what the server charges.
        // F-DEAL-N2: on top of this order type's / this hour's base price.
        unitPrice: resolveLineUnitPrice(basePriceOf(item.product), item.selections, menuModifiers.priceIndexFor(item.product.id)),
        modifierLabels: [
          // F-DEAL-N1: a deal's fixed items lead, as on the kitchen ticket.
          ...rulesIndex.comboParts(item.product.id).map((p) => (p.quantity > 1 ? `${p.name} x${p.quantity}` : p.name)),
          ...item.selections
            .map((sel) => {
              const o = menuModifiers.optionByIdFor(item.product.id).get(sel.option_id)
              if (!o) return ''
              return sel.quantity > 1 ? `${o.name} x${sel.quantity}` : o.name
            })
            .filter(Boolean),
          ...(() => {
            const rule = rulesIndex.basePrice(item.product, orderType, false, isMarketplaceOrderType)
            return rule.rule_id && rule.label ? [`${rule.label} price`] : []
          })(),
        ],
      })),
    [cart, menuModifiers, basePriceOf, rulesIndex, orderType, isMarketplaceOrderType],
  )

  const cartSubtotal = pricedCart.reduce((t, item) => t + item.unitPrice * item.quantity, 0)
  // F-DEAL-N3: the automatic deal a NEW order will get (the server applies
  // the same rule when the order is created). An open tab's deal is
  // re-derived by the server on every add and shows on its bill.
  const autoDealPreview = useMemo(() => {
    if (pricedCart.length === 0) return { best: null, hints: [] }
    const lines = pricedCart.map((i) => ({
      product_id: i.product.id,
      category_id: i.product.category_id ?? '',
      unit_price: i.unitPrice,
      quantity: i.quantity,
    }))
    // Adding to an open tab (every dine-in table): the server re-derives the
    // deal over the whole order, so preview over what is already on it plus
    // the new items, and show only the extra saving. An explicit discount on
    // the tab (promo, card promo, reward, manual) owns the slot — no preview.
    let already = 0
    if (appendingToLoadedOrder) {
      const tab = existingOrder
      if (!tab) return { best: null, hints: [] }
      const tabDiscount = Number(tab.discount_amount ?? 0)
      const tabIsDeal = (tab.discount_label ?? '').startsWith('Deal · ')
      if (tabDiscount > 0 && !tabIsDeal) return { best: null, hints: [] }
      already = tabIsDeal ? tabDiscount : 0
      for (const it of tab.items ?? []) {
        if (it.status === 'voided') continue
        lines.unshift({
          product_id: it.product_id,
          category_id: productById.get(it.product_id)?.category_id ?? '',
          unit_price: Number(it.unit_price),
          quantity: it.quantity,
          ...(Number(it.discount_amount ?? 0) > 0 ? { excluded: true } : {}),
        })
      }
    }
    const full = rulesIndex.autoDiscount(lines, orderType, 'till', isMarketplaceOrderType)
    const res = {
      best:
        full.best && full.best.amount - already > 0.004
          ? { ...full.best, amount: Math.round((full.best.amount - already) * 100) / 100 }
          : null,
      hints: full.hints,
    }
    // Only nudges that would beat the deal already on the ticket.
    const hints = usefulHints(
      (menuRules.auto_discounts ?? []).filter((r) => !isMarketplaceOrderType || (r.order_types ?? []).includes(orderType)),
      lines,
      { order_type: orderType, channel: 'till', clock: menuClock },
      res.hints,
      full.best,
      (id) => {
        const p = productById.get(id)
        return p ? { category_id: p.category_id ?? '', unit_price: basePriceOf(p) } : undefined
      },
    )
    return { best: res.best, hints }
  }, [appendingToLoadedOrder, existingOrder, pricedCart, rulesIndex, orderType, menuRules, menuClock, productById, basePriceOf, isMarketplaceOrderType])
  const autoDealAmount = autoDealPreview.best?.amount ?? 0
  const cartTotals = computeCartTotals(cartSubtotal, autoDealAmount, createCheckoutIntent, pricing, {
    includeServiceCharge: createOrderPricingExtras.includeServiceCharge,
    deliveryFee: effectiveDeliveryFee,
    // F-MM-N23: the not-yet-placed ticket prices from the order type config;
    // once created, the server-stamped order.tax_treatment is the authority.
    taxTreatment: createOrderPricingExtras.taxTreatment,
    serviceChargeRateOverride:
      serviceChargeOverride?.kind === 'rate' ? serviceChargeOverride.value : null,
    serviceChargeAmountOverride:
      serviceChargeOverride?.kind === 'amount' ? serviceChargeOverride.value : null,
  })
  // Total quantity of unsent draft items in the cart — feeds the Ticket tab
  // badge in narrow-mode so cashiers get visible confirmation when adding from
  // the Menu tab (the cart panel itself is hidden in that layout).
  const unsentCount = cart.reduce((n, item) => n + item.quantity, 0)

  // Hold the desktop update banner's restart while a checkout is open (F-FE-N47).
  // The cart itself is NOT a reason — useCounterDraft persists it and restores
  // it on boot. A half-entered tender does not survive, so that is.
  useBlockRestart('Finish the open checkout first', checkoutOpen)

  /**
   * Add one unit of a product to the cart.
   *
   * Merging is decided by `mergeKey`, never by product id: two lines of the
   * same product with different modifier selections (or different notes) must
   * stay apart. For a product with no selections and no note the key is
   * `"<productId>##"`, so this behaves exactly as the old product-id merge did.
   */
  /**
   * F-DEAL-N4: after an item lands in the ticket, offer its upsells (and a
   * "make it a deal" upgrade) once per item per ticket. Non-blocking: the
   * strip sits under the ticket and the cashier can ignore it.
   */
  const offerUpsellsFor = (product: Product, lineId: string) => {
    if (!rulesIndex.upsellEnabled || upsellShownRef.current.has(product.id)) return
    const inCart = new Set(cart.map((c) => c.product.id))
    inCart.add(product.id)
    const usable = (id: string) => {
      const p = productById.get(id)
      return Boolean(p && p.is_available && !inCart.has(id) && rulesIndex.available(p) && !rulesIndex.comboSoldOut(id))
    }
    const sugg = rulesIndex.upsellFor(product.id, product.category_id)
    const productIds = (sugg?.productIds ?? []).filter(usable)
    const dealIds = rulesIndex.dealUpgradesFor(product.id).filter(usable)
    if (productIds.length === 0 && dealIds.length === 0) return
    upsellShownRef.current.add(product.id)
    setUpsellOffer({
      triggerId: product.id,
      triggerLineId: lineId,
      triggerName: product.name,
      prompt: sugg?.prompt ?? 'Goes well with',
      productIds,
      dealIds,
    })
    logUpsellEvents([
      ...productIds.map((id) => ({ kind: 'item' as const, event: 'shown' as const, trigger_product_id: product.id, suggested_product_id: id })),
      ...dealIds.map((id) => ({ kind: 'deal' as const, event: 'shown' as const, trigger_product_id: product.id, suggested_product_id: id })),
    ])
  }

  const acceptUpsell = (suggestedId: string, kind: 'item' | 'deal') => {
    const offer = upsellOffer
    const p = productById.get(suggestedId)
    if (!offer || !p) return
    logUpsellEvents([{ kind, event: 'accepted', trigger_product_id: offer.triggerId, suggested_product_id: suggestedId, value: basePriceOf(p) }])
    if (kind === 'deal') {
      setUpsellOffer(null)
      // The deal's choices still have to be picked; the trigger item comes
      // off the ticket when the cashier confirms the sheet.
      setConfigurator({ product: p, initial: null, editingLineId: null, dealUpgradeFromLineId: offer.triggerLineId })
      return
    }
    const remaining = offer.productIds.filter((id) => id !== suggestedId)
    setUpsellOffer(remaining.length > 0 || offer.dealIds.length > 0 ? { ...offer, productIds: remaining } : null)
    if (menuModifiers.requiresConfigurator(p.id)) {
      setConfigurator({ product: p, initial: null, editingLineId: null })
      return
    }
    addToCart(p, [], 1, { skipUpsell: true })
  }

  const addToCart = (product: Product, selections: LineSelection[] = [], addQty = 1, opts?: { skipUpsell?: boolean }) => {
    if (!product.is_available) return
    if (!rulesIndex.available(product)) {
      const hours = rulesIndex.hoursLabel(product)
      toastHelpers.info('Not available right now', hours ? `${product.name} is only available ${hours}.` : `${product.name} is not available right now.`)
      return
    }
    const key = cartLineMergeKey(product.id, selections)
    const existingItem = cart.find((item) => item.mergeKey === key)
    const wasExisting = Boolean(existingItem)
    let targetLineId: string
    // Functional updates, so an add composes with a change queued in the same
    // tick (F-DEAL-N4 "make it a deal" removes the item, then adds the deal).
    if (existingItem) {
      targetLineId = existingItem.lineId
      setCart((prev) =>
        prev.map((item) =>
          item.lineId === existingItem.lineId ? { ...item, quantity: item.quantity + addQty } : item
        )
      )
    } else {
      const line = makeCartLine(product, addQty, selections)
      targetLineId = line.lineId
      setCart((prev) => [...prev, line])
    }
    if (tapFlashTimeoutRef.current != null) clearTimeout(tapFlashTimeoutRef.current)
    setLastTappedProductId(product.id)
    tapFlashTimeoutRef.current = setTimeout(() => {
      tapFlashTimeoutRef.current = null
      setLastTappedProductId((current) => (current === product.id ? null : current))
    }, 240)
    // Trigger the "+1" pop. Date.now() guarantees a new key on every tap (even
    // rapid-fire on the same product) so the keyframe restarts cleanly.
    setTapPop({ productId: product.id, nonce: Date.now() })

    if (cartFlashTimeoutRef.current != null) clearTimeout(cartFlashTimeoutRef.current)
    setLastCartFlashLineId(targetLineId)
    cartFlashTimeoutRef.current = setTimeout(() => {
      cartFlashTimeoutRef.current = null
      setLastCartFlashLineId((current) => (current === targetLineId ? null : current))
    }, 280)

    setCartLiveAnnouncement({
      text: wasExisting ? `Updated ${product.name} in cart` : `Added ${product.name} to cart`,
      id: Date.now(),
    })
    if (!opts?.skipUpsell) offerUpsellsFor(product, targetLineId)

    requestAnimationFrame(() => {
      cartRowRefs.current[targetLineId]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    })
  }

  /**
   * A product tile was tapped.
   *
   * Owner decision (2026-08-20): the sheet opens ONLY when the product has a
   * group that has to be answered — a size, a required pick. A latte carrying
   * nothing but optional add-ons still rings up in a single tap, because the
   * coffee shop taps it hundreds of times a shift and would feel a second tap
   * on every one. Optional groups are reached from the cart row instead.
   */
  const handleTileTap = (product: Product) => {
    if (!product.is_available) return
    // Cold start only: until the catalogue lands we cannot tell whether this
    // product needs a size, and adding it blind produces a line the server
    // will refuse at submit. Sub-second on a warm cache (idbPersister keeps it
    // through a restart), so this says so rather than guessing.
    if (menuModifiersLoading) {
      toastHelpers.info('One moment', 'Still loading this menu’s options.')
      return
    }
    if (menuModifiers.requiresConfigurator(product.id)) {
      setConfigurator({ product, initial: null, editingLineId: null })
      return
    }
    addToCart(product)
  }

  /** Open the sheet for a staged line — the way optional options are reached. */
  const openLineOptions = useCallback(
    (lineId: string) => {
      const line = cart.find((i) => i.lineId === lineId)
      if (!line) return
      setConfigurator({
        product: line.product,
        initial: {
          selections: line.selections.map((sel) => ({
            group_id: sel.group_id,
            option_id: sel.option_id,
            quantity: sel.quantity,
          })),
          quantity: line.quantity,
        },
        editingLineId: lineId,
      })
    },
    [cart],
  )

  /**
   * Commit the sheet. Adding merges into a line with the same options; editing
   * replaces the line's options and quantity, then folds any collision the
   * change created — editing one line into an exact match of another must
   * leave a single line, not two identical rows and two identical KOT lines.
   */
  const commitConfigurator = useCallback(
    (result: ConfiguredLine) => {
      const ctx = configurator
      if (!ctx) return
      const selections: LineSelection[] = result.selections.map((sel) => ({
        group_id: sel.group_id,
        option_id: sel.option_id,
        quantity: sel.quantity,
      }))
      if (ctx.editingLineId) {
        setCart((prev) =>
          foldCartCollisions(
            prev.map((item) =>
              item.lineId === ctx.editingLineId
                ? {
                    ...item,
                    selections,
                    quantity: result.quantity,
                    mergeKey: cartLineMergeKey(
                      item.product.id,
                      selections,
                      item.special_instructions,
                    ),
                  }
                : item,
            ),
          ),
        )
      } else {
        // F-DEAL-N4: a deal chosen from "make it a deal" replaces one unit of
        // the item it was suggested for.
        if (ctx.dealUpgradeFromLineId) {
          const from = ctx.dealUpgradeFromLineId
          setCart((prev) =>
            prev.flatMap((item) =>
              item.lineId !== from ? [item] : item.quantity > 1 ? [{ ...item, quantity: item.quantity - 1 }] : [],
            ),
          )
        }
        addToCart(ctx.product, selections, result.quantity, { skipUpsell: Boolean(ctx.dealUpgradeFromLineId) })
      }
      setConfigurator(null)
    },
    // addToCart closes over `cart`, so it is intentionally not a dependency —
    // the callback is recreated each render anyway.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [configurator, cart],
  )

  /** Decrement one unit of a specific LINE, dropping it at zero. */
  const removeFromCart = (lineId: string) => {
    const existingItem = cart.find((item) => item.lineId === lineId)
    if (existingItem && existingItem.quantity > 1) {
      setCart(
        cart.map((item) =>
          item.lineId === lineId ? { ...item, quantity: item.quantity - 1 } : item
        )
      )
    } else {
      setCart(cart.filter((item) => item.lineId !== lineId))
      delete cartRowRefs.current[lineId]
    }
  }

  /**
   * Update the per-line `special_instructions` on a staged cart item. The
   * note rides through the existing submit pipeline (`createCounterOrder` and
   * `addItemsToOrder` both forward `special_instructions`) and is rendered by
   * the KOT printer (`kot.go` line 885) and the KDS payload (`kot.go` line
   * 273). Empty strings are stored as undefined so the JSON payload stays
   * tidy and KOT lines without a note skip the `>>` decoration.
   *
   * The note participates in `mergeKey`, so it is recomputed here and any
   * resulting collision is folded — clearing a note off a line that now
   * matches another one must produce a single line of summed quantity, not two
   * identical rows and two identical KOT lines.
   */
  const setItemNotes = useCallback((lineId: string, note: string) => {
    const trimmed = note.trim()
    setCart((prev) =>
      foldCartCollisions(
        prev.map((item) =>
          item.lineId === lineId
            ? {
                ...item,
                special_instructions: trimmed === '' ? undefined : trimmed,
                mergeKey: cartLineMergeKey(item.product.id, item.selections, trimmed),
              }
            : item
        )
      )
    )
  }, [])

  // F-RSV-N1: a free table held for an upcoming booking asks before opening.
  const reservedGuard = useReservedTableGuard()
  const handleFreeTableGuarded = (table: DiningTable) =>
    reservedGuard.guard(table, () => handleFreeTable(table))

  const handleFreeTable = (table: DiningTable) => {
    // F-FE-N62 — an order is already loaded, so this is "give THIS order a
    // table", not "start a new one".
    //
    // Falling through to openTableTab here cut a brand-new order on the chosen
    // table and abandoned the loaded one: its items, discounts and payment
    // state stayed behind on the original — now permanently table-less — order
    // while the cashier was left looking at an empty ticket. A dine-in order
    // created without a table has no other way to get one; "Pick table" is the
    // only affordance the rail offers (the transfer dialog refuses to open
    // without an existing table), so this was the whole path, not an edge of it.
    //
    // Deliberately does NOT reset checkout/session state below: the button
    // lives in the checkout toolbar, so the operator is usually mid-payment and
    // only wants the table attached. reassignTableMutation refreshes
    // existingOrder and selectedTable from the server response.
    //
    // Safe against every entry point — both the toolbar button and the "T"
    // shortcut are scoped to the current dine-in order, and TablesPicker's
    // transfer mode stages its selection instead of calling this at all.
    if (existingOrder?.id) {
      reassignTableMutation.mutate({ orderId: existingOrder.id, tableId: table.id })
      return
    }
    setCheckoutOpen(false)
    setSelectedOrder(null)
    setContinuingOrderId(null)
    setExistingOrder(null)
    setExistingItemsExpanded(false)
    setDineInSession(null)
    // Open the tab immediately with everything unset — guests, server and
    // notes are edited afterwards via the rail toolbar popovers.
    void openTableTab(table, { guestCount: 0, serverId: '', serverDisplayName: '' })
  }

  const handleOccupiedTable = async (table: DiningTable) => {
    setCheckoutOpen(false)
    setSelectedOrder(null)
    try {
      const res = await apiClient.getActiveOrderForTable(table.id)
      if (res.success && res.data) {
        const o = res.data
        setContinuingOrderId(o.id)
        setExistingOrder(o)
        setExistingItemsExpanded(false)
        setSelectedTable(table)
        const disp =
          o.user && (o.user.first_name || o.user.last_name)
            ? `${o.user.first_name} ${o.user.last_name}`.trim()
            : o.user?.username ?? '—'
        setDineInSession({
          guestCount: o.guest_count ?? 0,
          serverId: o.user_id ?? '',
          serverDisplayName: disp,
          customerName: o.customer_name,
          customerEmail: o.customer_email,
          customerPhone: o.customer_phone,
          guestBirthday: o.guest_birthday,
        })
        setCustomerName(o.customer_name ?? '')
        setCustomerEmail(o.customer_email ?? '')
        setCustomerPhone(o.customer_phone ?? '')
        setGuestBirthday(toGuestDateInputValue(o.guest_birthday))
        setDeliveryAddress(o.delivery_address ?? '')
        setDeliveryFeeOverride(o.delivery_fee_override ?? null)
        setServiceChargeOverride(serviceChargeOverrideFromOrder(o))
        clearCart()
        setOrderNotes('')
        clearDraft(draftUserId)
        // Intentionally no toast: the rail immediately re-renders with the loaded
        // order (order number, items, table badge) — the UI itself is the affirmation.
      }
    } catch {
      toastHelpers.error('Table', 'Could not load the open order for this table.')
    }
  }

  const openTableTab = async (table: DiningTable, s: TableSession) => {
    // F-CTRL-N38: opening a tab is a SERVER call — it allocates an order number
    // and takes a row lock on the table. Offline it cannot run, and dine-in used
    // to die right here, at table selection, before the cashier ever reached the
    // fire button.
    //
    // Offline we simply hold the selection locally. No tab is opened, no order
    // exists yet; the order is created on replay carrying this table_id, and the
    // occupancy check runs then — server-side, where it can actually be trusted.
    //
    // The trade is explicit and worth stating precisely. The strict
    // one-order-per-table gate lives in OpenCounterTableTab, which is what we
    // are skipping. Replay goes through createOrderCore, which deliberately
    // does NOT reject a busy table (orders.go: a second dine-in order can be
    // legitimate — split remnant, re-fire, second party after a mis-close).
    //
    // So two offline terminals picking the same table produce TWO open orders
    // on it. No error, and nothing lands in the reconciliation queue. That is
    // the same outcome the online create-order path already allows, and the
    // floor resolves it by settling or merging — but it is a real difference
    // from the tab flow, not a caught-and-supervised conflict.
    if (!online && offlineCapture.capability.ok) {
      setSelectedTable(table)
      setDineInSession(s)
      setContinuingOrderId(null)
      setExistingOrder(null)
      setCustomerName(s.customerName ?? '')
      setCustomerEmail(s.customerEmail ?? '')
      setCustomerPhone(s.customerPhone ?? '')
      setGuestBirthday(s.guestBirthday ?? '')
      setCustomerSavedAddresses([])
      setDeliveryAddress('')
      setDeliveryFeeOverride(null)
      setServiceChargeOverride(null)
      setServiceChargeAuth(null)
      return
    }
    try {
      const res = await apiClient.openCounterTableTab({
        table_id: table.id,
        guest_count: s.guestCount,
        ...(s.serverId ? { assigned_server_id: s.serverId } : {}),
        customer_name: s.customerName,
        customer_email: s.customerEmail,
        customer_phone: s.customerPhone,
        guest_birthday: s.guestBirthday,
        notes: s.notes,
      })
      if (!res.success || !res.data) {
        toastHelpers.error('Table', res.message || 'Could not open table tab')
        return
      }
      setSelectedTable(table)
      setDineInSession(s)
      setContinuingOrderId(res.data.id)
      setExistingOrder(res.data)
      setCustomerName(s.customerName ?? res.data.customer_name ?? '')
      setCustomerEmail(s.customerEmail ?? res.data.customer_email ?? '')
      setCustomerPhone(s.customerPhone ?? res.data.customer_phone ?? '')
      setGuestBirthday(s.guestBirthday ?? toGuestDateInputValue(res.data.guest_birthday))
      // Dine-in tab — no delivery address; clear any leftover from a prior order type.
      setCustomerSavedAddresses([])
      setDeliveryAddress('')
      setDeliveryFeeOverride(null)
      setServiceChargeOverride(null)
      setServiceChargeAuth(null)
      clearDraft(draftUserId)
      // Intentionally no toast: the newly-opened tab appears in the rail header
      // (order number, table badge, guest/server meta) as visible confirmation.
      invalidateTables(queryClient)
    } catch (e) {
      toastHelpers.error('Table', e instanceof Error ? e.message : 'Request failed')
    }
  }

  // Reprint KOT — rebuild the fired ticket(s) and either print them on this
  // counter's receipt printer ("here") or route them to the station printers
  // exactly like a fire ("kitchen"). The backend is read-only for this call.
  const hasFiredLines = Boolean(
    existingOrder?.items?.some((it) => VOIDABLE_STATUSES.has(String(it.status ?? ''))),
  )
  const [reprintKotPending, setReprintKotPending] = useState(false)
  const handleReprintKot = async (mode: 'here' | 'kitchen') => {
    const orderId = existingOrder?.id ?? continuingOrderId
    if (!orderId) return
    setReprintKotPending(true)
    try {
      const r = await apiClient.reprintKOT(orderId)
      if (!r.success || !r.data) {
        const code = r.error ?? ''
        toastHelpers.error(
          'Reprint KOT',
          code === 'no_fired_items'
            ? 'Nothing has been fired to the kitchen on this order yet.'
            : r.message || 'Could not rebuild the kitchen ticket.',
        )
        return
      }
      const kots = r.data.kots ?? []
      const settingsRes = await queryClient.fetchQuery({
        queryKey: ['settings', 'all'],
        queryFn: () => apiClient.getAllSettings(),
      })
      const cfg = parseReceiptSettings((settingsRes.data ?? {}) as Record<string, unknown>)

      if (mode === 'here') {
        // Every station, KDS ones included — a reprint is a paper artefact.
        // Android PWA caveat: after an await this is no longer a user gesture,
        // so intent:// may not launch there; the counter is desktop in practice.
        await printKotReceipts(kots, cfg, { device: getReceiptPrinter(), includeKds: true })
        toastHelpers.success(
          'Reprint KOT',
          `Printed ${kots.length} ticket${kots.length === 1 ? '' : 's'} here.`,
        )
        return
      }

      const printerSlips = kots.filter((k) => k.output_type === 'printer')
      if (printerSlips.length === 0) {
        toastHelpers.info(
          'Reprint KOT',
          'This order is on the KDS — use "Print here" for a paper copy.',
        )
        return
      }
      if (isAndroidPrintClient() && !isBhooklyNativeServerApp()) {
        // Chrome PWA: the Print tap inside the modal is the gesture intent:// needs.
        setLastFireKots(kots)
        setKotPrintOpen(true)
        return
      }
      await printKotReceipts(kots, cfg)
      toastHelpers.success(
        'Reprint KOT',
        `Resent ${printerSlips.length} ticket${printerSlips.length === 1 ? '' : 's'} to the kitchen.`,
      )
    } catch (e) {
      toastHelpers.error('Reprint KOT', e instanceof Error ? e.message : 'Request failed')
    } finally {
      setReprintKotPending(false)
    }
  }

  const cancelOpenTabMutation = useMutation({
    mutationFn: (orderId: string) => apiClient.cancelCounterOpenTab(orderId),
    onSuccess: (res) => {
      if (!res.success) {
        toastHelpers.error('Cancel tab', res.message || 'Could not cancel')
        return
      }
      setContinuingOrderId(null)
      setExistingOrder(null)
      setDineInSession(null)
      setSelectedTable(null)
      clearCart()
      setOrderNotes('')
      clearDraft(draftUserId)
      setCheckoutOpen(false)
      setSelectedOrder(null)
      invalidateTables(queryClient)
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      toastHelpers.success('Session cancelled', 'Table is free again.')
    },
    onError: (e: Error) => {
      toastHelpers.error('Cancel tab', e.message || 'Request failed')
    },
  })

  const reassignTableMutation = useMutation({
    mutationFn: ({ orderId, tableId }: { orderId: string; tableId: string }) =>
      apiClient.reassignCounterOrderTable(orderId, { table_id: tableId }),
    onSuccess: (res) => {
      if (!res.success || !res.data) {
        toastHelpers.error('Change table', res.message || 'Could not reassign table')
        return
      }
      setExistingOrder(res.data)
      setSelectedTable(res.data.table ?? null)
      setTableTransferOpen(false)
      invalidateTables(queryClient)
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['counterPendingOrders'] })
      // Intentionally no toast: the table badge in the ticket header flips to the
      // new table number, which is clearer than a floating banner.
    },
    onError: (e: Error) => {
      toastHelpers.error('Change table', e.message || 'Request failed')
    },
  })

  /**
   * F-FE-N30: take the order on this device and fire its kitchen ticket
   * locally. Only reachable when offlineCapture.capability.ok — i.e. a shell
   * that prints silently (desktop app or Server APK, F-FE-N123) with station
   * routing cached.
   *
   * Ordering matters. The order is queued for replay BEFORE the ticket prints,
   * so a crash between the two leaves a queued order with no ticket (recoverable
   * — the kitchen simply never saw it) rather than a printed ticket with no
   * queued order, which is food out the door with no record.
   */
  const handleOfflineSubmitCart = async () => {
    const userId = getDraftUserId()
    if (!userId) {
      toastHelpers.error('Offline order', 'Could not identify the signed-in user.')
      return
    }

    const result = await offlineCapture.captureOffline({
      // F-FE-N31: a held order has no server id, so its append is a local
      // mutation of the queue entry. It wins over targetOrderId — an order
      // cannot be both queued here and already live there.
      heldOpId: offlineTarget?.opId ?? null,
      // F-CTRL-N37: continuing an order that already exists on the server is an
      // APPEND, not a new order. Replay routes it to add-items, so the customer
      // is not billed for a duplicate order and the existing tab stays whole.
      targetOrderId: offlineTarget ? null : continuingOrderId,
      // The identifier already printed on this order's earlier slip: the held
      // entry's local ticket, or the real number if the server already has it.
      existingTicket: offlineTarget?.ticketRef ?? existingOrder?.order_number,
      orderType,
      tableId: offlineTarget?.tableId ?? selectedTable?.id ?? null,
      tableNumber: selectedTable?.table_number ?? '',
      serverName: offlineServerName,
      orderTypeLabel: orderType.replace(/_/g, ' ').toUpperCase(),
      orderNote: orderNotes,
      userId,
      lines: cart.map((c) => ({
        productId: c.product.id,
        quantity: c.quantity,
        special_instructions: c.special_instructions,
        name: c.product.name,
        categoryId: c.product.category_id ?? '',
        // Ids only for the replay queue — the server reprices on reconnect.
        // Printed labels are resolved at CAPTURE time further down, because
        // the KOT is a snapshot of what was printed while the queue is an
        // instruction to be re-priced later. Different data, both correct.
        ...(c.selections.length > 0
          ? {
              selections: c.selections.map((sel) => ({
                option_id: sel.option_id,
                quantity: sel.quantity,
              })),
            }
          : {}),
      })),
    })

    if (!result.ok) {
      if (result.reason === 'already_synced') {
        // F-FE-N31: the queue drained while the cashier was composing, so the
        // order is a real one now and the local copy is no longer the way in.
        // The cart is deliberately kept — those items were typed for a
        // customer, and clearing them would make the round vanish.
        // What the SLIP said — the local ticket, or a legacy reserved number.
        const num = offlineTarget?.ticketRef || offlineTarget?.orderNumber || ''
        setOfflineTarget(null)
        setOfflineTrayNonce((n) => n + 1)
        toastHelpers.warning(
          `Order ${num} has synced`,
          'It is back in Open Orders. Your items are still in the cart — pick that order and add them.',
        )
        return
      }
      toastHelpers.error(
        'Offline order',
        'This terminal is not ready to take offline orders. Reconnect once to prepare it.',
      )
      return
    }

    // Keep the held order loaded so the same table's next round goes to the
    // same ticket, and re-read it so the "already sent" list shows what just
    // fired. A fresh capture does NOT stay loaded (see handleSubmitCart) —
    // there is no payment step offline to end the ticket, so it would sit
    // loaded until someone noticed, and the next customer's items would land
    // on the previous customer's order.
    if (result.held) {
      setOfflineTarget(result.held)
      setOfflineTrayNonce((n) => n + 1)
    }

    try {
      // F-FE-N72: use the TENANT's receipt settings, read straight out of the
      // persisted query cache.
      //
      // This passed `parseReceiptSettings({})` — i.e. the hard-coded 80 mm
      // defaults — so a 58 mm tenant printed every offline kitchen ticket at the
      // wrong width, for exactly the tickets nobody can reprint correctly later.
      // `['settings','all']` IS in PERSIST_PREFIXES, so it survives the outage.
      //
      // getQueryData, not fetchQuery: fetchQuery would go to the network when
      // the entry is stale, and the network is the thing that is gone. An empty
      // object still falls back to the same defaults as before, so the worst
      // case is unchanged.
      const cachedSettings = queryClient.getQueryData<APIResponse<Record<string, unknown>>>([
        'settings',
        'all',
      ])
      await printKotReceipts(result.slips, parseReceiptSettings(cachedSettings?.data ?? {}))
    } catch {
      // The order is already queued, so a print failure loses the ticket, not
      // the sale. Tell the cashier so they can relay it by hand.
      toastHelpers.warning(
        `Order ${result.ticket} held`,
        'The order is saved, but the kitchen ticket did not print. Tell the kitchen directly.',
      )
      clearOfflineCapturedCart(result.held != null)
      return
    }

    toastHelpers.success(
      result.held
        ? `Added to ${result.ticket} — ticket printed`
        : `Ticket ${result.ticket} — printed`,
      'Saved on this device. It will sync when you reconnect.',
    )

    // F-FE-N77: payment is NOT offered here.
    //
    // It used to open a modal automatically the moment the ticket printed,
    // which took the terminal hostage: the cashier could not ring the next
    // order, could not open another ticket, and on a short window could not
    // reach the sheet's own buttons. Firing a ticket and settling a bill are
    // two different moments — the guest is usually still deciding. The held
    // card in the tray carries a Pay action for whenever they are ready.
    clearOfflineCapturedCart(result.held != null)
  }

  /**
   * Cancel a line on the held offline order, and tell the kitchen (F-CTRL-N56).
   *
   * Two things have to happen, in this order:
   *
   *  1. The void chit prints FIRST. The kitchen is already making this item, and
   *     the only thing that stops them is paper. If the chit fails, the line
   *     stays on the order — better the guest is charged for food that was
   *     actually made than for the POS to quietly drop a line the kitchen is
   *     still cooking, which is "food out, nobody billed" arrived at backwards.
   *  2. Only then is the line removed from the queued entry.
   *
   * No manager PIN, by the owner's decision. Nothing is being overridden: this
   * order has never reached the server, so there is no live state to protect,
   * and staffpin.Identify is a bcrypt check against the database that cannot run
   * with no connection. The void is recorded on the entry with who was signed in.
   */
  const voidOfflineLine = async (lineIndex: number) => {
    const target = offlineTarget
    if (!target) return
    const line = target.lines[lineIndex]
    if (!line) return

    const cache = await readStationCache()
    if (!cache) {
      toastHelpers.error(
        'Cannot void',
        'Kitchen routing is not cached, so the void chit cannot be routed. Void this after reconnecting.',
      )
      return
    }

    const product = products.find((p) => p.id === line.productId)
    const slips = buildOfflineVoidSlips({
      ticket: target.ticketRef || target.orderNumber || '',
      tableNumber: selectedTable?.table_number ?? '',
      serverName: offlineServerName,
      orderTypeLabel: displayLabelForOrderType(target.orderType, orderTypesRaw),
      capturedAt: Date.now(),
      orderNote: '',
      items: [
        {
          name: product?.name ?? offlineProductName(line.productId),
          quantity: line.quantity,
          categoryId: product?.category_id ?? '',
        },
      ],
      stations: cache.stations,
      categoryToStation: cache.categoryToStation,
    })

    try {
      const cachedSettings = queryClient.getQueryData<APIResponse<Record<string, unknown>>>([
        'settings',
        'all',
      ])
      await printKotReceipts(slips, parseReceiptSettings(cachedSettings?.data ?? {}))
    } catch {
      toastHelpers.error(
        'Void chit did not print',
        'The item was NOT removed — the kitchen is still making it. Tell them directly, then try again.',
      )
      return
    }

    const updated = await voidLineFromPendingOfflineOrder(
      target.opId,
      lineIndex,
      // Same source the KOT prints as the server name — who was signed in.
      offlineServerName,
      Date.now(),
    )
    if (!updated) {
      // It synced while the cashier was deciding, so the server owns it now and
      // the void has to go through the server with a real PIN.
      setOfflineTarget(null)
      offlineCapture.refreshCounts()
      toastHelpers.warning(
        'Order has synced',
        'The void chit printed, but this order is now on the server — void the line from the order itself.',
      )
      return
    }

    // F-FE-N160: that may have been the LAST line, in which case the store has
    // finalised the entry as `discarded` — there is nothing left to send, so it
    // leaves the tray instead of becoming a zero-line row that replays forever.
    //
    // The old copy here said "The order syncs without it" unconditionally, which
    // on the last line was the exact opposite of what happened: the entry could
    // never sync and could never be removed.
    const emptied = updated.status === 'discarded'
    setOfflineTarget(emptied ? null : updated)
    setOfflineTrayNonce((n) => n + 1)
    offlineCapture.refreshCounts()
    toastHelpers.success(
      emptied ? 'Order cancelled' : 'Item cancelled',
      emptied
        ? 'That was the last item, so the whole order is cancelled. Void chits printed for the kitchen; nothing will be billed.'
        : 'A void chit printed for the kitchen. The order syncs without it.',
    )
  }

  /**
   * Value of a held offline order at a given tender (F-FE-N77).
   *
   * A queue entry stores ids and quantities only — never a price — so its worth
   * has to be re-derived from the cached menu, exactly as `pricedCart` derives
   * the live cart. The tender is a parameter because it selects the tax rate:
   * cash, card and online are three different amounts due (invariant #11).
   */
  const priceHeldOfflineOrder = useCallback(
    (o: OfflineOrder, intent: 'cash' | 'card' | 'online') =>
      priceOfflineOrder(o, {
        products,
        optionByIdFor: menuModifiers.optionByIdFor,
        priceIndexFor: menuModifiers.priceIndexFor,
        pricing,
        intent,
        // Priced for the HELD order's own type, not whatever tab the cart is
        // on now — a Foodpanda ticket held during the outage stays untaxed
        // and service-free when the cashier has since switched to Dine In.
        includeServiceCharge: getOrderTypePricing(o.orderType, orderTypesRaw, { marketplaceSettledEnabled }).includeServiceCharge,
        deliveryFee: o.orderType === 'delivery' ? effectiveDeliveryFee : 0,
        taxTreatment: getOrderTypePricing(o.orderType, orderTypesRaw, { marketplaceSettledEnabled }).taxTreatment,
        // F-DEAL-N2: priced at the moment it was rung, as the server replays it.
        basePriceFor: (p, entry) =>
          resolveBasePrice(p.price, menuRules.price_rules?.[p.id], { orderType: entry.orderType, online: false }, clockAt(entry.capturedAt, menuRules.utc_offset_minutes)).price,
      }),
    [
      menuRules,
      products,
      menuModifiers.optionByIdFor,
      menuModifiers.priceIndexFor,
      pricing,
      orderTypesRaw,
      marketplaceSettledEnabled,
      effectiveDeliveryFee,
    ],
  )


  /**
   * Record the tender on the queued entry and print the customer's slip
   * (F-CTRL-N55).
   *
   * The tender is written to IndexedDB FIRST. If the print then fails, the money
   * is still recorded and will settle on reconnect — a missing slip is an
   * annoyance, an unrecorded payment is a hole in the till. The reverse order
   * would risk handing over a receipt for a payment the POS forgot.
   */
  // F-MM-N23: same lock as the online payment panel, for a held offline order.
  const offlineMarketplaceLock = useMemo(() => {
    if (!offlinePayTarget) return undefined
    const t = getOrderTypePricing(offlinePayTarget.orderType, orderTypesRaw, { marketplaceSettledEnabled })
    if (t.taxTreatment !== 'marketplace_settled') return undefined
    const label = (orderTypesRaw.find((r) => r.id === offlinePayTarget.orderType)?.label ?? 'Marketplace').trim()
    const key = label.toLowerCase()
    const match = (pricing.online_submethods ?? []).find(
      (m) => m.label.trim().toLowerCase() === key || m.slug === key.replace(/[^a-z0-9]+/g, '_'),
    )
    return { label, providerSlug: match?.slug }
  }, [offlinePayTarget, orderTypesRaw, marketplaceSettledEnabled, pricing.online_submethods])

  const takeOfflinePayment = async (payment: OfflinePayment) => {
    const target = offlinePayTarget
    if (!target) return
    if (offlineMarketplaceLock) {
      // Belt and braces: the prompt cannot offer anything else, but the
      // stored payment is what replays, so pin it here too.
      payment = { ...payment, method: 'online', tendered: undefined, ...(offlineMarketplaceLock.providerSlug ? { subMethod: offlineMarketplaceLock.providerSlug } : {}) }
    }
    const ticket = target.ticketRef || target.orderNumber || ''
    setOfflinePayBusy(true)
    try {
      await updateOfflineOrder(target.opId, { payment })
      offlineCapture.refreshCounts()
      setOfflineTrayNonce((n) => n + 1)

      // Priced at the TENDER THE GUEST CHOSE, not at whatever tab the screen
      // happens to be on: the three tax rates are separate settings, so the
      // slip must show the figures that were actually charged.
      const priced = priceHeldOfflineOrder(target, payment.method)
      const cachedSettings = queryClient.getQueryData<APIResponse<Record<string, unknown>>>([
        'settings',
        'all',
      ])
      printOfflineNonFiscalReceipt(
        {
          ticketRef: ticket,
          lines: priced.lines,
          subtotal: priced.subtotal,
          tax: priced.tax,
          serviceCharge: priced.serviceCharge,
          discount: 0,
          deliveryFee: priced.deliveryFee,
          total: priced.total,
          orderType: target.orderType,
          taxTreatment: getOrderTypePricing(target.orderType, orderTypesRaw, { marketplaceSettledEnabled }).taxTreatment,
        },
        parseReceiptSettings(cachedSettings?.data ?? {}),
        {
          cashierName: offlineServerName,
          paymentMethod: payment.method,
          orderTypeLabel: displayLabelForOrderType(target.orderType, orderTypesRaw),
          ...(payment.tendered != null ? { cashTendered: payment.tendered } : {}),
        },
      )
      toastHelpers.success(
        `${ticket} — payment recorded`,
        'Slip printed. It is not a tax invoice; the real one is filed when you reconnect.',
      )
    } catch {
      toastHelpers.warning(
        `${ticket} — payment recorded, slip did not print`,
        'The money is saved on this device and will settle when you reconnect.',
      )
    } finally {
      setOfflinePayBusy(false)
      setOfflinePayTarget(null)
    }
  }

  const handleSubmitCart = () => {
    if (cart.length === 0) return
    // F-FE-N103: the only guard that works at millisecond scale. Read and set
    // synchronously, so a second activation in the same frame — a tap that
    // registers twice, F1 landing alongside a click — sees `true` and stops
    // here. The button's `disabled` prop cannot do this: it is driven by
    // `isPending`, which React has not applied yet.
    if (submitInFlightRef.current) return
    submitInFlightRef.current = true
    const releaseSubmit = () => {
      submitInFlightRef.current = false
    }
    // One key per SUBMISSION. Minted here so both racing calls would carry the
    // same one, and kept across a failure so a retry of this cart is deduped
    // rather than counted as a new order. Cleared in onSuccess.
    if (!cartSubmitOpIdRef.current) cartSubmitOpIdRef.current = newSubmitOpId()
    // F-FE-N31: a held order lives only in this device's queue, so its next
    // round can only be appended locally — even if the connection came back a
    // moment ago. If it synced in the meantime, captureOffline says so and
    // hands the cashier to the real order rather than silently ringing a
    // duplicate through the online path.
    if (offlineTarget) {
      void handleOfflineSubmitCart().finally(releaseSubmit)
      return
    }
    if (!online && offlineCapture.capability.ok) {
      void handleOfflineSubmitCart().finally(releaseSubmit)
      return
    }
    submitCartMutation.mutate(undefined, { onSettled: releaseSubmit })
  }

  // F-MM-N23: a marketplace-settled order is locked to Online › <its provider>.
  const marketplaceLock = useMemo(() => {
    if (!payOrder || payOrder.tax_treatment !== 'marketplace_settled') return undefined
    const label = (orderTypesRaw.find((r) => r.id === payOrder.order_type)?.label ?? 'Marketplace').trim()
    const key = label.toLowerCase()
    const match = (pricing.online_submethods ?? []).find(
      (m) => m.label.trim().toLowerCase() === key || m.slug === key.replace(/[^a-z0-9]+/g, '_'),
    )
    return { label, providerSlug: match?.slug }
  }, [payOrder, orderTypesRaw, pricing.online_submethods])

  const selectPaymentOrder = (order: Order) => {
    setCheckoutOpen(true)
    setSelectedOrder(order)
    setSelectedOnlineSub(null)
    // F-MM-N23: a marketplace-settled order is paid by the platform, not at
    // the till, so open on the Online tender (unless a tender was already
    // chosen) and preselect the online provider named after the order type
    // ("Foodpanda") when the admin has configured one.
    const marketplace = order.tax_treatment === 'marketplace_settled'
    const chosen = order.checkout_payment_method
    const intent: 'cash' | 'card' | 'online' = marketplace ? 'online' : (chosen ?? 'cash')
    if (marketplace) {
      const typeLabel = (orderTypesRaw.find((r) => r.id === order.order_type)?.label ?? '').trim().toLowerCase()
      const match = (pricing.online_submethods ?? []).find(
        (m) => m.label.trim().toLowerCase() === typeLabel || m.slug === typeLabel.replace(/[^a-z0-9]+/g, '_'),
      )
      if (match) setSelectedOnlineSub(match.slug)
    }
    setPaymentCheckoutIntent(intent)
    startIntentSwitch(order.id, intent)
  }

  // Settle hand-off from the server floor: /admin/counter?tableId=… loads that
  // table's open dine-in order into the ticket and opens checkout, then clears
  // the param so a refresh doesn't re-trigger. The table auto-frees on payment.
  const settleHandledRef = useRef<string | null>(null)
  useEffect(() => {
    const tid = counterSearch.tableId
    // Param cleared (we navigated to search:{} after handling, or arrived with
    // no table): re-arm so a later Settle of the SAME table id isn't blocked.
    if (!tid) {
      settleHandledRef.current = null
      return
    }
    if (settleHandledRef.current === tid) return
    const table = sortedTables.find((t) => t.id === tid)
    if (!table) return // tables not loaded yet — effect re-runs once they are
    settleHandledRef.current = tid
    void (async () => {
      await handleOccupiedTable(table)
      const res = await apiClient.getActiveOrderForTable(tid)
      if (res.success && res.data) {
        selectPaymentOrder(res.data)
      } else {
        toastHelpers.error('Settle', 'No open order found for that table.')
      }
      navigate({ to: '/admin/counter', search: {}, replace: true })
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [counterSearch.tableId, sortedTables])

  // Reservations hand-off (F-RSV-N1): /admin/counter?openTableId=… loads the
  // tab the Reservations page just opened for a seated booking — the same
  // load as tapping an occupied table — then clears the param.
  const openTableHandledRef = useRef<string | null>(null)
  useEffect(() => {
    const tid = counterSearch.openTableId
    if (!tid) {
      openTableHandledRef.current = null
      return
    }
    if (openTableHandledRef.current === tid) return
    const table = sortedTables.find((t) => t.id === tid)
    if (!table) return
    openTableHandledRef.current = tid
    void (async () => {
      await handleOccupiedTable(table)
      navigate({ to: '/admin/counter', search: {}, replace: true })
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [counterSearch.openTableId, sortedTables])

  /**
   * Apply a fetched active order to counter rail state (continuing tab, guests,
   * dine-in table session). Does not open/close checkout — callers decide.
   */
  const applyActiveOrderToCounterState = useCallback(
    (od: Order) => {
      setExistingOrder(od)
      setContinuingOrderId(od.id)
      setExistingItemsExpanded(false)
      setCustomerName(od.customer_name ?? '')
      setCustomerEmail(od.customer_email ?? '')
      setCustomerPhone(od.customer_phone ?? '')
      setGuestBirthday(toGuestDateInputValue(od.guest_birthday))
      setDeliveryAddress(od.delivery_address ?? '')
      setDeliveryFeeOverride(od.delivery_fee_override ?? null)
      setServiceChargeOverride(serviceChargeOverrideFromOrder(od))
      if (od.order_type === 'dine_in') {
        const tbl =
          (od.table_id ? sortedTables.find((t) => t.id === od.table_id) : undefined) ?? od.table ?? null
        if (tbl) {
          setSelectedTable(tbl)
          const disp =
            od.user && (od.user.first_name || od.user.last_name)
              ? `${od.user.first_name} ${od.user.last_name}`.trim()
              : od.user?.username ?? '—'
          setDineInSession({
            guestCount: od.guest_count ?? 0,
            serverId: od.user_id ?? '',
            serverDisplayName: disp,
            customerName: od.customer_name ?? undefined,
            customerEmail: od.customer_email ?? undefined,
            customerPhone: od.customer_phone ?? undefined,
            guestBirthday: toGuestDateInputValue(od.guest_birthday) || undefined,
          })
        } else {
          setSelectedTable(null)
          setDineInSession(null)
          if (od.table_id) {
            toastHelpers.error(
              'Table',
              'Could not resolve this check’s table on the floor map. Use Pick a table.'
            )
          }
        }
      } else {
        setSelectedTable(null)
        setDineInSession(null)
      }
    },
    [sortedTables]
  )

  /** Load full ticket context then open payment — fixes dine-in menu when using Orders to close. */
  const ordersStripCheckoutMutation = useMutation({
    mutationFn: async ({
      order,
      orderTypeAtClick,
    }: {
      order: Order
      orderTypeAtClick: string
    }) => {
      // F-CTRL-N37: offline, work from the order the strip already holds.
      //
      // This fetch is a freshness refresh, not a load — `order` came from the
      // cached pending-orders query and carries everything the rail needs. With
      // the backend unreachable it threw, so a cashier could not open a tab
      // that was already on screen, and adding to an order opened before the
      // outage was impossible.
      //
      // The staleness is real but bounded: totals may lag a change made on
      // another terminal during the outage. Appending is additive and the
      // server recomputes totals on replay, so the worst case is a briefly
      // out-of-date figure on screen — against not being able to serve at all.
      if (!online) {
        return { od: order, orderTypeAtClick, fetched: false }
      }
      const r = await apiClient.getOrder(order.id)
      if (!r.success || !r.data) {
        throw new Error(r.message || 'Could not load order')
      }
      return { od: r.data, orderTypeAtClick, fetched: true }
    },
    onSuccess: ({ od, orderTypeAtClick, fetched }) => {
      if (normalizeOrderTypeId(od.order_type) !== normalizeOrderTypeId(orderTypeAtClick)) {
        toastHelpers.error(
          'Orders',
          `Switch to ${displayLabelForOrderType(od.order_type, orderTypesRaw)} mode to work this ticket.`
        )
        return
      }
      applyActiveOrderToCounterState(od)
      clearCart()
      setOrderNotes('')
      clearDraft(draftUserId)
      // F-FE-N176: `od` is the server copy read a moment ago from the same
      // endpoint the payment panel queries. Seed the panel with it so opening
      // checkout does not read GET /orders/:id a second time. Only a real fetch
      // seeds — offline, `od` is the strip's list row, not a full order.
      if (fetched) queryClient.setQueryData(['order', od.id, 'payment-panel'], od)
      selectPaymentOrder(od)
    },
    onError: (e: Error) => {
      toastHelpers.error('Orders', e.message || 'Could not load order')
    },
  })

  const handleGuestUpdated = useCallback((order: Order) => {
    setExistingOrder(order)
    setSelectedOrder((prev) => (prev?.id === order.id ? order : prev))
    setCustomerName(order.customer_name ?? '')
    setCustomerEmail(order.customer_email ?? '')
    setCustomerPhone(order.customer_phone ?? '')
    setGuestBirthday(toGuestDateInputValue(order.guest_birthday))
    setDeliveryAddress(order.delivery_address ?? '')
    setDeliveryFeeOverride(order.delivery_fee_override ?? null)
    setServiceChargeOverride(serviceChargeOverrideFromOrder(order))
    setDineInSession((prev) =>
      prev && orderType === 'dine_in'
        ? {
            ...prev,
            customerName: order.customer_name ?? undefined,
            customerEmail: order.customer_email ?? undefined,
            customerPhone: order.customer_phone ?? undefined,
            guestBirthday: toGuestDateInputValue(order.guest_birthday) || undefined,
          }
        : prev
    )
  }, [orderType])

  const handleServiceUpdated = useCallback((order: Order) => {
    setExistingOrder(order)
    setSelectedOrder((prev) => (prev?.id === order.id ? order : prev))
    const disp =
      order.user && (order.user.first_name || order.user.last_name)
        ? `${order.user.first_name} ${order.user.last_name}`.trim()
        : order.user?.username ?? ''
    setDineInSession((prev) =>
      prev && orderType === 'dine_in'
        ? {
            ...prev,
            guestCount: order.guest_count ?? 0,
            serverId: order.user_id ?? '',
            serverDisplayName: disp,
          }
        : prev
    )
  }, [orderType])

  const handleSelectHistoryOrder = useCallback(
    async (o: Order) => {
      if (o.status === 'completed' || o.status === 'cancelled') {
        const full = await apiClient.getOrder(o.id)
        setHistoryReadOnlyOrder(full.success && full.data ? full.data : o)
        return
      }
      const r = await apiClient.getOrder(o.id)
      if (!r.success || !r.data) {
        toastHelpers.error('Orders', r.message || 'Could not load order')
        return
      }
      const od = r.data
      if (normalizeOrderTypeId(od.order_type) !== normalizeOrderTypeId(orderType)) {
        toastHelpers.error(
          'Orders',
          `Switch to ${displayLabelForOrderType(od.order_type, orderTypesRaw)} mode to work this ticket.`
        )
        return
      }
      setCheckoutOpen(false)
      setSelectedOrder(null)
      applyActiveOrderToCounterState(od)
      // Intentionally no toast: the rail re-renders with the loaded ticket's
      // order number, items, and status — the UI itself is the affirmation.
    },
    [orderType, applyActiveOrderToCounterState, orderTypesRaw]
  )

  const clearLoadedOrder = useCallback(() => {
    setCheckoutOpen(false)
    setSelectedOrder(null)
    setContinuingOrderId(null)
    setOfflineTarget(null)
    setExistingOrder(null)
    setDineInSession(null)
    setSelectedTable(null)
    clearCart()
    setOrderNotes('')
    clearDraft(draftUserId)
    setCustomerName('')
    setCustomerEmail('')
    setCustomerPhone('')
    setGuestBirthday('')
    setCustomerSavedAddresses([])
    setDeliveryAddress('')
    setDeliveryFeeOverride(null)
    setServiceChargeOverride(null)
    setServiceChargeAuth(null)
    setExistingItemsExpanded(false)
    setPartialPaymentBanner(null)
  }, [])

  /**
   * Load a held order back into the rail so the next round can be added to it
   * (F-FE-N31).
   *
   * Tapping the loaded one again unloads it — the tray row is the only handle
   * on this state, so it has to be able to let go as well as take hold.
   */
  const handleSelectHeldOfflineOrder = useCallback(
    (o: OfflineOrder) => {
      // Unreconciled work belongs to a manager. Adding to something already
      // destined for a write-off would just make the write-off bigger.
      if (o.status !== 'pending') return
      if (offlineTarget?.opId === o.opId) {
        setOfflineTarget(null)
        return
      }
      // Same rule the Open Orders strip applies: the composer's order type
      // drives the slip header and the pricing preview, so working a ticket
      // from another mode would print the wrong thing.
      if (normalizeOrderTypeId(o.orderType) !== normalizeOrderTypeId(orderType)) {
        toastHelpers.error(
          'Offline order',
          `Switch to ${displayLabelForOrderType(o.orderType, orderTypesRaw)} mode to add to this ticket.`
        )
        return
      }
      // Drop whatever else is loaded first: leaving a server-side order in
      // continuingOrderId while composing for a held one would let the fire
      // land on the wrong ticket entirely.
      clearLoadedOrder()
      setOfflineTarget(o)
      const t = (tables ?? []).find((x) => x.id === o.tableId)
      if (t) setSelectedTable(t)
    },
    [offlineTarget?.opId, orderType, orderTypesRaw, clearLoadedOrder, tables]
  )

  /**
   * After a cart edit (items added / voided) the backend re-derives any
   * applied card promo from the new subtotal — and auto-removes it when the
   * bill drops below the promo's minimum. Refresh the promo state and, when
   * a promo the cashier could see disappears, say why with a toast instead
   * of leaving a silently vanished discount.
   */
  const refreshCardPromoAfterCartEdit = useCallback(
    async (orderId: string) => {
      // F-MKT-N2: an item-scoped promo code also re-derives on every cart edit
      // and is auto-removed when its last qualifying item goes — same pattern.
      void (async () => {
        const prevPromo = queryClient.getQueryData<{ data?: { applied?: boolean; code?: string; applies_to?: string } }>([
          'counter-applied-promo',
          orderId,
        ])
        if (!prevPromo?.data?.applied) {
          queryClient.invalidateQueries({ queryKey: ['counter-applied-promo', orderId] })
          return
        }
        try {
          const fresh = await apiClient.getAppliedPromo(orderId)
          queryClient.setQueryData(['counter-applied-promo', orderId], fresh)
          if (!fresh.data?.applied && prevPromo.data.applies_to) {
            toastHelpers.info(
              'Promo removed',
              `${prevPromo.data.code ?? 'The promo code'} no longer applies — no ${prevPromo.data.applies_to} left on the bill (or below its minimum).`
            )
          }
        } catch {
          queryClient.invalidateQueries({ queryKey: ['counter-applied-promo', orderId] })
        }
      })()
      queryClient.invalidateQueries({ queryKey: ['counter-eligible-card-promos', orderId] })
      const prev = queryClient.getQueryData<{ data?: { applied?: boolean; card_label?: string } }>([
        'counter-applied-card-promo',
        orderId,
      ])
      if (!prev?.data?.applied) {
        queryClient.invalidateQueries({ queryKey: ['counter-applied-card-promo', orderId] })
        return
      }
      try {
        const fresh = await apiClient.getAppliedCardPromo(orderId)
        queryClient.setQueryData(['counter-applied-card-promo', orderId], fresh)
        if (!fresh.data?.applied) {
          toastHelpers.info(
            'Card promo removed',
            `${prev.data.card_label ?? 'The bank card promo'} no longer applies — the bill dropped below its minimum.`
          )
        }
      } catch {
        queryClient.invalidateQueries({ queryKey: ['counter-applied-card-promo', orderId] })
      }
    },
    [queryClient]
  )

  /** Cashier confirmed: drop the card promo, then retry the tender switch. */
  const resolveCardPromoTenderConflict = useCallback(async () => {
    const conflict = cardPromoTenderConflict
    if (!conflict) return
    setCardPromoTenderConflict(null)
    try {
      await apiClient.removeCardPromoFromOrder(conflict.orderId)
    } catch {
      toastHelpers.error('Bank card promo', 'Could not remove the card promo — try again.')
      return
    }
    queryClient.invalidateQueries({ queryKey: ['counter-applied-card-promo', conflict.orderId] })
    queryClient.invalidateQueries({ queryKey: ['counter-eligible-card-promos', conflict.orderId] })
    queryClient.invalidateQueries({ queryKey: ['order', conflict.orderId, 'payment-panel'] })
    void startIntentSwitch(conflict.orderId, conflict.intent)
  }, [cardPromoTenderConflict, queryClient, startIntentSwitch])

  const handleVoidSuccess = useCallback(async (_itemId: string) => {
    const orderId = voidModal?.orderId
    setVoidModal(null)
    if (!orderId) return
    queryClient.invalidateQueries({ queryKey: ['orders'] })
    queryClient.invalidateQueries({ queryKey: ['counterPendingOrders'] })
    queryClient.invalidateQueries({ queryKey: ['order', orderId, 'payment-panel'] })
    void refreshCardPromoAfterCartEdit(orderId)
    try {
      const fresh = await apiClient.getOrder(orderId)
      if (fresh.success && fresh.data) {
        // F-CTRL-N30: voiding the LAST line makes the backend auto-cancel the
        // order (VoidItem's F-KDS-N2 branch). Before this check we kept the dead
        // order loaded as continuingOrderId, so the next punch appended a real
        // customer's items to a cancelled order and fired a KOT for it — the
        // food went out and the sale was never recorded (Chunkyard 20260729-021,
        // Rs 3,105). If the refetch comes back terminal, drop it and start clean.
        if (isTerminalOrderStatus(fresh.data.status)) {
          if (voidModal?.orderId === continuingOrderId || existingOrder?.id === orderId) {
            clearLoadedOrder()
          }
          toastHelpers.info(
            'Order closed',
            'Every item was voided, so this order was cancelled. Start a new order for the next customer.'
          )
          return
        }
        setExistingOrder((prev) => (prev?.id === orderId ? fresh.data! : prev))
        setSelectedOrder((prev) => (prev?.id === orderId ? fresh.data! : prev))
        queryClient.setQueryData(['order', orderId, 'payment-panel'], fresh.data)
      }
    } catch { /* best-effort refresh */ }
  }, [voidModal?.orderId, queryClient, continuingOrderId, existingOrder?.id, clearLoadedOrder])

  const openTableTransferDialog = () => {
    if (!existingOrder?.id) return
    setTableTransferOpen(true)
  }

  const handleCloseOrderFromHeader = useCallback(() => {
    if (cart.length > 0) {
      setDiscardCartConfirmOpen(true)
      return
    }
    clearLoadedOrder()
  }, [cart.length, clearLoadedOrder])

  /**
   * Something is loaded in the rail, so "New order" has work to do. Hoisted
   * (F-FE-N144) because two places render that control now — the ticket header
   * in the split layout and the tab bar below it — and they must appear and
   * disappear together.
   */
  const hasLoadedOrder = Boolean(
    existingOrder || selectedOrder || continuingOrderId || cart.length > 0
  )

  // --- Held carts: the "Hold" button (F-CTRL-N72 / F-FE-N158) ----------------
  const [heldSheetOpen, setHeldSheetOpen] = useState(false)
  const [heldBusyId, setHeldBusyId] = useState<string | null>(null)
  const [holdPending, setHoldPending] = useState(false)
  /** A held cart the cashier asked to resume while another cart is on screen. */
  const [resumeConflict, setResumeConflict] = useState<HeldCart | null>(null)

  // Only a NEW, un-placed cart can be held (v1). A placed order — server-side
  // or captured offline — already lives in Open Orders.
  const holdReason = holdBlockReason({
    cartLength: cart.length,
    hasPlacedOrder: Boolean(existingOrder || selectedOrder || continuingOrderId || offlineTarget),
    online,
  })
  const holdHint = holdBlockMessage(holdReason)

  /** Park the current cart on the server and clear the screen. */
  const holdCurrentCart = useCallback(async (): Promise<boolean> => {
    // A place-order request for this same cart is in flight: holding now could
    // leave the items both placed AND on hold.
    if (submitCartMutation.isPending || holdPending) return false
    if (holdReason !== null) {
      if (holdHint) toastHelpers.error('Cannot hold this order', holdHint)
      return false
    }
    const label = buildHeldCartLabel(
      displayLabelForOrderType(orderType, orderTypesRaw),
      customerName,
      new Date()
    )
    setHoldPending(true)
    try {
      await apiClient.createHeldCart({
        label,
        order_type: orderType,
        snapshot: draftFields,
        item_count: unsentCount,
        estimated_total: cartTotals.total,
        customer_name: customerName.trim() || undefined,
      })
      setDiscardCartConfirmOpen(false)
      clearLoadedOrder()
      queryClient.invalidateQueries({ queryKey: ['counterHeldCarts'] })
      toastHelpers.success('Order on hold', `${label} — open "On hold" to resume it.`)
      return true
    } catch (err) {
      toastHelpers.error(
        'Could not hold the order',
        heldCartErrorMessage(err instanceof ApiClientError ? err.code : undefined, 'Try again in a moment.')
      )
      return false
    } finally {
      setHoldPending(false)
    }
  }, [
    submitCartMutation.isPending,
    holdPending,
    holdReason,
    holdHint,
    orderType,
    orderTypesRaw,
    customerName,
    draftFields,
    unsentCount,
    cartTotals.total,
    clearLoadedOrder,
    queryClient,
  ])

  /**
   * Claim a held cart for this till and load it. `current` says what to do with
   * a cart already on screen: nothing is ever dropped without the cashier
   * choosing it. The claim is atomic on the server — if another till got there
   * first we say so and leave this screen untouched.
   */
  const resumeHeldCart = useCallback(
    async (held: HeldCart, current: 'none' | 'hold' | 'discard') => {
      setResumeConflict(null)
      if (current === 'hold') {
        const ok = await holdCurrentCart()
        if (!ok) return
      }
      const parkedCurrent = current === 'hold'
      setHeldBusyId(held.id)
      try {
        const res = await apiClient.resumeHeldCart(held.id)
        const snap = res.data?.snapshot ?? held.snapshot
        clearLoadedOrder()
        const fullProducts = queryClient.getQueryData<Product[]>(['products', 'all']) ?? products ?? []
        const { items, dropped } = restoreDraftItems(snap.cart ?? [], fullProducts)
        const { tableDropped } = applySnapshotFields(snap, items)
        setHeldSheetOpen(false)
        setMobileWorkspaceTab('ticket')
        if (items.length === 0) {
          toastHelpers.error(
            'Nothing to resume',
            `None of the items in ${held.label} are on the menu any more.`
          )
        } else {
          const notes = [
            dropped > 0 ? `${dropped} item${dropped > 1 ? 's' : ''} removed (no longer available).` : '',
            tableDropped ? 'Its table is now taken — pick a table again.' : '',
            'Prices are from the current menu.',
          ].filter(Boolean)
          toastHelpers.info(`Resumed ${held.label}`, notes.join(' '))
        }
      } catch (err) {
        const reason = heldCartErrorMessage(
          err instanceof ApiClientError ? err.code : undefined,
          'Try again in a moment.'
        )
        // The on-screen cart was already parked a moment ago — say where it
        // went, or an empty screen reads as a lost order.
        toastHelpers.error(
          'Could not resume',
          parkedCurrent ? `${reason} Your previous order is safe under "On hold".` : reason
        )
      } finally {
        setHeldBusyId(null)
        queryClient.invalidateQueries({ queryKey: ['counterHeldCarts'] })
      }
    },
    [holdCurrentCart, clearLoadedOrder, queryClient, products, applySnapshotFields]
  )

  const requestResumeHeldCart = useCallback(
    (held: HeldCart) => {
      // Never swap the rail out from under an order that is still being placed.
      if (submitCartMutation.isPending) {
        toastHelpers.info('One moment', 'Wait for the current order to finish sending, then resume.')
        return
      }
      if (cart.length > 0) {
        setResumeConflict(held)
        return
      }
      void resumeHeldCart(held, 'none')
    },
    [cart.length, resumeHeldCart, submitCartMutation.isPending]
  )

  const discardHeldCart = useCallback(
    async (held: HeldCart) => {
      setHeldBusyId(held.id)
      try {
        await apiClient.discardHeldCart(held.id)
        toastHelpers.info('Held order discarded', held.label)
      } catch (err) {
        toastHelpers.error(
          'Could not discard',
          heldCartErrorMessage(err instanceof ApiClientError ? err.code : undefined, 'Try again in a moment.')
        )
      } finally {
        setHeldBusyId(null)
        queryClient.invalidateQueries({ queryKey: ['counterHeldCarts'] })
      }
    },
    [queryClient]
  )

  const renameHeldCart = useCallback(
    async (held: HeldCart, label: string) => {
      try {
        await apiClient.renameHeldCart(held.id, label)
      } catch (err) {
        toastHelpers.error(
          'Could not rename',
          heldCartErrorMessage(err instanceof ApiClientError ? err.code : undefined, 'Try again in a moment.')
        )
      } finally {
        queryClient.invalidateQueries({ queryKey: ['counterHeldCarts'] })
      }
    },
    [queryClient]
  )
  // --- end held carts ---------------------------------------------------------

  // Extras shared by the payment panel and the split dialog so both compute
  // totals against the same service/delivery configuration.
  const payPricingExtras = useMemo(() => {
    if (!payOrder) return null
    const p = getOrderTypePricing(payOrder.order_type, orderTypesRaw)
    // Honor a per-order delivery fee override so the payment total matches the
    // server (which recomputes with the override) and the receipt.
    const payDeliveryFee =
      payOrder.order_type === 'delivery' && payOrder.delivery_fee_override != null
        ? payOrder.delivery_fee_override
        : p.deliveryFee
    return {
      includeServiceCharge: p.includeServiceCharge,
      deliveryFee: payDeliveryFee,
      itemDiscountsTotal: payOrder.item_discounts_total ?? 0,
      // F-MM-N23: server-stamped at creation — never re-derived from the
      // current order-type settings, which may have changed since.
      taxTreatment:
        payOrder.tax_treatment === 'marketplace_settled'
          ? ('marketplace_settled' as const)
          : ('standard' as const),
      // Likewise the per-order service charge override (migration 081) —
      // the server recomputed with it, the panel must render the same figure
      // and blank the "(X%)" label for a fixed amount.
      serviceChargeRateOverride: payOrder.service_charge_override_rate ?? null,
      serviceChargeAmountOverride: payOrder.service_charge_override_amount ?? null,
    }
  }, [payOrder, orderTypesRaw])

  const paymentTotals = useMemo(() => {
    if (!payOrder || !payPricingExtras) return null
    return computeCartTotals(
      payOrder.subtotal,
      payOrder.discount_amount,
      paymentCheckoutIntent,
      pricing,
      payPricingExtras
    )
  }, [payOrder, payPricingExtras, paymentCheckoutIntent, pricing])

  const onPaymentIntent = (intent: 'cash' | 'card' | 'online') => {
    if (!selectedOrder) return
    if (marketplaceLock && intent !== 'online') return
    if (intent !== 'cash') {
      setCashModalOpen(false)
      setCashReceived('')
    }
    if (intent !== 'online') {
      setSelectedOnlineSub(null)
    }
    setPaymentCheckoutIntent(intent)
    startIntentSwitch(selectedOrder.id, intent)
  }

  const remaining =
    payOrder &&
    (payOrder.total_amount -
      (payOrder.payments?.filter((p) => p.status === 'completed').reduce((s, p) => s + p.amount, 0) ?? 0))

  const payAmount = remaining ?? 0

  const hasPartialPayment =
    payOrder != null && (payOrder.payments ?? []).some((p) => p.status === 'completed')

  const handlePrintProvisionalBill = useCallback(async () => {
    if (!selectedOrder || !payOrder) return
    if (payAmount <= 0 || hasPartialPayment) return
    setProvisionalPrintBusy(true)
    try {
      const fresh = await apiClient.getOrder(selectedOrder.id)
      if (!fresh.success || !fresh.data) {
        toastHelpers.error('Print initial bill', fresh.message || 'Could not load order')
        return
      }
      const order = fresh.data
      if ((order.payments ?? []).some((p) => p.status === 'completed')) {
        toastHelpers.error(
          'Print initial bill',
          'Not available after a partial payment was recorded.',
        )
        return
      }
      const settingsRes = await queryClient.fetchQuery({
        queryKey: ['settings', 'all'],
        queryFn: () => apiClient.getAllSettings(),
      })
      if (!settingsRes.success || !settingsRes.data) {
        toastHelpers.error('Print initial bill', 'Could not load receipt settings')
        return
      }
      const cfg = parseReceiptSettings(settingsRes.data as Record<string, unknown>)
      // F-MM-N28: priced by the server's checkout recompute — never from the
      // local pricing query, whose empty state falls back to 10% service.
      const prebill = await loadServerPrebill(order.id)
      if (!prebill.ok) {
        toastHelpers.error('Print initial bill', `${prebill.reason} — bill not printed.`)
        return
      }
      const { printed } = printUnpaidCustomerBill(
        order,
        cfg,
        { serverTotals: { cash: prebill.cash, card: prebill.card } },
        {
          cashierName: getCashierNameFromStorage(),
          serverName: getServerNameFromOrder(order),
          orderTypeLabel: displayLabelForOrderType(order.order_type, orderTypesRaw),
          formatAmount: formatCurrency,
          paidAt: new Date(),
          cardPromoLabel: order.id === activeRailOrderId ? activeDiscountLabel : null,
        },
      )
      if (!printed) {
        toastHelpers.error('Print initial bill', 'Popup blocked — allow popups for this site')
      }
    } finally {
      setProvisionalPrintBusy(false)
    }
  }, [selectedOrder, payOrder, payAmount, hasPartialPayment, queryClient, orderTypesRaw, formatCurrency, activeRailOrderId, activeDiscountLabel])

  const ticketLifecycle = useMemo(
    () =>
      getTicketLifecycle({
        order: existingOrder ?? payOrder,
        cartCount: cart.length,
        checkoutOpen,
        payableRemaining: payAmount,
      }),
    [existingOrder, payOrder, cart.length, checkoutOpen, payAmount]
  )

  // F-FE-N154: the SAME predicate the rail's "On order" section lists through.
  // These were two hand-written filters that disagreed about 'draft' — this one
  // billed unfired lines while the rail hid them and then rendered nothing, so
  // an order whose KOT fire never landed showed a payable total with no line
  // list, no void and no remove. See
  // audit/PARCHED_UNFIRED_ORDER_NO_VOID_BANNER_2026-09-10.md.
  //
  // Unsent lines stay billable on purpose. 'draft' means our record of the fire
  // request didn't land, NOT that the food was never made — so refusing the
  // charge would block a real sale on an inference about the kitchen. Staff get
  // to see, fire or remove them instead.
  const billableItems: OrderItem[] = useMemo(() => {
    const items = payOrder?.items ?? []
    return items.filter(isBillableLine)
  }, [payOrder?.items])

  // F-MM-N7: the amount is passed in rather than read from `payAmount` here.
  // These run AFTER an awaited tender switch, and the `payAmount` captured in
  // that closure is the pre-switch figure — the exact staleness we're closing.
  const runCardPayment = (amount: number) => {
    if (!selectedOrder || amount <= 0) return
    processPaymentMutation.mutate({
      orderId: selectedOrder.id,
      paymentData: {
        payment_method: 'card',
        amount,
      },
    })
  }

  // F-MM-N24 — settle part (or all) of the bill from a gift card balance.
  const { enabled: giftCardTenderEnabled } = useGiftCardTenderEnabled()
  const [giftCardDialogOpen, setGiftCardDialogOpen] = useState(false)

  /**
   * The gift leg rides the ordinary payment mutation, so it inherits the whole
   * settled path: order refetch, receipt print, celebration, partial-payment
   * banner. The card debit happens inside the same server transaction as the
   * payment row, so a failure here has taken nothing off the card.
   *
   * rate_intent is what keeps the tax honest. A gift card carries no rate of
   * its own, so the ORDER is billed at the intent the cashier picked for the
   * remainder — exactly the mechanism the split flow uses (handlePaySplitGuest
   * below). The one exception is a card that covers the whole bill: there is
   * no cash/card/online leg to take a rate from, and the owner's decision is
   * that such a bill bills at the standard cash rate, never the reduced card
   * one.
   *
   * When that re-rating would actually move the total, we switch the intent
   * and make the cashier look at the new figure rather than silently charging
   * a total they were never shown — the backend would reject the stale amount
   * anyway (F-MM-N7 order_reprice_conflict), and a rejection at the till reads
   * as a broken system.
   */
  const runGiftCardPayment = (code: string, amount: number) => {
    const order = selectedOrderRef.current ?? selectedOrder
    if (!order || amount <= 0) return
    const intent = paymentCheckoutIntentRef.current

    // Re-derive what the bill still owes from the freshest copy of the order
    // rather than the `payAmount` captured when this closure was built. A
    // tender switch rewrites the total, and sending a figure the operator was
    // not shown is exactly what F-MM-N7 exists to stop — onPrimaryPay does the
    // same thing for the same reason.
    const panel = payOrderRef.current?.id === order.id ? payOrderRef.current : null
    const stillOwing = remainingOn(panel ?? order)
    if (stillOwing <= 0) return

    // Paisa-precision compare, matching the backend's moneyPaisa discipline:
    // a float amount that lands one ULP under the total must still count as
    // covering the bill, or the cashier gets re-rated for a rounding artefact.
    const coversWholeBill = Math.round(amount * 100) >= Math.round(stillOwing * 100)
    if (coversWholeBill && intent !== 'cash') {
      setGiftCardDialogOpen(false)
      onPaymentIntent('cash')
      toastHelpers.info(
        'Re-rated to the standard rate',
        'A gift card paying the whole bill is billed at the cash rate. Check the new total, then apply the card again.',
      )
      return
    }
    setGiftCardDialogOpen(false)
    processPaymentMutation.mutate({
      orderId: order.id,
      paymentData: {
        payment_method: 'gift_card',
        amount,
        gift_card_code: code,
        rate_intent: coversWholeBill ? 'cash' : intent,
      },
    })
  }

  // F-MM-N30 — staff meal allowances and customer tabs.
  const houseAccounts = useHouseAccountsEnabled()
  const [chargeAccountOpen, setChargeAccountOpen] = useState(false)
  const [tabPaymentOpen, setTabPaymentOpen] = useState(false)

  const handleTabSettled = useCallback(
    (result: TabSettlementResult, _account: HouseAccount, extras: TabSettlementExtras) => {
      void printTabPaymentSlipForResult(queryClient, result, extras, formatCurrency)
        .then((printed) => {
          if (!printed) toastHelpers.error('Slip not printed', 'The payment is saved. Check the printer and pop-up settings.')
        })
        .catch(() => toastHelpers.error('Slip not printed', 'The payment is saved. Check the printer.'))
    },
    [queryClient, formatCurrency],
  )

  /**
   * The server reprices the bill at the account's terms (a staff meal drops
   * tax and service; a tab bills at its own rate), records the charge and
   * completes the order in one transaction, so the till sends no amount.
   * paymentData carries the billed rate intent only so the receipt picks the
   * matching rate label.
   */
  const runChargeToAccount = (account: HouseAccount, managerPin: string | undefined) => {
    const order = selectedOrderRef.current ?? selectedOrder
    if (!order) return
    if (!backendReachable) {
      toastHelpers.error('Charge to account', 'The till is offline. An account charge must reach the server — take another tender or wait for the connection.')
      return
    }
    const rateIntent = account.kind === 'tab' ? account.tab_rate_intent : 'cash'
    processPaymentMutation.mutate(
      {
        orderId: order.id,
        paymentData: { payment_method: rateIntent, amount: remainingOn(order) },
        houseAccount: { accountId: account.id, managerPin },
      },
      { onSuccess: () => setChargeAccountOpen(false) },
    )
  }

  // Active online sub-methods (migration 050). Empty when the feature is off,
  // in which case the plain online flow runs unchanged. Memoised so the `?? []`
  // fallback does not hand every consumer below a fresh array each render.
  const onlineSubMethods = useMemo(() => pricing.online_submethods ?? [], [pricing.online_submethods])

  // F-CTRL-N69: providers reserved to a marketplace-settled order type. The
  // Foodpanda tile belongs to Foodpanda tickets and must not be offered on a
  // Dine In / Takeaway check — that sale is taxed and filed, so tagging it to
  // the platform would inflate the marketplace day-close count and break the
  // settlement reconciliation. Empty when the tenant flag is off, so a plain
  // online provider that merely happens to be called "Foodpanda" is untouched.
  const reservedProviderSlugs = useMemo(
    () => marketplaceProviderSlugs(orderTypesRaw, marketplaceSettledEnabled),
    [orderTypesRaw, marketplaceSettledEnabled],
  )
  // Checkout rail: full list on a marketplace order (the panel shows the
  // locked chip, not a picker), reserved tiles hidden on every other order.
  const payOnlineSubMethods = useMemo(
    () => onlineProvidersForOrder(onlineSubMethods, reservedProviderSlugs, marketplaceLock != null),
    [onlineSubMethods, reservedProviderSlugs, marketplaceLock],
  )
  // Held offline ticket — same rule, keyed off its own lock.
  const offlineOnlineSubMethods = useMemo(
    () => onlineProvidersForOrder(onlineSubMethods, reservedProviderSlugs, offlineMarketplaceLock != null),
    [onlineSubMethods, reservedProviderSlugs, offlineMarketplaceLock],
  )
  // Split bill is disabled outright on a marketplace order, so a split guest
  // is always an ordinary taxed share: never offer the reserved providers.
  const splitOnlineSubMethods = useMemo(
    () => onlineProvidersForOrder(onlineSubMethods, reservedProviderSlugs, false),
    [onlineSubMethods, reservedProviderSlugs],
  )

  // Drop a provider choice that is no longer on offer — the cashier switched
  // the rail from a Foodpanda ticket to a Dine In one with the tile already
  // picked, and a stale reserved slug would only earn a server rejection.
  useEffect(() => {
    if (!selectedOnlineSub || marketplaceLock) return
    if (!payOnlineSubMethods.some((m) => m.slug === selectedOnlineSub)) setSelectedOnlineSub(null)
  }, [selectedOnlineSub, payOnlineSubMethods, marketplaceLock])

  const runOnlinePayment = (amount: number, subMethod?: string) => {
    if (!selectedOrder || amount <= 0) return
    // Require a provider when sub-methods are configured (migration 050).
    if (payOnlineSubMethods.length > 0 && !subMethod && !marketplaceLock) return
    processPaymentMutation.mutate({
      orderId: selectedOrder.id,
      paymentData: {
        payment_method: 'online',
        amount,
        ...(subMethod ? { payment_sub_method: subMethod } : {}),
      },
    })
  }

  const runCashPayment = () => {
    if (!selectedOrder || payAmount <= 0) return
    const received = parseFloat(cashReceived || '0')
    if (received < payAmount) return
    processPaymentMutation.mutate({
      orderId: selectedOrder.id,
      paymentData: { payment_method: 'cash', amount: payAmount },
      // F-FE-N63: carry the note the customer handed over through to the
      // receipt. Passed here because `cashReceived` is cleared two lines below,
      // long before onSuccess prints.
      cashTendered: received,
    })
    setCashModalOpen(false)
    setCashReceived('')
  }

  /**
   * Remaining balance on a specific order object, rather than on whatever
   * `payOrder` happened to be at render time. Used after an awaited tender
   * switch, where the freshest order comes back from the mutation itself.
   */
  const remainingOn = (o: Order): number =>
    o.total_amount -
    ((o.payments ?? []).filter((p) => p.status === 'completed').reduce((s, p) => s + p.amount, 0) ?? 0)

  /** True while a pay tap is waiting on an in-flight tender switch (F-MM-N7). */
  const [payArming, setPayArming] = useState(false)

  /**
   * Primary pay action. Waits — briefly, and never indefinitely — for an
   * in-flight tender switch so the amount sent matches the tender being paid.
   *
   * Deliberately NOT `disabled={checkoutIntentMutation.isPending}`: with a 30s
   * axios timeout, a hung request would park the pay button for half a minute.
   * A till that cannot take money is a worse failure than the stale amount this
   * avoids, and the backend refuses a stale amount safely (F-MM-N7
   * order_reprice_conflict). So the cap expires and we proceed regardless.
   */
  const onPrimaryPay = async () => {
    if (payArming || processPaymentMutation.isPending) return
    const order = selectedOrder
    if (!order) return

    setPayArming(true)
    let fresh: Order | null = null
    try {
      fresh = await settleIntent()
    } finally {
      setPayArming(false)
    }

    // The operator moved to a different ticket while we waited — abandon rather
    // than pay the wrong order.
    if (!selectedOrderRef.current || selectedOrderRef.current.id !== order.id) return

    // Freshest known copy of this ticket, in order of authority: the order the
    // tender switch just returned, then the payment-panel query, then local
    // state. The panel copy outranks `selectedOrder` because it is what the
    // Charge button's figure is drawn from and it is refreshed on paths that
    // never touch `selectedOrder` — sending a number the operator was not shown
    // is how a stale copy turns into a rejected payment (F-MM-N10).
    const panel = payOrderRef.current?.id === order.id ? payOrderRef.current : null
    const local = selectedOrderRef.current?.id === order.id ? selectedOrderRef.current : null
    const effective =
      (fresh && fresh.id === order.id ? fresh : null) ?? panel ?? local ?? order
    const amount = remainingOn(effective)
    if (amount <= 0) return

    const intent = paymentCheckoutIntentRef.current
    // F-MM-N23: re-assert the lock where money moves, not only on the buttons.
    if (marketplaceLock && intent !== 'online') {
      toastHelpers.error('Marketplace order', `${marketplaceLock.label} orders are recorded as Online › ${marketplaceLock.label} only.`)
      onPaymentIntent('online')
      return
    }
    if (intent === 'cash') {
      // Start the tender pad empty (amount received = 0) so the clerk enters
      // what the customer actually handed over, rather than backspacing a
      // pre-filled "exact" amount.
      setCashModalOpen(true)
      setCashReceived('')
    } else if (intent === 'card') {
      runCardPayment(amount)
    } else if (payOnlineSubMethods.length > 0) {
      runOnlinePayment(amount, selectedOnlineSub ?? marketplaceLock?.providerSlug ?? undefined)
    } else {
      runOnlinePayment(amount)
    }
  }

  /** 100% comps / Rs 0 remaining: close via ProcessPayment self-heal (amount 0). */
  const runCompleteZeroBalance = useCallback(() => {
    if (!selectedOrder || payAmount > 0) return
    if (selectedOrder.status === 'completed' || selectedOrder.status === 'cancelled') return
    processPaymentMutation.mutate({
      orderId: selectedOrder.id,
      paymentData: {
        payment_method: paymentCheckoutIntent,
        amount: 0,
      },
    })
  }, [selectedOrder, payAmount, paymentCheckoutIntent, processPaymentMutation])

  // ----- Split bill (F-FE-N19) -----

  const splitState = payOrder?.split_state ?? null
  const splitPaidCount = (splitState?.guests ?? []).filter((g) => g.paid_payment_id).length
  // A pre-existing (non-split) partial payment would leave one tender
  // unlabeled and the shares ambiguous — cleanest to require a fresh check.
  const splitDisabledReason =
    !splitState && hasPartialPayment
      ? 'Unavailable after a partial payment — void payments to split'
      : billableItems.length === 0 && !splitState
        ? 'Nothing to split yet'
        : undefined

  /** Propagate the refreshed Order returned by the split endpoints. */
  const handleSplitOrderUpdated = useCallback(
    (order: Order) => {
      setSelectedOrder(order)
      setExistingOrder((prev) => (prev?.id === order.id ? order : prev))
      queryClient.setQueryData(['order', order.id, 'payment-panel'], order)
    },
    [queryClient]
  )

  const handlePaySplitGuest = useCallback(
    (args: PaySplitGuestArgs) => {
      if (!selectedOrder || !payOrder?.split_state) return
      // The rate the whole order is billed at: mixed splits use the standard
      // cash rate (never under-collect), card-only keeps the reduced rate.
      const rateIntent: 'cash' | 'card' =
        payOrder.split_state.tender_scope === 'card_only' ? 'card' : 'cash'
      processPaymentMutation.mutate({
        orderId: selectedOrder.id,
        paymentData: {
          payment_method: args.tender,
          amount: args.amount,
          ...(args.subMethod ? { payment_sub_method: args.subMethod } : {}),
          rate_intent: rateIntent,
          split_guest_no: args.guestNo,
        },
        splitPrint: {
          share: args.share,
          guestNo: args.guestNo,
          guestCount: payOrder.split_state.guest_count,
        },
        // F-FE-N63: this guest's own note, for the change block on their slip.
        cashTendered: args.cashTendered,
      })
    },
    [selectedOrder, payOrder, processPaymentMutation]
  )

  const handleReprintSplitGuest = useCallback(
    (share: GuestShare) => {
      if (!payOrder?.split_state) return
      void printSplitGuestSlip(
        payOrder.id,
        payOrder,
        share,
        share.guestNo,
        payOrder.split_state.guest_count,
        share.tender ?? 'cash'
      )
    },
    [payOrder, printSplitGuestSlip]
  )

  const clampCheckoutRail = useCallback((w: number, containerWidth: number) => {
    const max = Math.max(
      COUNTER_RAIL_MIN,
      // The menu pane must keep room for ≥2 product tiles (2×190px minmax +
      // gap + pane padding + drag separator ≈ 444px) — otherwise tablets
      // (768–1024) render a single sparse product column beside a half-screen
      // rail. The old floor of MIN+160 forced exactly that at 820px.
      Math.min(820, Math.floor(containerWidth * 0.56), containerWidth - 444)
    )
    return Math.min(max, Math.max(COUNTER_RAIL_MIN, Math.round(w)))
  }, [])

  const beginCheckoutRailDrag = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!isSplitLayout) return
      if (e.pointerType === 'mouse' && e.button !== 0) return
      e.preventDefault()
      const root = counterSplitRef.current
      if (!root) return
      const target = e.currentTarget
      try {
        target.setPointerCapture(e.pointerId)
      } catch {
        /* ignore */
      }
      const cw = root.getBoundingClientRect().width
      // Same bound as clampCheckoutRail: never let the rail eat the menu's
      // two-column minimum (~444px) on narrow split layouts.
      const maxW = Math.max(
        COUNTER_RAIL_MIN,
        Math.min(820, Math.floor(cw * 0.56), cw - 444)
      )
      railDragRef.current = { startX: e.clientX, startW: checkoutRailPx, maxW }
      let lastShift = e.shiftKey
      const onMove = (ev: PointerEvent) => {
        const d = railDragRef.current
        if (!d) return
        lastShift = ev.shiftKey
        const delta = d.startX - ev.clientX
        const nw = d.startW + delta
        setCheckoutRailPx(Math.min(d.maxW, Math.max(COUNTER_RAIL_MIN, Math.round(nw))))
      }
      const SNAP_POINTS = [400, 480, 560, 640]
      const onUp = () => {
        const useSnap = !lastShift
        railDragRef.current = null
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
        document.body.style.removeProperty('-webkit-user-select')
        try {
          target.releasePointerCapture(e.pointerId)
        } catch {
          /* ignore */
        }
        target.removeEventListener('pointermove', onMove)
        target.removeEventListener('pointerup', onUp)
        target.removeEventListener('pointercancel', onUp)
        setCheckoutRailPx((w) => {
          const cw2 = counterSplitRef.current?.getBoundingClientRect().width ?? 1200
          let next = clampCheckoutRail(w, cw2)
          if (useSnap) {
            const snap = SNAP_POINTS.reduce((acc, p) =>
              Math.abs(p - next) < Math.abs(acc - next) ? p : acc
            , next)
            if (Math.abs(snap - next) <= 22) next = clampCheckoutRail(snap, cw2)
          }
          try {
            localStorage.setItem(COUNTER_CHECKOUT_RAIL_KEY, String(next))
          } catch {
            /* ignore */
          }
          return next
        })
      }
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
      document.body.style.setProperty('-webkit-user-select', 'none')
      target.addEventListener('pointermove', onMove)
      target.addEventListener('pointerup', onUp)
      target.addEventListener('pointercancel', onUp)
    },
    [checkoutRailPx, clampCheckoutRail, isSplitLayout]
  )

  const resetCheckoutRailWidth = useCallback(() => {
    setCheckoutRailPx(COUNTER_RAIL_DEFAULT)
    try {
      localStorage.setItem(COUNTER_CHECKOUT_RAIL_KEY, String(COUNTER_RAIL_DEFAULT))
    } catch {
      /* ignore */
    }
  }, [])

  useEffect(() => {
    const onResize = () => {
      setCheckoutRailPx((w) => {
        const cw = counterSplitRef.current?.getBoundingClientRect().width ?? 1200
        return clampCheckoutRail(w, cw)
      })
    }
    // Clamp on mount too: a width persisted from a wide session could otherwise
    // crush the menu to ~0 when the split first appears on a narrow (≥768px) screen.
    onResize()
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [clampCheckoutRail])

  useEffect(() => {
    try {
      localStorage.removeItem(LEGACY_CATEGORY_PANE_KEY)
    } catch {
      /* ignore */
    }
  }, [])

  // Category bar scroll/overflow tracking lives in MenuCategoryBar (layout="row").

  useEffect(() => {
    if (checkoutOpen && !isSplitLayout) setMobileWorkspaceTab('ticket')
  }, [checkoutOpen, isSplitLayout])

  useEffect(() => {
    if (!checkoutOpen) return
    const t = window.setTimeout(() => {
      const firstTender = document.querySelector<HTMLButtonElement>(
        '[aria-labelledby="tender-input-heading"] button[aria-pressed]'
      )
      firstTender?.focus()
    }, 40)
    return () => window.clearTimeout(t)
  }, [checkoutOpen])

  const handleCounterEscape = useCallback(() => {
    if (historyReadOnlyOrder) {
      setHistoryReadOnlyOrder(null)
      return
    }
    if (checkoutCelebration != null) {
      dismissCheckoutCelebration()
      return
    }
    if (cashModalOpen) {
      setCashModalOpen(false)
      setCashReceived('')
      return
    }
    if (checkoutOpen) {
      setCheckoutOpen(false)
      setSelectedOrder(null)
    }
  }, [historyReadOnlyOrder, checkoutCelebration, dismissCheckoutCelebration, cashModalOpen, checkoutOpen])

  useCounterHotkeys({
    // F-FE-N103: F1 goes through handleSubmitCart like every other caller, and
    // no longer straight to mutate(). It used to bypass BOTH guards at once —
    // the disabled button (a window keydown listener does not care that a
    // button is disabled) and the offline routing above, so pressing F1 during
    // an outage sent a doomed online request instead of capturing offline.
    onSend: handleSubmitCart,
    onPay: () => {
      // Not gated on continuingOrderId: a fired order stays loaded for payment
      // even when it is no longer collecting items (F-FE-N112).
      if (existingOrder && !checkoutOpen && orderPayableRemaining(existingOrder) > 0) {
        selectPaymentOrder(existingOrder)
      }
    },
    onFocusDiscount: () => {
      if (!checkoutOpen) return
      const findInput = () =>
        document.querySelector<HTMLInputElement>(
          'input[placeholder^="e.g. 10"], input[placeholder="0.00"]'
        )
      const el = findInput()
      if (el) {
        el.focus()
        return
      }
      // Panel collapsed — expand the Discount accordion, then focus on next paint.
      const toggle = document.querySelector<HTMLButtonElement>(
        'button[aria-controls="counter-discount-panel"]'
      )
      toggle?.click()
      requestAnimationFrame(() => findInput()?.focus())
    },
    onEscape: handleCounterEscape,
    onFocusSearch: () => searchInputRef.current?.focus(),
    onOpenTables: () => {
      if (orderType !== 'dine_in') return
      setTablesPickerOpen(true)
    },
    onFocusCart: () => {
      railScrollRef.current?.scrollTo({ top: railScrollRef.current.scrollHeight, behavior: 'smooth' })
    },
  }, {
    // Suspend every shortcut while the options sheet owns the screen: Esc
    // would otherwise close the sheet and the checkout behind it, and "T"
    // would open the Tables picker underneath.
    enabled: configurator == null,
  })

  return (
    <div className="flex h-full min-h-0 w-full min-w-0 flex-1 flex-row bg-background">
      {reservedGuard.dialog}
      <TablesPicker
        open={tablesPickerOpen}
        onOpenChange={setTablesPickerOpen}
        mode="select"
        tables={sortedTables}
        holds={reservedGuard.holds}
        currentTableId={selectedTable?.id ?? null}
        onSelectFreeTable={handleFreeTableGuarded}
        onSelectOccupiedTable={(table) => {
          void handleOccupiedTable(table)
        }}
      />

      <TablesPicker
        open={tableTransferOpen}
        onOpenChange={setTableTransferOpen}
        mode="transfer"
        tables={sortedTables}
        holds={reservedGuard.holds}
        currentTableId={selectedTable?.id ?? null}
        onSelectFreeTable={handleFreeTableGuarded}
        onSelectOccupiedTable={(table) => {
          void handleOccupiedTable(table)
        }}
        onConfirmTransfer={(table) => {
          if (!existingOrder?.id) return
          const orderId = existingOrder.id
          reservedGuard.guard(table, () => reassignTableMutation.mutate({ orderId, tableId: table.id }))
        }}
        isTransferring={reassignTableMutation.isPending}
      />

      <PraInvoicePromptModal
        open={praPromptContext != null}
        onSkip={handlePraSkip}
        onPrint={handlePraPrint}
        busy={praPrinting}
      />

      {/* blockedReason is set only when a sale was actually blocked; opening
          this proactively from the banner shows no "your sale failed" alert. */}
      <OpenDayDialog
        open={startDayBlocker.open}
        onOpenChange={(open) => setStartDayBlocker({ open, message: startDayBlocker.message })}
        blockedReason={startDayBlocker.message}
        // F-CTRL-N32: counter tier confirms the standing float rather than
        // typing one. Reuses userCanRunDayClose (admin|manager) as the tier
        // test; the backend enforces the same rule independently, so this is
        // affordance only, not the control.
        confirmOnly={!userCanRunDayClose}
      />

      <AdjustmentAuthDialog pending={pendingAdjustment} onClose={() => setPendingAdjustment(null)} />

      <PreviousDayBlockerModal
        open={previousDayBlocker.open}
        onOpenChange={(open) => setPreviousDayBlocker({ open, message: previousDayBlocker.message })}
        message={previousDayBlocker.message}
        canRunDayClose={userCanRunDayClose}
      />

      <ManagerHandoffModal open={handoffOpen} onClose={() => setHandoffOpen(false)} />
      <KotPrintModal open={kotPrintOpen} onOpenChange={setKotPrintOpen} kots={lastFireKots} />

      {voidModal && (
        <PinEntryModal
          orderId={voidModal.orderId}
          itemId={voidModal.itemId}
          itemName={voidModal.itemName}
          quantity={voidModal.qty}
          unitPrice={voidModal.unitPrice}
          itemStatus={existingOrder?.items?.find((i) => i.id === voidModal.itemId)?.status}
          wholeOrder={
            voidModal.wholeOrder && existingOrder?.id === voidModal.orderId
              ? {
                  label: `Order #${existingOrder.order_number}`,
                  lines: (existingOrder.items ?? [])
                    .filter((i) => isBillableLine(i) && !isUnsentLine(i) && VOIDABLE_STATUSES.has(i.status))
                    .map((i) => ({
                      id: i.id,
                      name: i.product?.name ?? 'Item',
                      quantity: i.quantity,
                      amount: i.total_price,
                    })),
                }
              : undefined
          }
          onSuccess={handleVoidSuccess}
          onClose={() => setVoidModal(null)}
        />
      )}

      <OfflinePaymentPrompt
        marketplaceLock={offlineMarketplaceLock}
        open={offlinePayTarget != null}
        ticketRef={offlinePayTarget?.ticketRef || offlinePayTarget?.orderNumber || ''}
        price={(tender) => {
          if (!offlinePayTarget) return { total: 0, unknownLines: 0 }
          const p = priceHeldOfflineOrder(offlinePayTarget, tender)
          return { total: p.total, unknownLines: p.unknownLines }
        }}
        onlineSubMethods={offlineOnlineSubMethods}
        busy={offlinePayBusy}
        onClose={() => setOfflinePayTarget(null)}
        onTake={(p) => void takeOfflinePayment(p)}
      />
      {/* Options sheet (F-MOD-N7). Mounted beside the other dialogs so it
          layers above both the product grid and the checkout rail. */}
      {configurator && (
        <ModifierConfigurator
          open
          productName={configurator.product.name}
          basePrice={basePriceOf(configurator.product)}
          groups={configuratorGroups}
          optionById={configuratorOptionById}
          includes={configuratorIncludes}
          priceIndex={menuModifiers.priceIndexFor(configurator.product.id)}
          formatCurrency={formatCurrency}
          accent={categoryColor(
            configurator.product.category_id ? categoryById.get(configurator.product.category_id) : undefined,
            configurator.product.name,
          )}
          initial={configurator.initial}
          onCancel={() => setConfigurator(null)}
          onConfirm={commitConfigurator}
        />
      )}

      <Dialog open={discardCartConfirmOpen} onOpenChange={setDiscardCartConfirmOpen}>
        <DialogContent className="max-w-md sm:rounded-lg">
          <DialogHeader>
            <DialogTitle>Hold or discard staged items?</DialogTitle>
            <DialogDescription>
              {holdReason === null
                ? `Hold keeps the ${cart.length} item${cart.length === 1 ? '' : 's'} so you can serve the next guest and come back to this order. Discard throws them away.`
                : `Closing this order will discard the ${cart.length} item${cart.length === 1 ? '' : 's'} you have added to the cart.`}
            </DialogDescription>
          </DialogHeader>
          {holdHint && (
            <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
              {holdHint}
            </p>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="destructive"
              onClick={() => {
                setDiscardCartConfirmOpen(false)
                clearLoadedOrder()
              }}
            >
              Discard items
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDiscardCartConfirmOpen(false)}
            >
              Keep editing
            </Button>
            <Button
              type="button"
              autoFocus={holdReason === null}
              data-testid="counter-hold-from-new-order"
              className="bg-amber-500 text-white hover:bg-amber-600 dark:bg-amber-600 dark:hover:bg-amber-500"
              disabled={holdReason !== null || holdPending || submitCartMutation.isPending}
              onClick={() => void holdCurrentCart()}
            >
              <PauseCircle className="mr-2 h-4 w-4" aria-hidden />
              {holdPending ? 'Holding…' : 'Hold order'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <HeldOrdersSheet
        open={heldSheetOpen}
        onOpenChange={setHeldSheetOpen}
        carts={heldCarts}
        loading={heldCartsLoading}
        online={online}
        formatCurrency={formatCurrency}
        orderTypeLabel={(id) => displayLabelForOrderType(id, orderTypesRaw)}
        orderTypeColor={(id) => colorForOrderTypeId(orderTypesRaw, id)}
        productName={(id) => {
          const p = (queryClient.getQueryData<Product[]>(['products', 'all']) ?? products ?? []).find(
            (x) => x.id === id
          )
          if (!p) return undefined
          // Say it on the card, before the cashier resumes and the line is dropped.
          return p.is_available === false ? `${p.name} (unavailable)` : p.name
        }}
        busyId={heldBusyId}
        onResume={requestResumeHeldCart}
        onDiscard={discardHeldCart}
        onRename={renameHeldCart}
      />

      <Dialog open={resumeConflict != null} onOpenChange={(open) => !open && setResumeConflict(null)}>
        <DialogContent className="max-w-md sm:rounded-lg">
          <DialogHeader>
            <DialogTitle className="pr-6">Resume {resumeConflict?.label}?</DialogTitle>
            <DialogDescription>
              There {cart.length === 1 ? 'is 1 item' : `are ${cart.length} items`} on screen already. What should
              happen to {cart.length === 1 ? 'it' : 'them'}?
            </DialogDescription>
          </DialogHeader>
          {holdHint && (
            <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
              {holdHint}
            </p>
          )}
          {/* Three choices stack full-width: side by side they overflow max-w-md. */}
          <div className="flex flex-col gap-2">
            <Button
              type="button"
              autoFocus={holdReason === null}
              data-testid="counter-resume-hold-current"
              className="w-full bg-amber-500 text-white hover:bg-amber-600 dark:bg-amber-600 dark:hover:bg-amber-500"
              disabled={holdReason !== null || holdPending}
              onClick={() => resumeConflict && void resumeHeldCart(resumeConflict, 'hold')}
            >
              <PauseCircle className="mr-2 h-4 w-4" aria-hidden />
              Hold current order &amp; resume
            </Button>
            <Button
              type="button"
              variant="destructive"
              className="w-full"
              onClick={() => resumeConflict && void resumeHeldCart(resumeConflict, 'discard')}
            >
              Discard current items &amp; resume
            </Button>
            <Button type="button" variant="outline" className="w-full" onClick={() => setResumeConflict(null)}>
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={cardPromoTenderConflict != null}
        onOpenChange={(open) => !open && setCardPromoTenderConflict(null)}
      >
        <DialogContent className="max-w-md sm:rounded-lg">
          <DialogHeader>
            <DialogTitle>Remove the bank card promo?</DialogTitle>
            <DialogDescription>
              This order has a bank card discount that only applies when the bill is paid by card.
              Switching to {cardPromoTenderConflict?.intent === 'online' ? 'Online' : 'Cash'} will
              remove the discount and the total will go back up.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setCardPromoTenderConflict(null)}
            >
              Keep card promo
            </Button>
            <Button type="button" variant="destructive" onClick={resolveCardPromoTenderConflict}>
              Remove promo & switch
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {checkoutCelebration != null && (
        <CounterCheckoutSuccessOverlay
          mode={checkoutCelebration}
          onDismiss={dismissCheckoutCelebration}
        />
      )}

      <Dialog open={historyReadOnlyOrder != null} onOpenChange={(open) => !open && setHistoryReadOnlyOrder(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {historyReadOnlyOrder ? `Order #${historyReadOnlyOrder.order_number}` : 'Order'}
            </DialogTitle>
            <DialogDescription className="space-y-1 text-left">
              <span className="block capitalize text-muted-foreground">
                {historyReadOnlyOrder?.status?.replace('_', ' ')} ·{' '}
                {historyReadOnlyOrder && formatCurrency(historyReadOnlyOrder.total_amount)}
              </span>
              {historyReadOnlyOrder?.created_at && (
                <span className="block text-xs text-muted-foreground">
                  {format(new Date(historyReadOnlyOrder.created_at), "MMM d, yyyy 'at' h:mm a")}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            This ticket is closed. Use order history above to find open checks, or open{' '}
            <Link
              to="/admin/reports"
              className="font-medium text-primary underline-offset-2 hover:underline"
            >
              Reports
            </Link>{' '}
            for deeper analysis.
          </p>
          {historyReadOnlyOrder?.status === 'completed' ? (
            <div className="mt-2">
              <Button
                type="button"
                variant="outline"
                className="w-full border-[rgb(255,241,178)] bg-[rgb(255,241,178)] hover:bg-[rgb(255,241,178)]"
                disabled={historyGuestPrintBusy || praPrinting}
                onClick={async () => {
                  if (!historyReadOnlyOrder) return
                  setHistoryGuestPrintBusy(true)
                  try {
                    const r = await printGuestReceiptForOrderId(
                      apiClient,
                      queryClient,
                      historyReadOnlyOrder.id,
                      {
                        formatAmount: formatCurrency,
                        getCashierName: getCashierNameFromStorage,
                        isDuplicateCopy: true,
                      },
                    )
                    if (!r.ok) {
                      toastHelpers.error('Guest receipt', r.reason)
                    } else {
                      toastHelpers.success('Guest receipt', 'Sent to print dialog.')
                    }
                  } finally {
                    setHistoryGuestPrintBusy(false)
                  }
                }}
              >
                {historyGuestPrintBusy ? 'Printing…' : 'Print guest receipt'}
              </Button>
            </div>
          ) : null}
          {historyReadOnlyOrder?.pra_invoice_printed ? (
            <div className="mt-2 flex items-center justify-between gap-3 rounded-md border border-border bg-muted/30 p-3">
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium">PRA tax invoice printed</p>
                {historyReadOnlyOrder.pra_invoice_number ? (
                  <p className="text-[11px] text-muted-foreground truncate">
                    Invoice #{historyReadOnlyOrder.pra_invoice_number}
                  </p>
                ) : null}
              </div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={praPrinting}
                onClick={async () => {
                  if (!historyReadOnlyOrder) return
                  setPraPrinting(true)
                  try {
                    const settingsRes = await queryClient.fetchQuery({
                      queryKey: ['settings', 'all'],
                      queryFn: () => apiClient.getAllSettings(),
                    })
                    if (!settingsRes.success || !settingsRes.data) return
                    const cfg = parseReceiptSettings(
                      settingsRes.data as Record<string, unknown>,
                    )
                    const paidAt = historyReadOnlyOrder.completed_at
                      ? new Date(historyReadOnlyOrder.completed_at)
                      : new Date()
                    const method = checkoutStoredIntentToReceiptPaymentMethod(
                      historyReadOnlyOrder.checkout_payment_method,
                    )
                    const { invoiceNumber, printed } = await printPraTaxInvoice(
                      historyReadOnlyOrder,
                      cfg,
                      {
                        cashierName: getCashierNameFromStorage(),
                        paymentMethod: method,
                        paidAt,
                        orderTypeLabel: displayLabelForOrderType(
                          historyReadOnlyOrder.order_type,
                          orderTypesRaw,
                        ),
                        formatAmount: formatCurrency,
                      },
                    )
                    if (printed) {
                      try {
                        await apiClient.markPraInvoicePrinted(
                          historyReadOnlyOrder.id,
                          invoiceNumber || undefined,
                        )
                        queryClient.invalidateQueries({ queryKey: ['orders'] })
                        queryClient.invalidateQueries({
                          queryKey: ['order', historyReadOnlyOrder.id],
                        })
                      } catch {
                        /* audit log is best-effort */
                      }
                    }
                  } finally {
                    setPraPrinting(false)
                  }
                }}
              >
                {praPrinting ? 'Printing…' : 'Reprint PRA'}
              </Button>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col self-stretch">
        <div
          ref={counterSplitRef}
          className="flex min-h-0 min-w-0 flex-1 flex-col self-stretch lg:flex-row"
        >
        {/* Tabbed layout only (F-FE-N145: below 1024px, so phones and portrait
            tablets). The row holds the tablist plus the "New order" control,
            which sits OUTSIDE role="tablist" — a non-tab child there is an a11y
            violation — but inside the always-visible chrome row, so a server
            browsing the Menu tab can release the order without switching to the
            Ticket tab first (F-FE-N144). */}
        <div className="flex shrink-0 items-center gap-2 border-b border-border bg-muted/40 px-3 py-2 lg:hidden">
        <div
          role="tablist"
          aria-label="Counter workspace"
          className="flex min-w-0 flex-1 gap-2"
        >
          <Button
            type="button"
            role="tab"
            aria-selected={mobileWorkspaceTab === 'menu'}
            variant={mobileWorkspaceTab === 'menu' ? 'default' : 'outline'}
            size="sm"
            className="min-h-11 flex-1 touch-manipulation"
            onClick={() => setMobileWorkspaceTab('menu')}
          >
            Menu
          </Button>
          <Button
            type="button"
            role="tab"
            aria-selected={mobileWorkspaceTab === 'ticket'}
            variant={mobileWorkspaceTab === 'ticket' ? 'default' : 'outline'}
            size="sm"
            className="relative min-h-11 flex-1 touch-manipulation"
            onClick={() => setMobileWorkspaceTab('ticket')}
          >
            Ticket
            {unsentCount > 0 && (
              <Badge
                key={unsentCount}
                className="counter-tab-badge-pop ml-2 border-transparent bg-orange-500 px-1.5 py-0 text-xs font-semibold tabular-nums text-white"
                aria-label={`${unsentCount} item${unsentCount === 1 ? '' : 's'} in draft`}
              >
                {unsentCount}
              </Badge>
            )}
          </Button>
        </div>
          {heldCarts.length > 0 && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              data-testid="counter-on-hold-tabbed"
              className="min-h-11 shrink-0 touch-manipulation gap-1.5 bg-amber-100 px-3 text-amber-900 hover:bg-amber-200 dark:bg-amber-950/50 dark:text-amber-100"
              onClick={() => setHeldSheetOpen(true)}
              aria-label={`On hold: ${heldCarts.length}`}
            >
              <PauseCircle className="h-4 w-4" aria-hidden />
              {heldCarts.length}
            </Button>
          )}
          {hasLoadedOrder && (
            <Button
              type="button"
              // Not `outline` — that is the unselected tab's look, and this must
              // not read as a third tab.
              variant="secondary"
              size="sm"
              data-testid="counter-new-order-tabbed"
              className="min-h-11 shrink-0 touch-manipulation gap-1.5 border-l border-border/60 px-3"
              onClick={handleCloseOrderFromHeader}
              title="Close the current order and start a new one"
            >
              <X className="h-4 w-4" aria-hidden />
              New order
            </Button>
          )}
        </div>

        <div className="flex min-h-0 min-w-0 flex-1 flex-col lg:min-h-0 lg:flex-row">
        <main
          aria-label="Counter workspace"
          className={cn(
            'flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden border-r border-border bg-background',
            mobileWorkspaceTab === 'ticket' && 'hidden lg:flex'
          )}
        >
        <StartTheDayBanner
          currentDay={currentDay}
          isLoading={dayOpsCurrentPending}
          onStartClick={() => setStartDayBlocker({ open: true })}
        />
        <DailyClosingBanner
          currentDay={currentDay}
          closeoutHour={closeoutHour}
          userRole={userRole}
          onCloseClick={() => navigate({ to: '/admin/day-close' })}
          onGetManagerClick={() => setHandoffOpen(true)}
        />
        {/*
          F-FE-N112: on the tablet the ticket rail is hidden while the cashier is
          on the menu tab, and the Ticket badge counts staged items — zero right
          after a fire. So the one state that decides where the next tap lands was
          invisible on the only screen they were looking at. A table tab still
          collects deliberately; this says so, and offers the way out in one tap.
        */}
        {isBhooklyNativeServerApp() && appendingToLoadedOrder && (
          // Hidden from 1024px up, for the same reason the bar exists at all
          // (F-FE-N150). It says what the ticket rail already says, and it is
          // only needed where that rail is off screen — the tabbed layout
          // above. On a landscape tablet the rail is visible and naming the
          // same order, and a second amber bar reads as a warning about
          // something else.
          <div className="flex shrink-0 items-center justify-between gap-3 border-b border-amber-300 bg-amber-50 px-4 py-2 lg:hidden dark:border-amber-800 dark:bg-amber-950/40">
            <span className="min-w-0 truncate text-sm font-medium text-amber-900 dark:text-amber-100">
              Adding to{' '}
              {existingOrder?.order_number ? `#${existingOrder.order_number}` : 'an open order'}
              {existingOrder?.table?.table_number ? ` · Table ${existingOrder.table.table_number}` : ''}
            </span>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-8 shrink-0 border-amber-400 bg-background text-amber-900 dark:text-amber-100"
              onClick={clearLoadedOrder}
            >
              New order
            </Button>
          </div>
        )}
        <div className="shrink-0 border-b border-border bg-card/95 px-6 pt-4 pb-3 backdrop-blur-sm">
          <div className="mb-2 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h1 className="text-3xl font-bold tracking-tight">Point of Sale (POS)</h1>
              <p className="mt-1 text-muted-foreground">
                Choose order type, then add items from the menu
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
            {houseAccounts.tabsVisible && backendReachable && (
              <Button
                type="button"
                variant="outline"
                data-testid="counter-receive-tab-payment"
                className="h-11 shrink-0 gap-2 rounded-full px-4 font-semibold"
                onClick={() => setTabPaymentOpen(true)}
                title="A guest paying down their tab"
              >
                <BookUser className="h-5 w-5" aria-hidden />
                Tab payment
              </Button>
            )}
            {heldCarts.length > 0 && (
              <Button
                type="button"
                variant="outline"
                data-testid="counter-on-hold"
                // Below lg the tab bar carries its own On hold button (visible
                // on both tabs), so this one would be a duplicate there.
                className="hidden h-11 shrink-0 gap-2 lg:inline-flex rounded-full border-amber-300 bg-amber-50 px-4 font-semibold text-amber-900 hover:bg-amber-100 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100 dark:hover:bg-amber-900/50"
                onClick={() => setHeldSheetOpen(true)}
                title="Orders parked while a guest decides"
              >
                <PauseCircle className="h-5 w-5" aria-hidden />
                On hold
                <span className="rounded-full bg-amber-500 px-2 py-0.5 text-xs tabular-nums text-white">
                  {heldCarts.length}
                </span>
              </Button>
            )}
            </div>
          </div>
          {houseAccounts.tabsVisible && (
            <ReceiveTabPaymentDialog
              open={tabPaymentOpen}
              onOpenChange={setTabPaymentOpen}
              onSettled={handleTabSettled}
            />
          )}

          <div className="mb-3">
            {enabledOrderTypeIds.size === 0 ? (
              <div className="rounded-2xl border border-dashed border-border bg-muted/20 p-4 text-sm text-muted-foreground">
                No order types are currently enabled. Ask an admin to enable at least one
                order type under Settings &rarr; Order Types.
              </div>
            ) : (
              <CounterOrderTypeToggle
                value={orderType}
                enabledIds={enabledOrderTypeIds}
                options={counterOrderToggleOptions}
                onChange={(next) => {
                  setOrderType(next)
                  setDineInSession(null)
                  setSelectedTable(null)
                  setCheckoutOpen(false)
                  setSelectedOrder(null)
                  // Guest fields deliberately survive the switch (F-FE-N29) —
                  // e.g. customer picked first, then the cashier flips to
                  // delivery and the saved address auto-fills. Address + fee
                  // are per-type, so those still reset.
                  setDeliveryAddress('')
                  setDeliveryFeeOverride(null)
                  setServiceChargeOverride(null)
                  setServiceChargeAuth(null)
                }}
              />
            )}
          </div>

          {pendingOrdersLoading && ordersToCloseStrip.length === 0 && (
            <div
              style={railSurfaceStyle}
              className={cn(
                'flex items-center gap-2.5 rounded-lg px-2.5 py-1.5',
                !railAccent && 'bg-amber-500/[0.06]'
              )}
            >
              <div
                style={railAccent ? { color: railAccent } : undefined}
                className={cn(
                  'flex shrink-0 items-center gap-1.5 text-[10.7px] font-semibold uppercase tracking-wide',
                  !railAccent && 'text-amber-700 dark:text-amber-400'
                )}
              >
                <span
                  aria-hidden
                  className={cn('h-[6.3px] w-[6.3px] rounded-full', !railAccent && 'bg-amber-500')}
                  style={railAccent ? { background: railAccent } : undefined}
                />
                <span className="max-w-[70px] leading-tight">Open orders</span>
              </div>
              <div className="flex min-w-0 flex-1 gap-2 overflow-x-auto py-0.5">
                <Skeleton className="h-11 w-24 shrink-0 rounded-md" />
                <Skeleton className="h-11 w-24 shrink-0 rounded-md" />
                <Skeleton className="h-11 w-24 shrink-0 rounded-md" />
              </div>
            </div>
          )}
          {ordersToCloseStrip.length > 0 && (
            <div
              style={railSurfaceStyle}
              className={cn(
                'flex items-center gap-2.5 rounded-lg px-2.5 py-1.5',
                !railAccent && 'bg-amber-500/[0.06]'
              )}
            >
              <div
                style={railAccent ? { color: railAccent } : undefined}
                className={cn(
                  'flex shrink-0 items-center gap-1.5 text-[10.7px] font-semibold uppercase tracking-wide',
                  !railAccent && 'text-amber-700 dark:text-amber-400'
                )}
              >
                <span
                  aria-hidden
                  className={cn('h-[6.3px] w-[6.3px] rounded-full', !railAccent && 'bg-amber-500')}
                  style={railAccent ? { background: railAccent } : undefined}
                />
                <span className="max-w-[70px] leading-tight">Open orders</span>
              </div>
              <div className="flex min-w-0 flex-1 gap-2 overflow-x-auto py-0.5">
                {ordersToCloseStrip.map((order) => {
                  const isActive = activeRailOrderId === order.id
                  // The order's order-type color (yellow/orange for takeaway, green for
                  // dine-in, purple for delivery, etc.) carries the same identity it has
                  // everywhere else: `${hex}22` (~13% alpha) washes the hover, and the
                  // selected tile keeps that exact wash plus a solid-tint border/bar.
                  const tint = colorForOrderTypeId(orderTypesRaw, order.order_type)
                  // Table for dine-in, guest's first name otherwise; '' when
                  // neither is known, in which case the tile just shows money.
                  const tagLabel = openOrderTagLabel(order, tables ?? [])
                  const tileStyle = tint
                    ? ({
                        '--tile-hover': `${tint}22`,
                        '--tile-accent': tint,
                        ...(isActive
                          ? {
                              background: `${tint}22`,
                              borderColor: tint,
                              '--tw-ring-color': `${tint}66`,
                            }
                          : {}),
                      } as React.CSSProperties)
                    : undefined
                  return (
                    <Button
                      key={order.id}
                      type="button"
                      size="sm"
                      variant="outline"
                      style={tileStyle}
                      className={cn(
                        'relative shrink-0 h-auto min-h-11 flex-col items-stretch py-2 px-3 text-foreground hover:text-foreground',
                        // Soft order-type tint on hover; muted fallback if no color configured.
                        !isActive && (tint ? 'hover:bg-[var(--tile-hover)]' : 'hover:bg-muted/40'),
                        isActive && tint &&
                          'ring-1 before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-1 before:rounded-r before:bg-[var(--tile-accent)]',
                        isActive && !tint &&
                          'border-primary bg-primary/10 ring-1 ring-primary/40 hover:bg-primary/15 before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-1 before:rounded-r before:bg-primary'
                      )}
                      disabled={
                        checkoutIntentMutation.isPending || ordersStripCheckoutMutation.isPending
                      }
                      onClick={() =>
                        ordersStripCheckoutMutation.mutate({ order, orderTypeAtClick: orderType })
                      }
                    >
                      {/* Identity first: the tile leads with the table or the
                          guest, because that is what an operator scans for.
                          The order number drops to a muted second line — it is
                          for reference, not for finding the ticket. */}
                      <span className="flex w-full items-center gap-2">
                        {tagLabel && (
                          <span
                            style={tint ? { background: tint } : undefined}
                            className={cn(
                              'shrink-0 rounded px-1.5 py-0.5 text-[11px] font-bold leading-none',
                              // Order-type colour when the tenant configured
                              // one; otherwise a semantic token, never a raw
                              // palette weight — that breaks dark (F-FE-N55).
                              tint ? 'text-white' : 'bg-primary text-primary-foreground'
                            )}
                          >
                            {tagLabel}
                          </span>
                        )}
                        <span className="ml-auto font-semibold tabular-nums">
                          {formatCurrency(order.total_amount)}
                        </span>
                      </span>
                      <span className="mt-0.5 text-[10px] leading-none opacity-55 tabular-nums">
                        #{order.order_number}
                      </span>
                    </Button>
                  )
                })}
              </div>
            </div>
          )}

        </div>

        <div className="flex min-h-0 flex-1 flex-col">
          {/* Search + categories — one open section above the product grid
              (the header's bottom border draws the line after the rail). */}
          <div className="shrink-0 px-3 pt-3 sm:px-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                ref={searchInputRef}
                placeholder="Search products by name"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-11"
              />
            </div>
          </div>
          <MenuCategoryBar
            layout="row"
            categories={categories as Category[]}
            selectedCategory={selectedCategory}
            onSelect={setSelectedCategory}
            totalCount={totalCategoryItems}
          />

          {/* Items pane (bottom) — the product grid, filtered by selection. */}
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-3 sm:p-4">
          {searchScopeNotice && (
            <div className="mb-3 flex items-center justify-between gap-2 rounded-md border border-dashed bg-muted/30 px-3 py-2 text-xs">
              <span className="text-muted-foreground">
                Searching <strong className="text-foreground">all products</strong> — clear the search
                to return to {searchScopeNotice}.
              </span>
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                className="shrink-0 font-medium underline underline-offset-2"
              >
                Clear
              </button>
            </div>
          )}
          {gridState === 'no-match' ? (
            <div className="flex h-full min-h-[12rem] flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border bg-muted/20 p-8 text-center">
              <p className="text-sm font-medium">No products match &ldquo;{searchTerm}&rdquo;.</p>
              <p className="text-xs text-muted-foreground">
                Try a different keyword or clear the search box.
              </p>
            </div>
          ) : gridState === 'load-failed' ? (
            <div className="flex h-full min-h-[12rem] flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border bg-muted/20 p-8 text-center">
              <p className="text-sm font-medium">The menu could not be loaded.</p>
              <p className="text-xs text-muted-foreground">
                Check the internet connection, then try again.
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={() => {
                  void refetchProducts()
                }}
              >
                Retry
              </Button>
            </div>
          ) : gridState === 'loading' ? (
            <div className="grid gap-2 [grid-template-columns:repeat(auto-fill,minmax(140px,1fr))] sm:[grid-template-columns:repeat(auto-fill,minmax(190px,1fr))]" aria-hidden>
              {Array.from({ length: 10 }).map((_, idx) => (
                <div
                  key={`skeleton-${idx}`}
                  className="h-[108px] rounded-lg border border-border bg-muted/40 animate-pulse"
                />
              ))}
            </div>
          ) : (
          <div className="grid gap-2 [grid-template-columns:repeat(auto-fill,minmax(140px,1fr))] sm:[grid-template-columns:repeat(auto-fill,minmax(190px,1fr))]">
            {filteredProducts.map((product) => {
                const cat = product.category_id ? categoryById.get(product.category_id) : undefined
                const bg = categoryColor(cat, product.name)
                // F-DEAL-N1/N2: outside its hours, or a deal with a sold-out item.
                const outOfHours = product.is_available && !rulesIndex.available(product)
                const dealSoldOut = product.is_available && rulesIndex.comboSoldOut(product.id)
                const disabled = !product.is_available || outOfHours || dealSoldOut
                const tilePrice = rulesIndex.basePrice(product, orderType, false, isMarketplaceOrderType)
                const showTapFlash = lastTappedProductId === product.id
                return (
                  <button
                    key={product.id}
                    type="button"
                    disabled={disabled}
                    onClick={() => handleTileTap(product)}
                    className={cn(
                      'relative flex min-h-[108px] flex-col rounded-lg border border-border border-l-4 p-2 text-left shadow-sm select-none sm:p-2.5',
                      'transition-[transform,box-shadow,filter,outline] duration-100 [@media(prefers-reduced-motion:reduce)]:transition-none',
                      'active:scale-[0.96] active:brightness-[0.97] active:ring-2 active:ring-primary/40 active:ring-offset-2 [@media(prefers-reduced-motion:reduce)]:active:scale-100',
                      'disabled:pointer-events-none disabled:opacity-40'
                    )}
                    style={{
                      borderLeftColor: bg,
                      ...categorySurfaceStyle(bg),
                      ...(showTapFlash
                        ? {
                            outline: `2px solid ${bg}`,
                            outlineOffset: '3px',
                            zIndex: 1,
                          }
                        : {}),
                    }}
                  >
                    {tapPop.productId === product.id && (
                      <span
                        key={tapPop.nonce}
                        aria-hidden
                        className="counter-product-plus-one pointer-events-none absolute -top-2 right-2 z-20 rounded-full bg-orange-500 px-2 py-0.5 text-xs font-bold text-white shadow-md"
                      >
                        +1
                      </span>
                    )}
                    {/*
                     * Layout: image stacks above text on narrow (<sm) so the
                     * full card width is available for the product name and
                     * price; switches to image-left / text-right on sm+ where
                     * cards are wider. At narrow the 80px image was eating
                     * almost the entire card width, leaving the name + price
                     * truncated to ~32px.
                     */}
                    <div className="flex min-h-0 flex-1 flex-col gap-1.5 sm:flex-row sm:gap-2">
                      <div className="flex shrink-0 justify-center sm:block">
                        {product.image_url ? (
                          <img
                            src={product.image_url}
                            alt={product.name}
                            className="h-14 w-14 rounded-md object-cover bg-muted sm:h-20 sm:w-20"
                          />
                        ) : (
                          <ProductImagePlaceholder
                            className="h-14 w-14 sm:h-20 sm:w-20"
                            iconClassName="h-7 w-7 sm:h-10 sm:w-10"
                          />
                        )}
                      </div>
                      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden sm:h-20">
                        {/*
                         * Stay at text-sm at all breakpoints — text-base + 3-line
                         * clamp overflows the 80px text column by ~8px and clips
                         * the bottom of the third line (visible on "Grilled
                         * Chicken Breast", "Fresh Orange Juice"). text-sm
                         * leading-snug fits 3 lines (~58px) + price (~19px) inside
                         * the 80px column cleanly.
                         */}
                        <span className="min-h-0 flex-1 overflow-hidden text-left text-sm font-semibold leading-snug text-foreground line-clamp-2 sm:line-clamp-3">
                          {product.name}
                        </span>
                        {outOfHours || dealSoldOut ? (
                          <span className="mt-auto shrink-0 self-end text-right text-xs font-medium text-muted-foreground">
                            {dealSoldOut ? 'Sold out' : `Available ${rulesIndex.hoursLabel(product)}`}
                          </span>
                        ) : (
                          <span className="mt-auto flex shrink-0 flex-wrap items-baseline justify-end gap-x-1.5 self-end text-xs font-normal tabular-nums sm:text-sm">
                            {rulesIndex.isCombo(product.id) && (
                              <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">Deal</span>
                            )}
                            {tilePrice.rule_id && tilePrice.price !== product.price ? (
                              <>
                                <span className="text-[11px] text-muted-foreground line-through">{formatCurrency(product.price)}</span>
                                <span className="text-primary" title={tilePrice.label}>{formatCurrency(tilePrice.price)}</span>
                              </>
                            ) : (
                              <span className="text-primary">{formatCurrency(tilePrice.price)}</span>
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                )
              })}
          </div>
          )}
          </div>
        </div>
        </main>

        <div
          role="separator"
          aria-orientation="vertical"
          aria-valuenow={checkoutRailPx}
          aria-valuemin={COUNTER_RAIL_MIN}
          aria-valuemax={820}
          title="Drag to resize checkout panel. Double-click to reset width."
          style={{ touchAction: 'none' }}
          className="group relative z-10 hidden w-px shrink-0 cursor-col-resize touch-none select-none flex-col items-center bg-transparent lg:flex"
          onPointerDown={beginCheckoutRailDrag}
          onDoubleClick={(e) => {
            e.preventDefault()
            resetCheckoutRailWidth()
          }}
        >
          <span className="sr-only">Resize checkout column</span>
          {/* The visible seam is the rail's own border-l; no separate track
              line here (keeps it a single thin hairline). The pill is the
              grab target. */}
          {/* Persistent grab handle ("blob") — the focal drag target. It
              receives pointer events so a grab anywhere on it bubbles to the
              separator's onPointerDown (currentTarget stays the separator, so
              pointer capture + snap + persistence are unchanged). */}
          <div
            aria-hidden
            className="absolute left-1/2 top-1/2 flex h-12 w-6 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-card shadow-md ring-1 ring-black/[0.02] transition-all group-hover:border-primary/60 group-hover:shadow-lg group-active:scale-95 group-active:border-primary"
          >
            <GripVertical className="pointer-events-none h-4 w-4 text-muted-foreground transition-colors group-hover:text-primary" />
          </div>
        </div>

        <aside
          aria-label="Ticket"
          style={isSplitLayout ? { width: checkoutRailPx, maxWidth: '100%' } : undefined}
          className={cn(
            'grid h-full min-h-0 min-w-0 shrink-0 grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden border-l border-border bg-gradient-to-b from-card via-card to-muted/[0.35]',
            !isSplitLayout && 'w-full max-w-none flex-1 min-h-0',
            mobileWorkspaceTab === 'menu' && 'hidden lg:grid'
          )}
        >
          <div className="shrink-0 bg-card/90 backdrop-blur-sm">
            <TicketHeader
              orderType={orderType}
              orderTypesConfig={orderTypesRaw}
              selectedTable={selectedTable}
              existingOrder={existingOrder}
              dineInSession={dineInSession}
              customerName={customerName || existingOrder?.customer_name || ''}
              deliveryAddress={deliveryAddress}
              checkoutOpen={checkoutOpen}
              lifecycle={ticketLifecycle}
              firedConfirmation={firedConfirmation}
              onChangeTable={existingOrder?.id ? openTableTransferDialog : undefined}
              onCloseOrder={handleCloseOrderFromHeader}
              hasLoadedOrder={hasLoadedOrder}
            />
            <CounterRailToolbar
              orderType={orderType}
              existingOrder={existingOrder}
              continuingOrderId={continuingOrderId}
              selectedTable={selectedTable}
              customerName={customerName}
              setCustomerName={setCustomerName}
              customerEmail={customerEmail}
              setCustomerEmail={setCustomerEmail}
              customerPhone={customerPhone}
              setCustomerPhone={setCustomerPhone}
              guestBirthday={guestBirthday}
              setGuestBirthday={setGuestBirthday}
              deliveryAddress={deliveryAddress}
              setDeliveryAddress={setDeliveryAddress}
              savedAddresses={customerSavedAddresses}
              setSavedAddresses={setCustomerSavedAddresses}
              onGuestUpdated={handleGuestUpdated}
              onServiceUpdated={handleServiceUpdated}
              onSelectHistoryOrder={handleSelectHistoryOrder}
              formatCurrency={formatCurrency}
              onPickTable={() => setTablesPickerOpen(true)}
              onChangeTable={openTableTransferDialog}
              onCancelSession={(orderId) => cancelOpenTabMutation.mutate(orderId)}
              cancelSessionPending={cancelOpenTabMutation.isPending}
              onOpenSplit={
                checkoutOpen && payOrder && payAmount > 0 && !marketplaceLock
                  ? () => setSplitDialogOpen(true)
                  : undefined
              }
              splitActive={Boolean(splitState)}
              splitDisabledReason={splitDisabledReason}
              onReprintKot={hasFiredLines ? handleReprintKot : undefined}
              reprintKotPending={reprintKotPending}
            />
            {existingOrder?.customer_id && (
              <div className="border-b border-border/70 px-4 py-2.5 sm:px-5">
                <CounterLoyaltyChip
                  orderId={existingOrder.id}
                  customerId={existingOrder.customer_id}
                />
              </div>
            )}
            {existingOrder &&
              !checkoutOpen &&
              orderPayableRemaining(existingOrder) > 0 &&
              cart.length > 0 && (
                <div className="border-b border-border/70 px-4 py-2.5 sm:px-5">
                  <Button
                    type="button"
                    className="h-10 w-full text-sm font-semibold"
                    onClick={() => selectPaymentOrder(existingOrder)}
                  >
                    Pay {formatCurrency(orderPayableRemaining(existingOrder))}
                  </Button>
                </div>
              )}
            </div>

            <div
              ref={railScrollRef}
              className="counter-checkout-rail-scroll flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto overscroll-contain px-4 py-4 sm:px-5 sm:py-5"
              aria-label="Order and checkout"
            >
              {checkoutOpen && selectedOrder && payOrder && (
                <section className="space-y-3">
                  {splitState && payAmount > 0 && !splitDialogOpen && (
                    <button
                      type="button"
                      className="flex w-full items-center justify-between rounded-lg border border-amber-400/60 bg-amber-50/60 px-3 py-2 text-left text-xs font-medium text-amber-800 transition-colors hover:bg-amber-100/60 dark:bg-amber-950/30 dark:text-amber-300 dark:hover:bg-amber-950/50"
                      onClick={() => setSplitDialogOpen(true)}
                    >
                      <span>
                        Split in progress — {splitPaidCount} of {splitState.guest_count} guests paid
                      </span>
                      <span className="font-semibold underline underline-offset-2">Resume</span>
                    </button>
                  )}
                  <CounterPaymentPanel
                    payOrder={payOrder}
                    paymentCheckoutIntent={paymentCheckoutIntent}
                    onPaymentIntent={onPaymentIntent}
                    marketplaceLock={marketplaceLock}
                    paymentOrderFetching={paymentOrderFetching}
                    billableItems={billableItems}
                    paymentTotals={paymentTotals}
                    formatCurrency={formatCurrency}
                    payAmount={payAmount}
                    discountMode={discountMode}
                    onDiscountModeChange={setDiscountMode}
                    discountValue={discountValue}
                    onDiscountValueChange={setDiscountValue}
                    discountMutationPending={discountMutation.isPending}
                    onApplyDiscount={() => {
                      if (!selectedOrder) return
                      const raw = parseFloat(discountValue || '0')
                      if (Number.isNaN(raw) || raw < 0) return
                      const vars =
                        discountMode === 'percent'
                          ? raw === 0
                            ? { orderId: selectedOrder.id, discount_amount: 0 }
                            : { orderId: selectedOrder.id, discount_percent: raw }
                          : { orderId: selectedOrder.id, discount_amount: raw }
                      // F-CTRL-N80: the limit is on the bill's whole manual discount.
                      const items = selectedOrder.item_discounts_total ?? 0
                      const billBefore = selectedOrder.discount_amount ?? 0
                      const billAfter =
                        discountMode === 'percent' ? (Math.max(selectedOrder.subtotal - items, 0) * raw) / 100 : raw
                      const need = discountNeed(discountControls, {
                        changed: Math.abs(billAfter - billBefore) > 0.005,
                        elementAfter: billAfter,
                        subtotal: selectedOrder.subtotal,
                        totalBefore: billBefore + items,
                        totalAfter: billAfter + items,
                      })
                      askThen(
                        need,
                        {
                          title: 'Discount on the bill',
                          summary: `${formatCurrency(billAfter)} off a ${formatCurrency(selectedOrder.subtotal)} bill.`,
                          pinReason: pinLimitReason,
                        },
                        (auth) => discountMutation.mutate({ ...vars, auth }),
                      )
                    }}
                    // F-MM-N7: `payArming` is the ≤1.5s wait for an in-flight
                    // tender switch. Folded in here so the tap gives feedback
                    // and can't be double-fired. Safe to disable on because it
                    // is capped and released in a finally — unlike binding
                    // straight to checkoutIntentMutation.isPending, which would
                    // ride the 30s axios timeout and strand the till.
                    processPaymentPending={processPaymentMutation.isPending || payArming}
                    onlineSubMethods={payOnlineSubMethods}
                    selectedOnlineSub={selectedOnlineSub}
                    onSelectOnlineSub={setSelectedOnlineSub}
                    onPrimaryPay={onPrimaryPay}
                    onCompleteZeroBalance={runCompleteZeroBalance}
                    partialPaymentBanner={partialPaymentBanner}
                    onDismissPartialPaymentBanner={() => setPartialPaymentBanner(null)}
                    onPrintProvisionalBill={handlePrintProvisionalBill}
                    printProvisionalDisabled={
                      payAmount <= 0 || hasPartialPayment || processPaymentMutation.isPending
                    }
                    printProvisionalPending={provisionalPrintBusy}
                    onPromoChanged={() => {
                      // A promo apply/remove (bank card promo or promo code)
                      // rewrites discount_amount + total server-side. Mirror the
                      // discountMutation success path exactly — panel query AND
                      // selectedOrder — or the pay tap sends the pre-discount
                      // total and the backend refuses it (F-MM-N10).
                      if (payOrder) void refreshOrderIntoTill(payOrder.id)
                    }}
                    onApplyItemDiscount={payOrder ? (itemId, next) => {
                      const vars = {
                        orderId: payOrder.id,
                        itemId,
                        discount_amount: next?.mode === 'amount' ? next.value : 0,
                        discount_percent: next?.mode === 'percent' ? next.value : null,
                        // F-CTRL-N44: only present when the popover determined
                        // this comps the line to zero.
                        pin: next?.pin,
                      }
                      // F-CTRL-N80: measured on the bill's whole manual discount;
                      // a percent bill discount re-derives on the lower base.
                      const line = payOrder.items?.find((i) => i.id === itemId)
                      const lineSubtotal = line ? line.unit_price * line.quantity : 0
                      const lineBefore = line?.discount_amount ?? 0
                      const lineAfter = next == null ? 0 : next.mode === 'amount' ? next.value : (lineSubtotal * next.value) / 100
                      const items = payOrder.item_discounts_total ?? 0
                      const itemsAfter = items - lineBefore + lineAfter
                      const billBefore = payOrder.discount_amount ?? 0
                      const billPct = payOrder.discount_percent ?? 0
                      const billAfter = billPct > 0 ? (Math.max(payOrder.subtotal - itemsAfter, 0) * billPct) / 100 : billBefore
                      const need = discountNeed(discountControls, {
                        changed: Math.abs(lineAfter - lineBefore) > 0.005,
                        elementAfter: lineAfter,
                        subtotal: payOrder.subtotal,
                        totalBefore: billBefore + items,
                        totalAfter: billAfter + itemsAfter,
                      })
                      // A full comp already carries a manager PIN (F-CTRL-N44).
                      if (next?.pin) need.pin = false
                      askThen(
                        need,
                        {
                          title: 'Discount on an item',
                          summary: `${formatCurrency(lineAfter)} off ${line?.product?.name ?? 'this item'}.`,
                          pinReason: pinLimitReason,
                        },
                        (auth) => itemDiscountMutation.mutate({ ...vars, auth }),
                      )
                    } : undefined}
                    cardPromoLabel={activeDiscountLabel}
                    lineDiscountDisabled={hasActivePromo || hasActiveCardPromo}
                    lineDiscountDisabledHint={
                      hasActiveCardPromo
                        ? 'Remove the card promo to apply a line discount'
                        : 'Remove the promo to apply a line discount'
                    }
                    lineDiscountPending={itemDiscountMutation.isPending}
                    onEditDeliveryFee={payOrder?.order_type === 'delivery' ? onEditDeliveryFee : undefined}
                    deliveryDefaultFee={
                      payOrder ? getOrderTypePricing(payOrder.order_type, orderTypesRaw).deliveryFee : 0
                    }
                    deliveryFeePending={deliveryFeeMutation.isPending}
                    onEditServiceCharge={onEditServiceCharge}
                    serviceChargeDefaultRate={
                      payOrder && getOrderTypePricing(payOrder.order_type, orderTypesRaw).includeServiceCharge
                        ? pricing.service_charge_rate
                        : 0
                    }
                    serviceChargePending={serviceChargeMutation.isPending}
                    giftCardTenderEnabled={giftCardTenderEnabled}
                    onApplyGiftCard={() => setGiftCardDialogOpen(true)}
                    houseAccountsEnabled={(houseAccounts.staff || houseAccounts.tabs) && backendReachable}
                    onChargeToAccount={() => {
                      setChargeAccountError(null)
                      setChargeAccountOpen(true)
                    }}
                  />

                  {cashModalOpen && paymentCheckoutIntent === 'cash' && (
                    <CashTenderPad
                      amountDue={payAmount}
                      received={cashReceived}
                      onReceivedChange={setCashReceived}
                      onCancel={() => {
                        setCashModalOpen(false)
                        setCashReceived('')
                      }}
                      onComplete={runCashPayment}
                      formatCurrency={formatCurrency}
                      processing={processPaymentMutation.isPending}
                    />
                  )}

                  {payPricingExtras && (
                    <SplitBillDialog
                      open={splitDialogOpen}
                      onOpenChange={setSplitDialogOpen}
                      order={payOrder}
                      pricing={pricing}
                      pricingExtras={payPricingExtras}
                      serverRemaining={payAmount}
                      processing={processPaymentMutation.isPending}
                      formatCurrency={formatCurrency}
                      onlineSubMethods={splitOnlineSubMethods}
                      onOrderUpdated={handleSplitOrderUpdated}
                      onSetIntent={onPaymentIntent}
                      onPayGuest={handlePaySplitGuest}
                      onReprintGuest={handleReprintSplitGuest}
                    />
                  )}

                  {giftCardTenderEnabled && (
                    <GiftCardTenderDialog
                      open={giftCardDialogOpen}
                      onOpenChange={setGiftCardDialogOpen}
                      remaining={payAmount}
                      formatCurrency={formatCurrency}
                      onApply={runGiftCardPayment}
                      pending={processPaymentMutation.isPending}
                    />
                  )}

                  {(houseAccounts.staff || houseAccounts.tabs) && (
                    <ChargeToAccountDialog
                      open={chargeAccountOpen}
                      onOpenChange={setChargeAccountOpen}
                      staffEnabled={houseAccounts.staff}
                      tabsEnabled={houseAccounts.tabs}
                      billTotal={payAmount}
                      customerId={payOrder?.customer_id}
                      formatCurrency={formatCurrency}
                      onConfirm={runChargeToAccount}
                      pending={processPaymentMutation.isPending}
                      serverError={chargeAccountError}
                    />
                  )}

                </section>
              )}

              <SentItemsSection
                order={existingOrder}
                categoryById={categoryById}
                categoryColor={categoryColor}
                formatCurrency={formatCurrency}
                defaultExpanded={existingItemsExpanded}
                onRequestVoid={existingOrder?.id ? (itemId, itemName, qty, unitPrice) => {
                  setVoidModal({ orderId: existingOrder.id, itemId, itemName, qty, unitPrice })
                } : undefined}
                onRequestVoidOrder={existingOrder?.id ? () => {
                  setVoidModal({ orderId: existingOrder.id, itemId: '', itemName: '', qty: 0, unitPrice: 0, wholeOrder: true })
                } : undefined}
                onRequestRemove={existingOrder?.id ? (itemId) => {
                  removeUnsentItemMutation.mutate({ orderId: existingOrder.id, itemId })
                } : undefined}
                removingItemId={removingItemId}
                onFireUnsent={existingOrder?.id ? handleFireUnsent : undefined}
                fireUnsentPending={fireUnsentPending}
              />

              {offlineTarget && (
                <OfflineHeldTicket
                  order={offlineTarget}
                  nameFor={offlineProductName}
                  onClear={() => setOfflineTarget(null)}
                  onVoidLine={(i) => void voidOfflineLine(i)}
                />
              )}

              <UnsentItemsSection
                cart={pricedCart}
                categoryById={categoryById}
                categoryColor={categoryColor}
                formatCurrency={formatCurrency}
                continuing={appendingToLoadedOrder}
                onIncrement={addToCart}
                onDecrement={removeFromCart}
                onItemNotesChange={setItemNotes}
                onEditOptions={openLineOptions}
                lineHasOptions={(productId) => menuModifiers.hasOptions(productId)}
                flashLineId={lastCartFlashLineId}
                cartRowRefs={cartRowRefs}
                notes={orderNotes}
                onNotesChange={setOrderNotes}
                liveAnnouncementId={cartLiveAnnouncement.id}
                liveAnnouncementText={cartLiveAnnouncement.text}
              />
            </div>

            <div className="border-t border-border/90 bg-card/95 backdrop-blur-md">
              {/* F-FE-N30: orders held on this device. Renders nothing when the
                  queue is empty, which is the normal case — it appears only
                  during and just after an outage. Keyed on the pending count so
                  it re-reads immediately after a capture or a drain rather than
                  waiting for its poll. */}
              <div className="px-4 pt-2.5 sm:px-5">
                <OfflineOrdersTray
                  refreshKey={
                    offlineCapture.pendingCount +
                    offlineCapture.unreconciledCount +
                    offlineTrayNonce
                  }
                  selectedOpId={offlineTarget?.opId ?? null}
                  onSelect={handleSelectHeldOfflineOrder}
                  onTakePayment={setOfflinePayTarget}
                  priceOf={(o) => priceHeldOfflineOrder(o, createCheckoutIntent).total}
                />
              </div>
              {cart.length > 0 && !checkoutOpen && autoDealPreview.hints.length > 0 && (
                // F-DEAL-N3: "add 1 more to unlock…" — the best open nudge only.
                <div
                  className="mx-4 mt-2.5 flex items-start gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-sm sm:mx-5"
                  data-testid="counter-deal-hint"
                >
                  <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  <span className="min-w-0 flex-1">
                    {describeHint(autoDealPreview.hints[0], (id) => productById.get(id)?.name, formatCurrency)}
                  </span>
                  {autoDealPreview.hints[0].kind === 'add_item' &&
                    autoDealPreview.hints[0].product_id &&
                    productById.get(autoDealPreview.hints[0].product_id) && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-7 shrink-0"
                        onClick={() => {
                          const p = productById.get(autoDealPreview.hints[0].product_id as string)
                          if (p) handleTileTap(p)
                        }}
                      >
                        Add
                      </Button>
                    )}
                </div>
              )}
              {cart.length > 0 && !checkoutOpen && upsellOffer && (
                // F-DEAL-N4: upsell suggestions for the item just added.
                <div className="mx-4 mt-2.5 rounded-lg border border-border bg-muted/30 px-3 py-2 sm:mx-5" data-testid="counter-upsell-strip">
                  <div className="flex items-center gap-2">
                    <span className="min-w-0 flex-1 truncate text-sm font-medium">
                      {upsellOffer.prompt}
                      <span className="font-normal text-muted-foreground"> · {upsellOffer.triggerName}</span>
                    </span>
                    <button
                      type="button"
                      aria-label="Dismiss suggestions"
                      className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                      onClick={() => setUpsellOffer(null)}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {upsellOffer.dealIds.map((id) => {
                      const deal = productById.get(id)
                      const trigger = productById.get(upsellOffer.triggerId)
                      if (!deal || !trigger) return null
                      const diff = basePriceOf(deal) - basePriceOf(trigger)
                      return (
                        <Button key={`deal-${id}`} type="button" size="sm" className="h-8" onClick={() => acceptUpsell(id, 'deal')}>
                          Make it a {deal.name} · {diff >= 0 ? `+${formatCurrency(diff)}` : `save ${formatCurrency(-diff)}`}
                        </Button>
                      )
                    })}
                    {upsellOffer.productIds.map((id) => {
                      const p = productById.get(id)
                      if (!p) return null
                      return (
                        <Button key={id} type="button" size="sm" variant="outline" className="h-8" onClick={() => acceptUpsell(id, 'item')}>
                          <Plus className="mr-1 h-3.5 w-3.5" />
                          {p.name} · {formatCurrency(basePriceOf(p))}
                        </Button>
                      )
                    })}
                  </div>
                </div>
              )}
              {cart.length > 0 && !checkoutOpen && (
                <div className="flex items-center gap-2 px-4 pt-2.5 sm:px-5">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Tax preview
                  </span>
                  <div
                    role="tablist"
                    aria-label="Tax preview payment type"
                    className="ml-auto inline-flex rounded-md border border-border bg-muted/40 p-0.5"
                  >
                    {(
                      [
                        ['cash', 'Cash'],
                        ['card', 'Card'],
                        ['online', 'Online'],
                      ] as const
                    ).map(([k, label]) => {
                      const active = createCheckoutIntent === k
                      return (
                        <button
                          key={k}
                          type="button"
                          role="tab"
                          aria-selected={active}
                          onClick={() => setCreateCheckoutIntent(k)}
                          className={cn(
                            'h-7 rounded-[5px] px-3 text-xs font-medium transition-colors',
                            active
                              ? 'bg-background text-foreground shadow-sm'
                              : 'text-muted-foreground hover:text-foreground'
                          )}
                        >
                          {label}
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}
              {existingOrder &&
              !checkoutOpen &&
              orderPayableRemaining(existingOrder) > 0 &&
              cart.length === 0 ? (
                <div className="px-4 py-3 sm:px-5 sm:py-4">
                  <Button
                    type="button"
                    className="h-12 w-full text-base font-semibold sm:h-14 sm:text-lg"
                    onClick={() => selectPaymentOrder(existingOrder)}
                  >
                    <BadgeDollarSign className="mr-2 h-5 w-5" />
                    Pay {formatCurrency(orderPayableRemaining(existingOrder))}
                  </Button>
                </div>
              ) : (
                <ActionFooter
                  mode={cart.length > 0 ? 'compose' : checkoutOpen ? 'checkout' : 'idle'}
                  totals={
                    cart.length === 0
                      ? null
                      : {
                          subtotal: cartSubtotal,
                          service: cartTotals.service,
                          tax: cartTotals.tax,
                          taxRate: cartTotals.taxRate,
                          serviceRate: cartTotals.serviceRate,
                          delivery: cartTotals.delivery,
                          posFee: cartTotals.posFee,
                          total: cartTotals.total,
                          taxTreatment: cartTotals.taxTreatment,
                          discount: autoDealAmount > 0 ? autoDealAmount : undefined,
                          discountLabel: autoDealPreview.best ? autoDiscountLabel(autoDealPreview.best.name) : undefined,
                        }
                  }
                  showCloseCheckoutWithCompose={checkoutOpen}
                  formatCurrency={formatCurrency}
                  deliveryEditable={orderType === 'delivery'}
                  deliveryOverridden={deliveryFeeOverride != null}
                  deliveryDefault={createOrderPricingExtras.deliveryFee}
                  deliveryPending={deliveryFeeMutation.isPending}
                  onEditDelivery={onEditDeliveryFee}
                  serviceChargeOverride={serviceChargeOverride}
                  serviceChargeDefaultRate={
                    createOrderPricingExtras.includeServiceCharge ? pricing.service_charge_rate : 0
                  }
                  serviceChargePending={serviceChargeMutation.isPending}
                  onEditServiceCharge={onEditServiceCharge}
                  primaryLabel={
                    appendingToLoadedOrder ? 'Add items & fire KOT' : 'Place order & Fire'
                  }
                  onPrimary={handleSubmitCart}
                  // F-FE-N158: Hold only offered for a new cart; a placed order's
                  // unsent items are out of v1 scope (holdBlockReason).
                  onHold={holdReason === 'placed_order' ? undefined : () => void holdCurrentCart()}
                  holdDisabled={holdReason !== null || holdPending || submitCartMutation.isPending}
                  holdPending={holdPending}
                  holdHint={holdReason === 'offline' ? holdHint ?? undefined : undefined}
                  primaryDisabled={
                    cart.length === 0 ||
                    submitCartMutation.isPending ||
                    // F-FE-N30: offline no longer means blocked. The button
                    // stays live when this terminal can genuinely capture and
                    // fire locally; otherwise the hint below says why not.
                    (!online && !offlineFireReady) ||
                    (!appendingToLoadedOrder && deliveryDetailsMissing) ||
                    (!appendingToLoadedOrder && guestPhoneError != null)
                  }
                  primaryPending={submitCartMutation.isPending}
                  disabledHint={
                    // F-CTRL-N37: adding to an existing tab now works offline
                    // too, so both paths ask the same predicate — there is no
                    // longer a separate "reconnect to add to the tab" case.
                    !online && cart.length > 0
                      ? // A refusal that no longer applies must not be quoted:
                        // when the fire will go through, the cashier gets the
                        // plain "held on this device" line instead of a reason
                        // that does not apply to them.
                        //
                        // F-FE-N73: the KDS warning is surfaced here. It has been
                        // computed since 2026-08-05 (capability.ts) and rendered
                        // nowhere, so on a tenant with a KDS station the items
                        // routed to that board silently reached nothing at all
                        // while the cashier was told everything was fine.
                        offlineFireReady && offlineCapture.capability.warning ===
                          'kds_stations_unreachable'
                        ? 'Offline — saved on this device. Screen-only stations cannot ' +
                          'receive a ticket: tell the kitchen those items by hand.'
                        : offlineHintForRefusal(
                            offlineFireReady ? undefined : offlineCapture.capability.reason
                          )
                      : !appendingToLoadedOrder && deliveryDetailsMissing && cart.length > 0
                      ? 'Delivery needs a phone & address — open Guest details (red dot).'
                      : !appendingToLoadedOrder && guestPhoneError != null && cart.length > 0
                      ? `${guestPhoneError} Fix it in Guest details.`
                      : !checkoutOpen
                      ? cart.length === 0
                        ? 'Add items to enable the primary action.'
                        : undefined
                      : undefined
                  }
                  onCloseCheckout={() => {
                    setCheckoutOpen(false)
                    setSelectedOrder(null)
                  }}
                />
              )}
            </div>
        </aside>
        </div>
        </div>
      </div>
    </div>
  )
}
